import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import FormRenderer from '../components/forms/FormRenderer';
import ErrorModal from '../components/modals/ErrorModal';
import AlertModal from '../components/modals/AlertModal';
import { usePageConfig } from '../contexts/PageConfigContext';
import { useAuth } from '../contexts/AuthContext';
import { getDefaultInitialAnswers as getDefaultInitialAnswersHelper, getAnswerByLabel } from '../utils/formHelpers';
import { APP_SLUGS, DEFAULT_STUDENT_INFO_ENABLED_ATTRIBUTES } from '../constants/appSlugs';
import {
  formatTransferCreditsBadgeValue,
  getStudentInformationFieldDef,
  hasTransferCreditsValue,
  TRANSFER_CREDITS_EMPTY_LABEL
} from '../constants/studentInformationFields';
import {
  getEndedStudentStatuses,
  getGradeLevelOptions,
  getStudentStatusOptions,
  statusBadgeTone
} from '../constants/studentInformationOptions';
import { buildAttachmentDisplayModel } from '../constants/attachmentRequirements';
import { buildCredentialDisplayModel, rowEntryType } from '../constants/credentialRequirements';
import PaymentChargeEditor from '../components/payments/PaymentChargeEditor';
import {
  getPaymentStatusMeta,
  resolveDisplayStatus,
  formatPaymentAmount,
  formatMonthKeyLabel,
  dateTimeLocalToStorage
} from '../constants/paymentStatuses';
import {
  buildPersonLinksApiUrl,
  getCounselorVisibleProperties,
  getEnabledPersonLinkTypes,
  getPersonLinkTypeCardsForMetadata,
  GUARDIAN_SUBTYPE_LABELS,
  GUARDIAN_SUBTYPE_OPTIONS,
  isGuardianPersonLinkType,
  isPersonLinkSearchOnlyType,
  isSiblingPersonLinkType,
  isUserToUserPersonLinkType,
  personLinkRequiresPeriodMatch,
  PERSON_LINK_ADD_TITLES,
  PERSON_LINK_CURRENT_USER_LABELS,
  PERSON_LINK_DISPLAY_ORDER,
  PERSON_LINK_SECTION_LABELS,
  PERSON_LINK_TARGET_LABELS,
  PERSON_LINKS_RELATIONSHIP_TYPES,
  SIBLING_SUBTYPE_LABELS,
  SIBLING_SUBTYPE_OPTIONS,
  personLinkHasResolvedProperty,
  personLinkResolvedProperty
} from '../constants/personLinksOptions';
import { getPropertyLabel } from '../constants/ugpProperties';
import UgpPropertiesSection from '../components/profile/UgpPropertiesSection';
import AddressesSection from '../components/profile/AddressesSection';
import FormAssignmentViewer from '../components/profile/FormAssignmentViewer';
import '../components/profile/FormAssignmentViewer.css';
import {
  FORM_RELATIONSHIP_TYPES,
  formatFormRelationshipTypeLabel,
  normalizeFormRelationshipType
} from '../constants/formRelationships';
import {
  getProfileFormPermissions,
  canOpenProfileForm,
  canEditProfileFormAnswers,
  canSeeProfileFormSubmission,
  canCreateProfileFormSubmission,
  canEditProfileFormDraft,
  canEditSubmittedProfileForm,
  canDeleteProfileFormSubmission,
  canUpdateProfileFormPoints,
  resolveCollaborationCapabilities,
  buildCustomFormsPermissionContext
} from '../utils/profileFormPermissions';
import {
  clearFormPendingBlobs,
  hasPendingFormFileUploads,
  hydrateFormSubmissionFileAnswers,
  stripFileUploadAnswers,
  uploadPendingFormFiles
} from '../utils/formFileUploads';
import { submissionAnswersToFormData } from '../utils/submissionAnswers';
import { resolveSubmissionReferenceFromFormRelationship } from '../utils/formSubmissionReference';
import {
  SUBMISSION_ACTIONS,
  validateRequiredForSubmit,
  isSubmissionLockedForStudent,
  canSubmitDraft
} from '../utils/formSubmissionLifecycle';
import {
  resolveSubmissionPolicy,
  canStartNewSubmissionAttempt
} from '../utils/formAssignmentMetadata';
import { getUserProfileViewLayoutFromConfig } from '../utils/pageConfigurationSummary';
import { getProfileAccessBadges } from '../utils/memberAccessDetails';
import AssociationsSection, { AddAssociationModal } from '../components/profile/AssociationsSection';
import {
  buildAssociationsApiUrl,
  parseEnabledAssociationTypes
} from '../constants/associations';
import ProfileSectionAddButton from '../components/profile/ProfileSectionAddButton';
import {
  buildUserProfileUrl,
  getProfileAssignmentFromSearchParams,
  getProfileEnrollmentFromSearchParams,
  resolveEnrollmentGroupsPeriodsId
} from '../utils/userProfileUrl';
import './UserProfileView.css';

const DEFAULT_PROFILE_VIEW_CONFIG = {
  columns: []
};

const LEFT_COLUMN_DEFAULT_PX = 600;
const LEFT_COLUMN_MIN_PX = 280;
const LEFT_COLUMN_MAX_PX = 1200;

const getDefaultStudentInfoDraft = () => ({
  status: 'Active',
  enrollment_date: '',
  exit_date: '',
  grade_level: '',
  previously_enrolled: false,
  transfer_credits: '',
  vendor_groups_periods_id: '',
  track_period_id: '',
  primary_pathway: ''
});

const studentInfoDraftFromRow = (row) =>
  row
    ? {
        status: row.status ?? '',
        enrollment_date: row.enrollment_date ?? '',
        exit_date: row.exit_date ?? '',
        grade_level: row.grade_level ?? '',
        previously_enrolled: row.previously_enrolled ?? false,
        transfer_credits: row.transfer_credits != null ? String(row.transfer_credits) : '',
        vendor_groups_periods_id: row.vendor_groups_periods_id ?? '',
        track_period_id: row.track_period_id ?? '',
        primary_pathway: row.primary_pathway ?? ''
      }
    : getDefaultStudentInfoDraft();

const getUserDefaultDisplayName = (u) => {
  if (!u) return '';
  return (u.first_name || '').trim();
};

const getUserProfileDisplayName = (u) => {
  if (!u) return '';
  const preferred = (u.preferred_name || '').trim();
  if (preferred) return preferred;
  return getUserDefaultDisplayName(u);
};

const formatDocCategoryLabel = (value) => {
  const key = String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
  if (!key) return '';
  return key
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

/** Category options only when assignment metadata includes allowed_categories. */
const getDocumentCategoryOptions = (app) => {
  const allowed = app?.metadata?.allowed_categories;
  if (!Array.isArray(allowed) || allowed.length === 0) return [];
  return allowed.map((c) => {
    const raw = String(c).trim();
    const v = raw.toLowerCase().replace(/\s+/g, '_');
    return { value: v, label: formatDocCategoryLabel(v) || raw };
  });
};

const DOC_MEDIA_LABELS = {
  pdf: 'PDF',
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  link: 'Link',
  other: 'File'
};

const DOC_MEDIA_ICONS = {
  pdf: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  image: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  video: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  ),
  audio: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  ),
  link: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  other: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <polyline points="13 2 13 9 20 9" />
    </svg>
  )
};

const getDocMediaIcon = (mediaType) =>
  DOC_MEDIA_ICONS[String(mediaType || '').toLowerCase()] || DOC_MEDIA_ICONS.other;

const isGroupsReturnUrl = (url) => {
  if (!url) return false;
  try {
    return decodeURIComponent(url).startsWith('/groups');
  } catch {
    return url.startsWith('/groups');
  }
};

const getGroupsBackLabel = (url) => {
  try {
    const decoded = decodeURIComponent(url || '');
    return decoded.includes('returnTo=members') ? 'Back to members' : 'Back to Groups';
  } catch {
    return 'Back to Groups';
  }
};

function ProfileAccessIcon({ icon }) {
  if (icon === 'check') {
    return (
      <svg className="profile-access-badge-icon" viewBox="0 0 16 16" aria-hidden>
        <path
          fill="currentColor"
          d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm3.07 4.78-3.6 3.6a.75.75 0 0 1-1.06 0l-1.5-1.5a.75.75 0 1 1 1.06-1.06l.97.97 3.07-3.07a.75.75 0 1 1 1.06 1.06z"
        />
      </svg>
    );
  }
  if (icon === 'cancel') {
    return (
      <svg className="profile-access-badge-icon" viewBox="0 0 16 16" aria-hidden>
        <path
          fill="currentColor"
          d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zm2.28 3.72a.75.75 0 0 1 0 1.06L9.06 8l1.22 1.22a.75.75 0 1 1-1.06 1.06L8 9.06 6.78 10.28a.75.75 0 1 1-1.06-1.06L6.94 8 5.72 6.78a.75.75 0 0 1 1.06-1.06L8 6.94l1.22-1.22a.75.75 0 0 1 1.06 0z"
        />
      </svg>
    );
  }
  return null;
}

function ProfileAccessBadge({ badge, disabled, onClick, ariaChecked }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ariaChecked}
      className={`profile-access-badge profile-access-badge--${badge.tone}`}
      title={badge.title}
      disabled={disabled}
      onClick={onClick}
    >
      <ProfileAccessIcon icon={badge.icon} />
      <span>{badge.label}</span>
    </button>
  );
}

const UserProfileView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pageConfig = usePageConfig();
  const { fetchWithAuth, user: authUser, loadCurrentRole } = useAuth();
  const canEditUserData = pageConfig?.configuration?.can_edit_user_data === true;
  const canEditSignIn = pageConfig?.configuration?.can_edit_sign_in === true;
  const canGrantPlatformAccess = pageConfig?.configuration?.can_grant_platform_access === true;
  const canEditStudentInformation = pageConfig?.configuration?.can_edit_student_information === true;
  const canAddCredentials = pageConfig?.configuration?.can_add_credentials === true;
  const canEditCredentials = pageConfig?.configuration?.can_edit_credentials === true;
  const canRemoveCredentials = pageConfig?.configuration?.can_remove_credentials === true;
  const canAddAttachments = pageConfig?.configuration?.can_add_attachments === true;
  const canEditAttachments = pageConfig?.configuration?.can_edit_attachments === true;
  const canRemoveAttachments = pageConfig?.configuration?.can_remove_attachments === true;
  const canEditPayments = pageConfig?.configuration?.can_edit_payments === true;
  const canAddUgpProperties = pageConfig?.configuration?.can_add_ugp_properties === true;
  const canEditUgpProperties = pageConfig?.configuration?.can_edit_ugp_properties === true;
  const canRemoveUgpProperties = pageConfig?.configuration?.can_remove_ugp_properties === true;
  // can_add_person_links: add Person Links; must be explicitly true
  const canAddPersonLinks = pageConfig?.configuration?.can_add_person_links === true;
  // can_remove_person_links: remove Person Links; must be explicitly true
  const canRemovePersonLinks = pageConfig?.configuration?.can_remove_person_links === true;
  // can_edit_person_links: edit Person Links (guardian type, primary, user name/email/phone)
  const canEditPersonLinks = pageConfig?.configuration?.can_edit_person_links === true;
  const canAddAssociations = pageConfig?.configuration?.can_add_associations === true;
  const canRemoveAssociations = pageConfig?.configuration?.can_remove_associations === true;
  const formPerms = getProfileFormPermissions(pageConfig);
  const [user, setUser] = useState(null);
  const formPermCtx = useMemo(
    () => buildCustomFormsPermissionContext(authUser, user),
    [authUser, user]
  );
  const canViewFormsSection = canOpenProfileForm(formPerms, formPermCtx);
  const canCreateSubmission = canCreateProfileFormSubmission(formPerms, formPermCtx);
  const canDeleteSubmission = canDeleteProfileFormSubmission(formPerms, formPermCtx);
  const canUpdatePoints = canUpdateProfileFormPoints(formPerms, formPermCtx);
  const canViewQuestionAnswers = formPerms.canViewQuestionAnswers === true;
  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorModal, setErrorModal] = useState(null); // { title, message } for validation/save errors shown in modal
  const [editFormData, setEditFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    dob: '',
    platformAccess: true
  });
  const [originalFormData, setOriginalFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    dob: '',
    platformAccess: true
  });
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');
  const [showPlatformPasswordField, setShowPlatformPasswordField] = useState(false);
  const [shellAccess, setShellAccess] = useState(null);
  const [shellAccessSaving, setShellAccessSaving] = useState(false);
  const fileInputRef = React.createRef();
  
  // Section expanded state.
  // basicInfo always defaults open; everything else starts collapsed and is opened
  // only when its config says so (page-config bucket.expanded_by_default for right-column
  // sections, or app-assignment metadata.expanded_by_default for left-column app sections).
  const [sectionsExpanded, setSectionsExpanded] = useState({
    basicInfo: true,
    addresses: true,
    personLinks: false,
    associations: false,
    customForms: false,
    studentInformation: false,
    credentialsAndKeys: false,
    attachmentsApp: false,
    payments: false,
    forms: false,
    evaluations: false,
    benchmarks: false,
    grades: false,
    certificates: false,
    password: false
  });
  
  const [leftColumnWidthPx, setLeftColumnWidthPx] = useState(LEFT_COLUMN_DEFAULT_PX);

  // Column widths (dynamic / right-side splits when resizing between content columns)
  const [columnWidths, setColumnWidths] = useState({
    left: 33.33,
    middle: 33.33,
    right: 33.33
  });

  // Dragging state
  const [isDragging, setIsDragging] = useState(null);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartWidths, setDragStartWidths] = useState({ left: 0, middle: 0, right: 0 });
  const [dragStartLeftPx, setDragStartLeftPx] = useState(LEFT_COLUMN_DEFAULT_PX);
  const [hoveredHandle, setHoveredHandle] = useState(null);
  // Inline forms state
  const [expandedFormIds, setExpandedFormIds] = useState({}); // { [formId]: boolean }
  const [existingSubmissions, setExistingSubmissions] = useState({}); // { [formId]: submission or null }
  const [isSubmittingFormId, setIsSubmittingFormId] = useState(null);
  const [selectedSubmissionIdByFormId, setSelectedSubmissionIdByFormId] = useState({}); // { [formId]: submissionId|null }
  const [submissionsByFormId, setSubmissionsByFormId] = useState({}); // { [formId]: [submissions] }
  // Form data change tracking
  const [formDataByFormId, setFormDataByFormId] = useState({}); // { [formId]: currentFormData }
  const [originalFormDataByFormId, setOriginalFormDataByFormId] = useState({}); // { [formId]: originalFormData }
  const [activeAssignmentFormId, setActiveAssignmentFormId] = useState(null);
  const assignmentDeepLinkKeyRef = useRef('');
  const [leftColumnCollapsed, setLeftColumnCollapsed] = useState(false);
  const leftColumnWidthBeforeCollapseRef = useRef(LEFT_COLUMN_DEFAULT_PX);
  const pendingFormFileBlobsRef = useRef({});
  const [formDocumentUrlByFormId, setFormDocumentUrlByFormId] = useState({});
  
  const profileViewConfig = useMemo(() => {
    const layout = getUserProfileViewLayoutFromConfig(pageConfig?.configuration || {});
    if (layout?.columns?.length) return layout;
    return DEFAULT_PROFILE_VIEW_CONFIG;
  }, [pageConfig]);
  const [selectedGroupsPeriodsId, setSelectedGroupsPeriodsId] = useState(null);
  const [availableGroupsPeriods, setAvailableGroupsPeriods] = useState([]);
  const [showGroupsPeriodsModal, setShowGroupsPeriodsModal] = useState(false);
  const [defaultRoleUgpId, setDefaultRoleUgpId] = useState(null); // selected in modal for "default role" (ugp.id)
  const [savingDefaultRole, setSavingDefaultRole] = useState(false);
  // Person Links (left column): assigned apps with metadata + person links data
  const [assignedApps, setAssignedApps] = useState([]);
  const [personLinks, setPersonLinks] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [showAddAssociationModal, setShowAddAssociationModal] = useState(false);
  const [assocRemoveConfirm, setAssocRemoveConfirm] = useState(null);
  const [ugpProperties, setUgpProperties] = useState([]);
  const [loadingUgpProperties, setLoadingUgpProperties] = useState(false);
  const [showUgpPropertyAddSheet, setShowUgpPropertyAddSheet] = useState(false);
  const [showAddPersonLinkModal, setShowAddPersonLinkModal] = useState(false);
  const [addPersonLinkUserSearch, setAddPersonLinkUserSearch] = useState('');
  const [addPersonLinkUserResults, setAddPersonLinkUserResults] = useState([]);
  const [addPersonLinkRelationshipType, setAddPersonLinkRelationshipType] = useState('guardian');
  const [addPersonLinkSelectedUser, setAddPersonLinkSelectedUser] = useState(null);
  const [addPersonLinkMode, setAddPersonLinkMode] = useState('create'); // 'search' | 'create'
  const [addPersonLinkGuardianType, setAddPersonLinkGuardianType] = useState('father');
  const [addPersonLinkSiblingType, setAddPersonLinkSiblingType] = useState('brother');
  const [addPersonLinkPrimary, setAddPersonLinkPrimary] = useState(false);
  const [addPersonLinkEmergency, setAddPersonLinkEmergency] = useState(false);
  const [addPersonLinkCreateFirstName, setAddPersonLinkCreateFirstName] = useState('');
  const [addPersonLinkCreateLastName, setAddPersonLinkCreateLastName] = useState('');
  const [addPersonLinkCreateEmail, setAddPersonLinkCreateEmail] = useState('');
  const [addPersonLinkCreatePhone, setAddPersonLinkCreatePhone] = useState('');
  const [addPersonLinkSearching, setAddPersonLinkSearching] = useState(false);
  const [savingPersonLink, setSavingPersonLink] = useState(false);
  const addPlFlowRef = useRef(null);
  const addPlTargetHeadRef = useRef(null);
  const [addPlConnectorPath, setAddPlConnectorPath] = useState('');
  const [showEditPersonLinkModal, setShowEditPersonLinkModal] = useState(false);
  const [editPersonLinkItem, setEditPersonLinkItem] = useState(null);
  const [editPersonLinkGuardianType, setEditPersonLinkGuardianType] = useState('father');
  const [editPersonLinkSiblingType, setEditPersonLinkSiblingType] = useState('brother');
  const [editPersonLinkPrimary, setEditPersonLinkPrimary] = useState(false);
  const [editPersonLinkEmergency, setEditPersonLinkEmergency] = useState(false);
  const [removeConfirmModal, setRemoveConfirmModal] = useState(null); // { relId, personName }
  const [exitDateConfirm, setExitDateConfirm] = useState(null); // { newStatus }

  // Student Information app (left column)
  const [studentInfo, setStudentInfo] = useState(null);
  const [studentInfoDraft, setStudentInfoDraft] = useState({});
  const [studentInfoReady, setStudentInfoReady] = useState(false);
  const [savingStudentInfo, setSavingStudentInfo] = useState(false);
  const [studentInfoBadgePicker, setStudentInfoBadgePicker] = useState(null);
  const [studentInfoFieldOptions, setStudentInfoFieldOptions] = useState({
    vendor_groups_periods_id: [],
    track_period_id: [],
    primary_pathway: []
  });
  const [studentInfoVendorOptionsLoading, setStudentInfoVendorOptionsLoading] = useState(false);
  const [resolvedVendorGroupName, setResolvedVendorGroupName] = useState('');
  const studentInfoBadgesWrapRef = useRef(null);

  const [credentialsKeys, setCredentialsKeys] = useState([]);
  const [showCkModal, setShowCkModal] = useState(false);
  const [ckEditId, setCkEditId] = useState(null);
  const [ckForm, setCkForm] = useState({
    entry_type: 'credentials',
    platform_pick: '',
    platform_custom: '',
    title: '',
    email: '',
    username: '',
    password: '',
    notes: ''
  });
  const [savingCk, setSavingCk] = useState(false);
  const [ckDeleteConfirm, setCkDeleteConfirm] = useState(null);
  const [showPreferredNameModal, setShowPreferredNameModal] = useState(false);
  const [preferredNameDraft, setPreferredNameDraft] = useState('');
  const [savingPreferredName, setSavingPreferredName] = useState(false);
  /** credentials_keys list rows: expanded detail panels */
  const [ckExpandedRowIds, setCkExpandedRowIds] = useState({});

  const [documents, setDocuments] = useState([]);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docEditId, setDocEditId] = useState(null);
  const [docForm, setDocForm] = useState({
    category: 'general',
    title: '',
    description: '',
    url: '',
    selectedFile: null,
    existingFileName: null
  });
  const [savingDoc, setSavingDoc] = useState(false);
  const [docDeleteConfirm, setDocDeleteConfirm] = useState(null);

  // Payments app (left column) — merged schedule + this student's payment rows
  const [payments, setPayments] = useState([]);
  const [paymentEditModal, setPaymentEditModal] = useState(null); // { item, draft }
  const [savingPayment, setSavingPayment] = useState(false);
  /** null | 'disable_platform_access' | 'password_change' */
  const [basicInfoSaveConfirm, setBasicInfoSaveConfirm] = useState(null);
  const [docSourceTab, setDocSourceTab] = useState('file');
  const docFileInputRef = useRef(null);
  /** Original url/storage when editing — used to detect file/link replace vs metadata-only save */
  const docEditOriginalRef = useRef(null);

  useEffect(() => {
    setLeftColumnWidthPx(LEFT_COLUMN_DEFAULT_PX);
    const columns = profileViewConfig?.columns || [];
    const enabledCount = columns.filter((c) => c.enabled !== false).length;
    const dynamicShare = enabledCount > 0 ? 100 / enabledCount : 100;
    setColumnWidths({
      left: 33.33,
      middle: dynamicShare,
      right: dynamicShare
    });
  }, [profileViewConfig]);

  // Apply expanded-by-default flags from page config (right-column buckets) and
  // app assignment metadata (left-column app sections). basicInfo stays open by
  // default; everything else stays collapsed unless explicitly opted-in.
  useEffect(() => {
    const next = {
      basicInfo: true,
      personLinks: false,
      customForms: false,
      studentInformation: false,
      credentialsAndKeys: false,
      attachmentsApp: false,
      payments: false,
      forms: false,
      evaluations: false,
      benchmarks: false,
      grades: false,
      certificates: false,
      password: false
    };
    (profileViewConfig?.columns || []).forEach((col) => {
      (col?.buckets || []).forEach((bucket) => {
        if (bucket?.id && bucket.expanded_by_default === true) {
          next[bucket.id] = true;
        }
      });
    });
    const slugToSectionKey = {
      [APP_SLUGS.PERSON_LINKS]: 'personLinks',
      [APP_SLUGS.ASSOCIATIONS]: 'associations',
      [APP_SLUGS.CUSTOM_FORMS]: 'customForms',
      [APP_SLUGS.STUDENT_INFORMATION]: 'studentInformation',
      [APP_SLUGS.CREDENTIALS_AND_KEYS]: 'credentialsAndKeys',
      [APP_SLUGS.ATTACHMENTS]: 'attachmentsApp',
      [APP_SLUGS.PROFILE_PROPERTIES]: 'profileProperties',
      [APP_SLUGS.PAYMENTS]: 'payments'
    };
    (assignedApps || []).forEach((app) => {
      const key = slugToSectionKey[app?.slug];
      if (key && app?.metadata?.expanded_by_default === true) {
        next[key] = true;
      }
    });
    setSectionsExpanded(next);
  }, [profileViewConfig, assignedApps]);

  // Get user ID and return URL from query parameters
  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('id');
  const returnUrl = searchParams.get('from');
  const groupsReturnUrl = isGroupsReturnUrl(returnUrl) ? returnUrl : null;

  useEffect(() => {
    if (userId) {
      setSelectedGroupsPeriodsId(null);
      loadUserProfile(userId);
      loadUserForms(userId);
    } else {
      setError('No user ID provided');
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    const currentGp = (availableGroupsPeriods || []).find(
      (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    if (!user?.id || !currentGp?.id) {
      setShellAccess(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          for_user_id: String(user.id),
          user_groups_periods_id: String(currentGp.id)
        });
        if (currentGp.period_id) params.set('periodId', String(currentGp.period_id));
        if (currentGp.group_name) params.set('group_name', currentGp.group_name);
        const res = await fetchWithAuth(`/api/page-configurations/platform-access?${params}`);
        const data = await res.json();
        if (!cancelled) {
          setShellAccess(res.ok && data?.data?.hasAccess === true);
        }
      } catch (err) {
        console.error('Error loading Platform Access:', err);
        if (!cancelled) setShellAccess(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, selectedGroupsPeriodsId, availableGroupsPeriods, fetchWithAuth]);

  // Reload forms when selectedGroupsPeriodsId changes to filter by groups_periods
  useEffect(() => {
    if (userId && selectedGroupsPeriodsId) {
      loadUserForms(userId, selectedGroupsPeriodsId);
      setActiveAssignmentFormId(null);
    }
  }, [userId, selectedGroupsPeriodsId]);

  useEffect(() => {
    assignmentDeepLinkKeyRef.current = '';
  }, [location.search]);

  // Open assignment viewer when linked from Work Samples portfolio (or other deep links).
  useEffect(() => {
    const link = getProfileAssignmentFromSearchParams(location.search);
    if (!link.openAssignment || !userId || isLoading || !selectedGroupsPeriodsId || !forms.length) {
      return;
    }
    if (!canViewFormsSection) return;

    const linkKey = [
      link.relationshipId || '',
      link.formId || '',
      link.submissionId || '',
      selectedGroupsPeriodsId
    ].join(':');
    if (assignmentDeepLinkKeyRef.current === linkKey) return;

    let targetForm = null;
    if (link.relationshipId) {
      targetForm = forms.find((f) => String(f.relationship_id) === String(link.relationshipId));
    }
    if (!targetForm && link.formId) {
      targetForm = forms.find((f) => String(f.id) === String(link.formId));
    }
    if (!targetForm) return;

    const relType =
      normalizeFormRelationshipType(targetForm.relationship_type) ||
      FORM_RELATIONSHIP_TYPES.FILL_OUT;
    if (relType !== FORM_RELATIONSHIP_TYPES.ASSIGNMENT) return;

    assignmentDeepLinkKeyRef.current = linkKey;
    setSectionsExpanded((prev) => ({ ...prev, customForms: true }));
    openAssignmentForm(targetForm, { submissionId: link.submissionId || null });
  }, [
    userId,
    forms,
    isLoading,
    selectedGroupsPeriodsId,
    location.search,
    canViewFormsSection
  ]);

  // Load assigned apps (slug + metadata) for selected groups_periods
  useEffect(() => {
    if (!selectedGroupsPeriodsId) {
      setAssignedApps([]);
      return;
    }
    const load = async () => {
      try {
        const res = await fetch(`/api/apps/assigned-to-groups-periods/${selectedGroupsPeriodsId}`);
        const data = await res.json();
        if (data.success) setAssignedApps(data.data || []);
        else setAssignedApps([]);
      } catch {
        setAssignedApps([]);
      }
    };
    load();
  }, [selectedGroupsPeriodsId]);

  // Load person links when person_links app is assigned and we have ugp id
  useEffect(() => {
    const hasPersonLinks = (assignedApps || []).some(a => a.slug === APP_SLUGS.PERSON_LINKS);
    const currentGp = (availableGroupsPeriods || []).find(
      gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!hasPersonLinks || !ugpId || !user?.id) {
      setPersonLinks([]);
      return;
    }
    const load = async () => {
      try {
        const res = await fetch(
          buildPersonLinksApiUrl({ userGroupsPeriodsId: ugpId, userId: user.id })
        );
        const data = await res.json();
        if (data.success) setPersonLinks(data.data || []);
        else setPersonLinks([]);
      } catch {
        setPersonLinks([]);
      }
    };
    load();
  }, [assignedApps, selectedGroupsPeriodsId, availableGroupsPeriods, user?.id]);

  useEffect(() => {
    const hasAssociations = (assignedApps || []).some((a) => a.slug === APP_SLUGS.ASSOCIATIONS);
    const currentGp = (availableGroupsPeriods || []).find(
      (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!hasAssociations || !ugpId) {
      setAssociations([]);
      return;
    }
    const load = async () => {
      try {
        const res = await fetch(buildAssociationsApiUrl(ugpId, { includeEnded: true }));
        const data = await res.json();
        if (data.success) setAssociations(data.data || []);
        else setAssociations([]);
      } catch {
        setAssociations([]);
      }
    };
    load();
  }, [assignedApps, selectedGroupsPeriodsId, availableGroupsPeriods]);

  const reloadUgpProperties = useCallback(async () => {
    const ppApp = (assignedApps || []).find((a) => a.slug === APP_SLUGS.PROFILE_PROPERTIES);
    const currentGp = (availableGroupsPeriods || []).find(
      (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!ppApp || !ugpId) {
      setUgpProperties([]);
      return;
    }
    setLoadingUgpProperties(true);
    try {
      const res = await fetch(`/api/ugp-properties?user_groups_periods_id=${ugpId}`);
      const data = await res.json();
      if (data.success) setUgpProperties(data.data || []);
      else setUgpProperties([]);
    } catch {
      setUgpProperties([]);
    } finally {
      setLoadingUgpProperties(false);
    }
  }, [assignedApps, selectedGroupsPeriodsId, availableGroupsPeriods]);

  useEffect(() => {
    reloadUgpProperties();
  }, [reloadUgpProperties]);

  useEffect(() => {
    if (!studentInfoBadgePicker) return undefined;
    const close = (e) => {
      if (studentInfoBadgesWrapRef.current?.contains(e.target)) return;
      if (e.target?.closest?.('.profile-choice-badge-menu--portal')) return;
      setStudentInfoBadgePicker(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [studentInfoBadgePicker]);

  const syncStudentInfoBadgeHeights = useCallback(() => {
    const root = studentInfoBadgesWrapRef.current;
    if (!root) return;
    const cells = root.querySelectorAll('.profile-si-badge-cell');
    if (!cells.length) return;
    cells.forEach((cell) => {
      cell.style.height = '';
    });
    let maxH = 40;
    cells.forEach((cell) => {
      maxH = Math.max(maxH, Math.ceil(cell.getBoundingClientRect().height));
    });
    const heightPx = `${maxH}px`;
    cells.forEach((cell) => {
      cell.style.height = heightPx;
    });
  }, []);

  useLayoutEffect(() => {
    if (!sectionsExpanded.studentInformation) return undefined;
    syncStudentInfoBadgeHeights();
    const root = studentInfoBadgesWrapRef.current;
    if (!root) return undefined;
    const ro = new ResizeObserver(() => syncStudentInfoBadgeHeights());
    ro.observe(root);
    window.addEventListener('resize', syncStudentInfoBadgeHeights);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncStudentInfoBadgeHeights);
    };
  }, [
    sectionsExpanded.studentInformation,
    studentInfoDraft,
    studentInfoBadgePicker,
    syncStudentInfoBadgeHeights
  ]);

  // Load student info when student_information app is assigned and we have ugp id
  useEffect(() => {
    const hasStudentInfo = (assignedApps || []).some(a => a.slug === APP_SLUGS.STUDENT_INFORMATION);
    const currentGp = (availableGroupsPeriods || []).find(
      gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!hasStudentInfo || !ugpId) {
      setStudentInfo(null);
      setStudentInfoDraft({});
      setStudentInfoReady(false);
      return;
    }
    let cancelled = false;
    setStudentInfoReady(false);
    const load = async () => {
      try {
        const res = await fetch(`/api/student-info?user_groups_periods_id=${ugpId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success) {
          const row = data.data;
          setStudentInfo(row || null);
          setStudentInfoDraft(studentInfoDraftFromRow(row));
        } else {
          setStudentInfo(null);
          setStudentInfoDraft(getDefaultStudentInfoDraft());
        }
      } catch {
        if (cancelled) return;
        setStudentInfo(null);
        setStudentInfoDraft(getDefaultStudentInfoDraft());
      } finally {
        if (!cancelled) setStudentInfoReady(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [assignedApps, selectedGroupsPeriodsId, availableGroupsPeriods]);

  const loadStudentInfoVendorOptions = useCallback(async () => {
    const studentInfoApp = (assignedApps || []).find((a) => a.slug === APP_SLUGS.STUDENT_INFORMATION);
    const currentGp = (availableGroupsPeriods || []).find(
      (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!studentInfoApp || !ugpId) return [];
    setStudentInfoVendorOptionsLoading(true);
    try {
      const params = new URLSearchParams({
        user_groups_periods_id: ugpId,
        field: 'vendor_groups_periods_id',
        assignment_metadata: JSON.stringify(studentInfoApp.metadata || {})
      });
      const res = await fetchWithAuth(`/api/student-info/field-options?${params.toString()}`);
      const data = await res.json();
      if (data.success && data.data) {
        const vendor = data.data.vendor_groups_periods_id || [];
        setStudentInfoFieldOptions((prev) => ({
          ...prev,
          vendor_groups_periods_id: vendor
        }));
        return vendor;
      }
      setStudentInfoFieldOptions((prev) => ({ ...prev, vendor_groups_periods_id: [] }));
      return [];
    } catch {
      setStudentInfoFieldOptions((prev) => ({ ...prev, vendor_groups_periods_id: [] }));
      return [];
    } finally {
      setStudentInfoVendorOptionsLoading(false);
    }
  }, [assignedApps, availableGroupsPeriods, selectedGroupsPeriodsId, fetchWithAuth]);

  useEffect(() => {
    const studentInfoApp = (assignedApps || []).find((a) => a.slug === APP_SLUGS.STUDENT_INFORMATION);
    const currentGp = (availableGroupsPeriods || []).find(
      (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!studentInfoApp || !ugpId) {
      setStudentInfoFieldOptions((prev) => ({ ...prev, track_period_id: [] }));
      return;
    }
    let cancelled = false;
    const loadTrackOptions = async () => {
      try {
        const params = new URLSearchParams({
          user_groups_periods_id: ugpId,
          field: 'track_period_id',
          assignment_metadata: JSON.stringify(studentInfoApp.metadata || {})
        });
        const res = await fetchWithAuth(`/api/student-info/field-options?${params.toString()}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success && data.data) {
          setStudentInfoFieldOptions((prev) => ({
            ...prev,
            track_period_id: data.data.track_period_id || []
          }));
        } else {
          setStudentInfoFieldOptions((prev) => ({ ...prev, track_period_id: [] }));
        }
      } catch {
        if (!cancelled) {
          setStudentInfoFieldOptions((prev) => ({ ...prev, track_period_id: [] }));
        }
      }
    };
    loadTrackOptions();
    return () => {
      cancelled = true;
    };
  }, [assignedApps, selectedGroupsPeriodsId, availableGroupsPeriods, fetchWithAuth]);

  useEffect(() => {
    const studentInfoApp = (assignedApps || []).find((a) => a.slug === APP_SLUGS.STUDENT_INFORMATION);
    const currentGp = (availableGroupsPeriods || []).find(
      (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!studentInfoApp || !ugpId) {
      setStudentInfoFieldOptions((prev) => ({ ...prev, primary_pathway: [] }));
      return;
    }
    let cancelled = false;
    const loadPathwayOptions = async () => {
      try {
        const params = new URLSearchParams({
          user_groups_periods_id: ugpId,
          field: 'primary_pathway',
          assignment_metadata: JSON.stringify(studentInfoApp.metadata || {})
        });
        const res = await fetchWithAuth(`/api/student-info/field-options?${params.toString()}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success && data.data) {
          setStudentInfoFieldOptions((prev) => ({
            ...prev,
            primary_pathway: data.data.primary_pathway || []
          }));
        } else {
          setStudentInfoFieldOptions((prev) => ({ ...prev, primary_pathway: [] }));
        }
      } catch {
        if (!cancelled) {
          setStudentInfoFieldOptions((prev) => ({ ...prev, primary_pathway: [] }));
        }
      }
    };
    loadPathwayOptions();
    return () => {
      cancelled = true;
    };
  }, [assignedApps, selectedGroupsPeriodsId, availableGroupsPeriods, fetchWithAuth]);

  useEffect(() => {
    const vendorId = studentInfoDraft.vendor_groups_periods_id;
    if (!vendorId) {
      setResolvedVendorGroupName('');
      return undefined;
    }
    const fromOptions = (studentInfoFieldOptions.vendor_groups_periods_id || []).find(
      (o) => String(o.id) === String(vendorId)
    );
    if (fromOptions?.group_name) {
      setResolvedVendorGroupName(fromOptions.group_name);
      return undefined;
    }
    if (
      String(vendorId) === String(studentInfo?.vendor_groups_periods_id) &&
      studentInfo?.vendor_group_name
    ) {
      setResolvedVendorGroupName(studentInfo.vendor_group_name);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/student-info/vendor-groups-periods-label?groups_periods_id=${encodeURIComponent(vendorId)}`
        );
        const data = await res.json();
        if (cancelled) return;
        setResolvedVendorGroupName(data.success && data.data?.group_name ? data.data.group_name : '');
      } catch {
        if (!cancelled) setResolvedVendorGroupName('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    studentInfoDraft.vendor_groups_periods_id,
    studentInfo?.vendor_groups_periods_id,
    studentInfo?.vendor_group_name,
    studentInfoFieldOptions.vendor_groups_periods_id
  ]);

  useEffect(() => {
    const hasCk = (assignedApps || []).some(a => a.slug === APP_SLUGS.CREDENTIALS_AND_KEYS);
    const currentGp = (availableGroupsPeriods || []).find(
      gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!hasCk || !ugpId) {
      setCredentialsKeys([]);
      return;
    }
    const load = async () => {
      try {
        const res = await fetch(`/api/credentials-keys?user_groups_periods_id=${ugpId}`);
        const data = await res.json();
        if (data.success) setCredentialsKeys(data.data || []);
        else setCredentialsKeys([]);
      } catch {
        setCredentialsKeys([]);
      }
    };
    load();
  }, [assignedApps, selectedGroupsPeriodsId, availableGroupsPeriods]);

  useEffect(() => {
    const hasAttachments = (assignedApps || []).some(a => a.slug === APP_SLUGS.ATTACHMENTS);
    const currentGp = (availableGroupsPeriods || []).find(
      gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!hasAttachments || !ugpId) {
      setDocuments([]);
      return;
    }
    const load = async () => {
      try {
        const res = await fetch(`/api/documents?user_groups_periods_id=${encodeURIComponent(ugpId)}`);
        const data = await res.json();
        if (data.success) setDocuments(data.data || []);
        else setDocuments([]);
      } catch {
        setDocuments([]);
      }
    };
    load();
  }, [assignedApps, selectedGroupsPeriodsId, availableGroupsPeriods]);

  useEffect(() => {
    const hasPayments = (assignedApps || []).some(a => a.slug === APP_SLUGS.PAYMENTS);
    const currentGp = (availableGroupsPeriods || []).find(
      gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!hasPayments || !ugpId || !selectedGroupsPeriodsId) {
      setPayments([]);
      return;
    }
    const load = async () => {
      try {
        const res = await fetch(
          `/api/payments/for-ugp?user_groups_periods_id=${encodeURIComponent(ugpId)}&groups_periods_id=${encodeURIComponent(selectedGroupsPeriodsId)}`
        );
        const data = await res.json();
        if (data.success) setPayments(data.data || []);
        else setPayments([]);
      } catch {
        setPayments([]);
      }
    };
    load();
  }, [assignedApps, selectedGroupsPeriodsId, availableGroupsPeriods]);

  // When Add Person Link modal opens, clear search results
  useEffect(() => {
    if (!showAddPersonLinkModal) return;
    setAddPersonLinkUserResults([]);
  }, [showAddPersonLinkModal]);

  const updateAddPlConnector = useCallback(() => {
    const flow = addPlFlowRef.current;
    if (!flow) return;
    const card = flow.querySelector('.profile-pl-rel-card.is-selected');
    const target = addPlTargetHeadRef.current;
    if (!card || !target) {
      setAddPlConnectorPath('');
      return;
    }
    const fr = flow.getBoundingClientRect();
    const cr = card.getBoundingClientRect();
    const tr = target.getBoundingClientRect();
    const startX = cr.right - fr.left;
    const startY = cr.top - fr.top + cr.height / 2;
    const endY = tr.top - fr.top + tr.height / 2;
    const endX = tr.left - fr.left + 12;
    const targetLeftX = tr.left - fr.left;
    const midX = startX + Math.max(16, (targetLeftX - startX) * 0.5);
    const d = [
      `M ${startX.toFixed(1)} ${startY.toFixed(1)}`,
      `L ${midX.toFixed(1)} ${startY.toFixed(1)}`,
      `L ${midX.toFixed(1)} ${endY.toFixed(1)}`,
      `L ${endX.toFixed(1)} ${endY.toFixed(1)}`
    ].join(' ');
    setAddPlConnectorPath(d);
  }, []);

  useLayoutEffect(() => {
    if (!showAddPersonLinkModal) {
      setAddPlConnectorPath('');
      return undefined;
    }
    const raf = requestAnimationFrame(() => updateAddPlConnector());
    const flow = addPlFlowRef.current;
    if (!flow) {
      return () => cancelAnimationFrame(raf);
    }
    const ro = new ResizeObserver(() => updateAddPlConnector());
    ro.observe(flow);
    window.addEventListener('resize', updateAddPlConnector);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', updateAddPlConnector);
    };
  }, [
    showAddPersonLinkModal,
    addPersonLinkRelationshipType,
    addPersonLinkMode,
    addPersonLinkSelectedUser,
    addPersonLinkUserResults.length,
    updateAddPlConnector
  ]);

  // Initialize form data when forms are loaded and user is available
  useEffect(() => {
    if (user && forms.length > 0) {
      forms.forEach(form => {
        const formId = form.id;
        // Only load if not already loaded
        if (existingSubmissions[formId] === undefined && submissionsByFormId[formId] === undefined) {
          // We'll load on first expansion, but ensure form data structure exists
          if (formDataByFormId[formId] === undefined) {
            const defaultAnswers = getDefaultInitialAnswers(form);
            setFormDataByFormId(prev => ({ ...prev, [formId]: defaultAnswers }));
            setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...defaultAnswers } }));
          }
        }
      });
    }
  }, [user, forms]);

  const loadUserProfile = async (id) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/users/${id}`);
      const data = await response.json();
      
      if (data.success) {
        const previousCanLogin = user?.can_login;
        setUser(data.data);
        
        // Extract available groups_periods from user data
        const groupsPeriods = (data.data.groups_periods || []).filter(gp => gp.groups_periods_id);
        setAvailableGroupsPeriods(groupsPeriods);

        const enrollmentFromUrl = getProfileEnrollmentFromSearchParams(location.search);
        const resolvedGpId = resolveEnrollmentGroupsPeriodsId(groupsPeriods, enrollmentFromUrl);
        if (resolvedGpId) {
          setSelectedGroupsPeriodsId(resolvedGpId);
        } else if (groupsPeriods.length > 0) {
          setSelectedGroupsPeriodsId(String(groupsPeriods[0].groups_periods_id));
        }
        
        // If can_login changed from true to false, collapse password section
        if (previousCanLogin === true && data.data.can_login === false) {
          setSectionsExpanded(prev => ({ ...prev, password: false }));
        }
        
        // Initialize edit form data
        const initialData = {
          email: data.data.email || '',
          first_name: data.data.first_name || '',
          last_name: data.data.last_name || '',
          password: '',
          dob: formatDateForInput(data.data.dob),
          platformAccess: !!data.data.can_login
        };
        setEditFormData(initialData);
        setOriginalFormData({ ...initialData });
        setShowPlatformPasswordField(false);
      } else {
        setError(data.message || 'Failed to load user profile');
      }
    } catch (error) {
      setError('Failed to load user profile');
      console.error('Error loading user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserForms = async (userId, groupsPeriodsId = null) => {
    try {
      let url = `/api/relationships/user/${userId}/forms`;
      if (groupsPeriodsId) {
        url += `?groups_periods_id=${encodeURIComponent(groupsPeriodsId)}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        const newForms = data.data || [];
        // Get list of form IDs that are no longer available
        const currentFormIds = new Set(newForms.map(f => f.id));
        const previousFormIds = new Set(forms.map(f => f.id));
        
        setForms(newForms);
        
        // Only clear state for forms that are no longer available
        // This prevents losing state when switching groups_periods if forms overlap
        setExpandedFormIds(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(formId => {
            if (!currentFormIds.has(formId)) {
              delete updated[formId];
            }
          });
          return updated;
        });
        setExistingSubmissions(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(formId => {
            if (!currentFormIds.has(formId)) {
              delete updated[formId];
            }
          });
          return updated;
        });
        setSubmissionsByFormId(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(formId => {
            if (!currentFormIds.has(formId)) {
              delete updated[formId];
            }
          });
          return updated;
        });
        setSelectedSubmissionIdByFormId(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(formId => {
            if (!currentFormIds.has(formId)) {
              delete updated[formId];
            }
          });
          return updated;
        });
        setFormDataByFormId(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(formId => {
            if (!currentFormIds.has(formId)) {
              delete updated[formId];
            }
          });
          return updated;
        });
        setOriginalFormDataByFormId(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(formId => {
            if (!currentFormIds.has(formId)) {
              delete updated[formId];
            }
          });
          return updated;
        });
      }
    } catch (error) {
      console.error('Error loading user forms:', error);
    }
  };

  const getUgpIdForForms = useCallback(() => {
    const currentGp = (availableGroupsPeriods || []).find(
      (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    return currentGp?.id || null;
  }, [availableGroupsPeriods, selectedGroupsPeriodsId]);

  const hydrateSubmissionFiles = useCallback(async (form, submissionId, answers) => {
    if (!form || !submissionId) {
      return submissionAnswersToFormData(answers || {});
    }
    const hydrated = await hydrateFormSubmissionFileAnswers(form, answers, submissionId);
    setFormDocumentUrlByFormId((prev) => ({
      ...prev,
      [form.id]: hydrated.documentUrlById
    }));
    return hydrated.formData || submissionAnswersToFormData(hydrated.answers);
  }, []);

  const loadExistingSubmission = async (formId, form = null) => {
    try {
      const response = await fetch(`/api/form-submissions?user_id=${user.id}&form_id=${formId}`);
      const data = await response.json();
      if (data.success && data.data.length > 0) {
        const latestSubmission = data.data[0];
        setExistingSubmissions(prev => ({ ...prev, [formId]: latestSubmission }));
        // Initialize form data tracking
        const answers = latestSubmission.answers || {};
        const hydrated = await hydrateSubmissionFiles(form, latestSubmission.id, answers);
        setFormDataByFormId(prev => ({ ...prev, [formId]: hydrated }));
        setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...hydrated } }));
        return latestSubmission;
      }
      setExistingSubmissions(prev => ({ ...prev, [formId]: null }));
      // Initialize empty form data with defaults if form provided
      const defaultAnswers = form ? getDefaultInitialAnswers(form) : {};
      setFormDataByFormId(prev => ({ ...prev, [formId]: defaultAnswers }));
      setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...defaultAnswers } }));
      return null;
    } catch (err) {
      console.error('Error loading existing submission:', err);
      setExistingSubmissions(prev => ({ ...prev, [formId]: null }));
      const defaultAnswers = form ? getDefaultInitialAnswers(form) : {};
      setFormDataByFormId(prev => ({ ...prev, [formId]: defaultAnswers }));
      setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...defaultAnswers } }));
      return null;
    }
  };

  const loadFormSubmissions = async (formId, form = null) => {
    try {
      const response = await fetch(`/api/form-submissions?user_id=${user.id}&form_id=${formId}`);
      const data = await response.json();
      if (data.success) {
        setSubmissionsByFormId(prev => ({ ...prev, [formId]: data.data || [] }));
        // Also set latest into existingSubmissions for non-open flows
        const latest = (data.data || [])[0] || null;
        setExistingSubmissions(prev => ({ ...prev, [formId]: latest }));
        // Update form data tracking if we have a submission
        if (latest && latest.answers) {
          const hydrated = await hydrateSubmissionFiles(form, latest.id, latest.answers);
          setFormDataByFormId(prev => ({ ...prev, [formId]: hydrated }));
          setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...hydrated } }));
        } else {
          // Initialize with defaults if no submission exists
          const defaultAnswers = form ? getDefaultInitialAnswers(form) : {};
          setFormDataByFormId(prev => ({ ...prev, [formId]: defaultAnswers }));
          setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...defaultAnswers } }));
        }
        return data.data || [];
      }
      setSubmissionsByFormId(prev => ({ ...prev, [formId]: [] }));
      // Initialize with defaults if no submissions
      const defaultAnswers = form ? getDefaultInitialAnswers(form) : {};
      setFormDataByFormId(prev => ({ ...prev, [formId]: defaultAnswers }));
      setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...defaultAnswers } }));
      return [];
    } catch (err) {
      console.error('Error loading submissions list:', err);
      setSubmissionsByFormId(prev => ({ ...prev, [formId]: [] }));
      const defaultAnswers = form ? getDefaultInitialAnswers(form) : {};
      setFormDataByFormId(prev => ({ ...prev, [formId]: defaultAnswers }));
      setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...defaultAnswers } }));
      return [];
    }
  };

  const toggleFormExpansion = async (form) => {
    if (!canOpenProfileForm(formPerms, formPermCtx)) return;
    const formId = form.id;
    const wasExpanded = expandedFormIds[formId];
    setExpandedFormIds(prev => ({ ...prev, [formId]: !prev[formId] }));
    // Lazy load existing submission on first expand
    if (!wasExpanded) {
      // On first expand, load submissions list and latest
      if (submissionsByFormId[formId] === undefined) {
        await loadFormSubmissions(formId, form);
      } else if (existingSubmissions[formId] === undefined) {
        await loadExistingSubmission(formId, form);
      }
      // Ensure form data is initialized (load functions handle this, but double-check)
      if (formDataByFormId[formId] === undefined || originalFormDataByFormId[formId] === undefined) {
        const existing = existingSubmissions[formId];
        const answers = existing?.answers || getDefaultInitialAnswers(form);
        if (existing?.id) {
          const hydrated = await hydrateSubmissionFiles(form, existing.id, answers);
          setFormDataByFormId(prev => ({ ...prev, [formId]: hydrated }));
          setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...hydrated } }));
        } else {
          setFormDataByFormId(prev => ({ ...prev, [formId]: answers }));
          setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...answers } }));
        }
      }
    }
  };

  const openAssignmentForm = async (form, { submissionId = null } = {}) => {
    if (!canOpenProfileForm(formPerms, formPermCtx)) return;
    const formId = form.id;
    setActiveAssignmentFormId(formId);
    const list = submissionsByFormId[formId] === undefined
      ? await loadFormSubmissions(formId, form)
      : (submissionsByFormId[formId] || []);
    if (list.length > 0) {
      const preferredId =
        submissionId && list.some((s) => String(s.id) === String(submissionId))
          ? submissionId
          : selectedSubmissionIdByFormId[formId] || list[0].id;
      setSelectedSubmissionIdByFormId(prev => ({ ...prev, [formId]: preferredId }));
      const hit = list.find(s => String(s.id) === String(preferredId)) || list[0];
      const answers = hit?.answers || getDefaultInitialAnswers(form);
      const hydrated = await hydrateSubmissionFiles(form, hit?.id, answers);
      setFormDataByFormId(prev => ({ ...prev, [formId]: hydrated }));
      setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...hydrated } }));
    } else if (canCreateSubmission) {
      const defaults = getDefaultInitialAnswers(form);
      setSelectedSubmissionIdByFormId(prev => ({ ...prev, [formId]: null }));
      setFormDataByFormId(prev => ({ ...prev, [formId]: defaults }));
      setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...defaults } }));
    }
  };

  const closeAssignmentForm = () => {
    setActiveAssignmentFormId(null);
  };

  const collapseLeftColumn = () => {
    leftColumnWidthBeforeCollapseRef.current = leftColumnWidthPx;
    setLeftColumnCollapsed(true);
  };

  const expandLeftColumn = () => {
    setLeftColumnCollapsed(false);
    setLeftColumnWidthPx(leftColumnWidthBeforeCollapseRef.current || LEFT_COLUMN_DEFAULT_PX);
  };

  const selectAssignmentSubmission = async (form, submissionId) => {
    const formId = form.id;
    const list = submissionsByFormId[formId] || [];
    const hit = list.find(s => String(s.id) === String(submissionId));
    if (!hit) return;
    setSelectedSubmissionIdByFormId(prev => ({ ...prev, [formId]: submissionId }));
    const answers = hit.answers || getDefaultInitialAnswers(form);
    const hydrated = await hydrateSubmissionFiles(form, submissionId, answers);
    setFormDataByFormId(prev => ({ ...prev, [formId]: hydrated }));
    setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...hydrated } }));
  };

  const startNewAssignmentSubmission = (form) => {
    if (!canCreateSubmission) return;
    const formId = form.id;
    const defaults = getDefaultInitialAnswers(form);
    setSelectedSubmissionIdByFormId(prev => ({ ...prev, [formId]: null }));
    setExistingSubmissions(prev => ({ ...prev, [formId]: null }));
    setFormDataByFormId(prev => ({ ...prev, [formId]: defaults }));
    setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...defaults } }));
    setFormDocumentUrlByFormId(prev => ({ ...prev, [formId]: {} }));
    clearFormPendingBlobs(pendingFormFileBlobsRef.current, formId);
  };

  const buildFormSubmissionReferencePayload = (form) => {
    const existing = existingSubmissions?.[form?.id];
    const ref = resolveSubmissionReferenceFromFormRelationship(form, user?.id, existing);
    return {
      reference_table: ref.reference_table,
      reference_id: ref.reference_id,
      relationship_id: form?.relationship_id || undefined,
      groups_periods_id:
        form?.parent_table === 'groups_periods'
          ? form.parent_id
          : selectedGroupsPeriodsId || undefined,
      user_groups_periods_id: form?.user_groups_periods_id || getUgpIdForForms() || undefined
    };
  };

  const registerPendingFormFile = (formId, localKey, file) => {
    if (!pendingFormFileBlobsRef.current[formId]) {
      pendingFormFileBlobsRef.current[formId] = {};
    }
    pendingFormFileBlobsRef.current[formId][localKey] = file;
  };

  const releasePendingFormFile = (formId, localKey) => {
    if (pendingFormFileBlobsRef.current[formId]) {
      delete pendingFormFileBlobsRef.current[formId][localKey];
    }
  };

  const getDefaultInitialAnswers = (form) => {
    return getDefaultInitialAnswersHelper(form);
  };

  const formatDateForInput = (value) => {
    if (!value) return '';
    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  };

  const formatDisplayDate = (value) => {
    if (!value) return '';
    // If value is plain YYYY-MM-DD, show as-is to avoid UTC shift
    const m = String(value).match(/^\d{4}-\d{2}-\d{2}$/);
    if (m) return value;
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString();
    } catch (_e) {
      return String(value);
    }
  };

  const sanitizeForKey = (text) => {
    return String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
  };

  const generateUuid = () => {
    try {
      if (window && window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
      }
    } catch (_e) {}
    // Fallback simple UUID v4 generator
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const clampColumnScrollPositions = useCallback(() => {
    document.querySelectorAll('.profile-column-scroll').forEach((el) => {
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll >= 0 && el.scrollTop > maxScroll) {
        el.scrollTop = maxScroll;
      }
    });
  }, []);

  const toggleSection = (sectionName) => {
    setSectionsExpanded(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // After expand/collapse, flex scroll areas can keep a stale scrollTop past the new max.
  useLayoutEffect(() => {
    clampColumnScrollPositions();
  }, [sectionsExpanded, expandedFormIds, leftColumnWidthPx, profileViewConfig, clampColumnScrollPositions]);

  const getStudentInfoOriginal = () => studentInfoDraftFromRow(studentInfo);

  const hasStudentInfoChanges = () => {
    if (!studentInfoReady) return false;
    const orig = getStudentInfoOriginal();
    const d = studentInfoDraft;
    return (
      (d.status ?? '') !== (orig.status ?? '') ||
      (d.enrollment_date ?? '') !== (orig.enrollment_date ?? '') ||
      (d.exit_date ?? '') !== (orig.exit_date ?? '') ||
      (d.grade_level ?? '') !== (orig.grade_level ?? '') ||
      (d.previously_enrolled ?? false) !== (orig.previously_enrolled ?? false) ||
      (d.transfer_credits ?? '') !== (orig.transfer_credits ?? '') ||
      String(d.vendor_groups_periods_id ?? '') !== String(orig.vendor_groups_periods_id ?? '') ||
      String(d.track_period_id ?? '') !== String(orig.track_period_id ?? '') ||
      String(d.primary_pathway ?? '') !== String(orig.primary_pathway ?? '')
    );
  };

  const handleSaveStudentInfo = async () => {
    const currentGp = (availableGroupsPeriods || []).find(gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId));
    const ugpId = currentGp?.id;
    const studentInfoApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.STUDENT_INFORMATION);
    if (!ugpId || !studentInfoApp) return;
    setSavingStudentInfo(true);
    try {
      const d = studentInfoDraft;
      const payload = {
        user_groups_periods_id: ugpId,
        withdrawal_sync_ugp_end_date: studentInfoApp.metadata?.withdrawal_sync_ugp_end_date === true,
        assignment_metadata: studentInfoApp.metadata || {},
        status: d.status || null,
        enrollment_date: d.enrollment_date || null,
        exit_date: d.exit_date || null,
        grade_level: d.grade_level || null,
        previously_enrolled: d.previously_enrolled ?? null,
        transfer_credits: d.transfer_credits === '' ? null : parseFloat(d.transfer_credits),
        vendor_groups_periods_id: d.vendor_groups_periods_id || null,
        track_period_id: d.track_period_id || null,
        primary_pathway: d.primary_pathway || null
      };
      const res = await fetchWithAuth('/api/student-info', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setStudentInfo(data.data);
        setStudentInfoDraft(studentInfoDraftFromRow(data.data));
        if (data.data?.vendor_group_name) {
          setResolvedVendorGroupName(data.data.vendor_group_name);
        }
        setStudentInfoReady(true);
      } else {
        setErrorModal({ title: 'Error', message: data.message || 'Failed to save student info' });
      }
    } catch (e) {
      setErrorModal({ title: 'Error', message: 'Failed to save student info' });
    } finally {
      setSavingStudentInfo(false);
    }
  };

  const handleCancelStudentInfo = () => {
    setStudentInfoDraft(getStudentInfoOriginal());
  };

  const reloadCredentialsKeys = async () => {
    const currentGp = (availableGroupsPeriods || []).find(gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId));
    const ugpId = currentGp?.id;
    if (!ugpId) return;
    try {
      const res = await fetch(`/api/credentials-keys?user_groups_periods_id=${ugpId}`);
      const data = await res.json();
      if (data.success) setCredentialsKeys(data.data || []);
    } catch {
      setCredentialsKeys([]);
    }
  };

  const resolveCkPlatformLabel = (ckApp) => {
    const meta = ckApp?.metadata || {};
    const control = String(meta.credential_control || 'both');
    const locked = Array.isArray(meta.locked_platforms) ? meta.locked_platforms : [];
    if (control === 'freeform_only') {
      return String(ckForm.platform_custom || '').trim();
    }
    if (control === 'locked_only') {
      const p = String(ckForm.platform_pick || '').trim();
      if (!locked.includes(p)) return '';
      return p;
    }
    if (ckForm.platform_pick === '__other__') {
      return String(ckForm.platform_custom || '').trim();
    }
    return String(ckForm.platform_pick || '').trim();
  };

  const openAddCredentialsKeysEntry = (options = {}) => {
    const ckApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.CREDENTIALS_AND_KEYS);
    const locked = Array.isArray(ckApp?.metadata?.locked_platforms) ? ckApp.metadata.locked_platforms : [];
    const control = String(ckApp?.metadata?.credential_control || 'both');
    const firstPick = control === 'locked_only' && locked.length ? locked[0] : '';
    const presetTitle = options?.title != null ? String(options.title).trim() : '';
    const presetType = options?.entry_type ?? options?.entryType;
    const entryType =
      String(presetType || '')
        .trim()
        .toLowerCase() === 'key'
        ? 'key'
        : 'credentials';
    setCkEditId(null);
    setCkForm({
      entry_type: entryType,
      platform_pick: firstPick,
      platform_custom: '',
      title: presetTitle,
      email: '',
      username: '',
      password: '',
      notes: ''
    });
    setShowCkModal(true);
  };

  const openEditCredentialsKeysEntry = (row) => {
    const ckApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.CREDENTIALS_AND_KEYS);
    const locked = Array.isArray(ckApp?.metadata?.locked_platforms) ? ckApp.metadata.locked_platforms : [];
    const control = String(ckApp?.metadata?.credential_control || 'both');
    const label = row.platform_label || '';
    const inLocked = locked.includes(label);
    let pick = '';
    let custom = '';
    if (control === 'freeform_only') {
      custom = label;
    } else if (control === 'locked_only') {
      pick = inLocked ? label : '';
    } else {
      pick = inLocked ? label : '__other__';
      custom = inLocked ? '' : label;
    }
    const et = String(row.entry_type || 'credentials').toLowerCase() === 'key' ? 'key' : 'credentials';
    setCkEditId(row.id);
    setCkForm({
      entry_type: et,
      platform_pick: pick,
      platform_custom: custom,
      title: row.title ?? '',
      email: row.email ?? '',
      username: row.username ?? '',
      password: row.password ?? '',
      notes: row.notes ?? ''
    });
    setShowCkModal(true);
  };

  const handleSaveCredentialsKeysEntry = async () => {
    const ckApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.CREDENTIALS_AND_KEYS);
    const currentGp = (availableGroupsPeriods || []).find(gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId));
    const ugpId = currentGp?.id;
    if (!ckApp || !ugpId) return;
    const platformLabel = resolveCkPlatformLabel(ckApp);
    if (!platformLabel) {
      setErrorModal({ title: 'Validation', message: 'Choose or enter a platform name.' });
      return;
    }
    const et = String(ckForm.entry_type || 'credentials').toLowerCase() === 'key' ? 'key' : 'credentials';
    const title = String(ckForm.title || '').trim() || null;
    const notes = String(ckForm.notes || '').trim() || null;

    let body;
    if (et === 'key') {
      const keyPassword = String(ckForm.password || '').trim();
      if (!keyPassword) {
        setErrorModal({ title: 'Validation', message: 'Enter a value for the key.' });
        return;
      }
      body = {
        entry_type: 'key',
        platform_label: platformLabel,
        title,
        email: null,
        username: null,
        password: keyPassword,
        notes
      };
    } else {
      const email = String(ckForm.email || '').trim() || null;
      const username = String(ckForm.username || '').trim() || null;
      const password = String(ckForm.password || '').trim() || null;
      const hasCred = [email, username, password].some(v => v != null && v !== '');
      if (!hasCred) {
        setErrorModal({ title: 'Validation', message: 'Enter at least one of email, username, or password.' });
        return;
      }
      body = {
        entry_type: 'credentials',
        platform_label: platformLabel,
        title,
        email,
        username,
        password,
        notes
      };
    }

    setSavingCk(true);
    try {
      let res;
      if (ckEditId) {
        res = await fetchWithAuth(`/api/credentials-keys/${ckEditId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      } else {
        res = await fetchWithAuth('/api/credentials-keys', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_groups_periods_id: ugpId, ...body })
        });
      }
      const data = await res.json();
      if (data.success) {
        setShowCkModal(false);
        await reloadCredentialsKeys();
      } else {
        setErrorModal({ title: 'Error', message: data.message || 'Failed to save entry' });
      }
    } catch {
      setErrorModal({ title: 'Error', message: 'Failed to save entry' });
    } finally {
      setSavingCk(false);
    }
  };

  const reloadDocuments = async () => {
    const currentGp = (availableGroupsPeriods || []).find(
      gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!ugpId) return;
    try {
      const res = await fetch(`/api/documents?user_groups_periods_id=${encodeURIComponent(ugpId)}`);
      const data = await res.json();
      if (data.success) setDocuments(data.data || []);
    } catch {
      setDocuments([]);
    }
  };

  const getPaymentUgpId = () => {
    const currentGp = (availableGroupsPeriods || []).find(
      gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    return currentGp?.id || null;
  };

  // Reload the merged schedule + ledger; if the editor is open, re-sync its charge.
  const reloadPayments = async () => {
    const ugpId = getPaymentUgpId();
    if (!ugpId || !selectedGroupsPeriodsId) return;
    try {
      const res = await fetch(
        `/api/payments/for-ugp?user_groups_periods_id=${encodeURIComponent(ugpId)}&groups_periods_id=${encodeURIComponent(selectedGroupsPeriodsId)}`
      );
      const data = await res.json();
      const items = data.success ? data.data || [] : [];
      setPayments(items);
      setPaymentEditModal((m) => {
        if (!m) return m;
        const key = m.item.requirement_id || m.item.label;
        const fresh = items.find(
          (it) => (it.requirement_id || it.label) === key
        );
        return fresh ? { ...m, item: fresh } : m;
      });
    } catch {
      setPayments([]);
    }
  };

  const openPaymentEditor = (item) => {
    setPaymentEditModal({ item });
  };

  // One-click status via the ledger translation endpoint.
  const handleQuickSetPayment = async (status) => {
    if (!paymentEditModal) return;
    const { item } = paymentEditModal;
    const ugpId = getPaymentUgpId();
    if (!ugpId || !item.requirement_id) return;
    setSavingPayment(true);
    try {
      const res = await fetchWithAuth('/api/payments/quick-set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_groups_periods_id: ugpId,
          requirement_id: item.requirement_id,
          status
        })
      });
      const data = await res.json();
      if (data.success) await reloadPayments();
      else setErrorModal({ title: 'Error', message: data.message || 'Failed to update status' });
    } catch {
      setErrorModal({ title: 'Error', message: 'Failed to update status' });
    } finally {
      setSavingPayment(false);
    }
  };

  const handleAddTransaction = async (draft) => {
    if (!paymentEditModal) return;
    const { item } = paymentEditModal;
    const ugpId = getPaymentUgpId();
    if (!ugpId) return;
    setSavingPayment(true);
    try {
      const res = await fetchWithAuth('/api/payments/transactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_groups_periods_id: ugpId,
          requirement_id: item.requirement_id || null,
          charge_label: item.label || null,
          txn_type: draft.txn_type,
          amount: draft.amount === '' || draft.amount == null ? null : draft.amount,
          is_partial: draft.txn_type === 'payment' ? draft.is_partial === true : false,
          reason: draft.reason || null,
          method: draft.method || null,
          occurred_on: dateTimeLocalToStorage(draft.occurred_on),
          notes: draft.notes || null,
          currency: item.currency
        })
      });
      const data = await res.json();
      if (data.success) await reloadPayments();
      else {
        setErrorModal({ title: 'Error', message: data.message || 'Failed to record transaction' });
        return false;
      }
      return true;
    } catch {
      setErrorModal({ title: 'Error', message: 'Failed to record transaction' });
      return false;
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeleteTransaction = async (txnId) => {
    setSavingPayment(true);
    try {
      const res = await fetchWithAuth(`/api/payments/transactions/${txnId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) await reloadPayments();
      else setErrorModal({ title: 'Error', message: data.message || 'Failed to delete transaction' });
    } catch {
      setErrorModal({ title: 'Error', message: 'Failed to delete transaction' });
    } finally {
      setSavingPayment(false);
    }
  };

  const uploadDocumentFile = async (file, docId) => {
    const customFilename = docId || generateUuid();
    const presignRes = await fetch('/api/s3/presigned-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type,
        fileLocation: 'documents',
        useCustomFilename: true,
        customFilename
      })
    });
    const presignData = await presignRes.json();
    if (!presignData.success) throw new Error(presignData.message || 'Failed to get presigned URL');
    const { presignedUrl, s3Key, bucket, region } = presignData.data;
    const uploadRes = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type }
    });
    if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.statusText}`);
    return {
      url: `https://${bucket}.s3.${region}.amazonaws.com/${s3Key}`,
      mimeType: file.type || null,
      fileName: file.name,
      fileSizeBytes: file.size || null
    };
  };

  const openAddDocument = (attachmentsApp, options = {}) => {
    const categories = getDocumentCategoryOptions(attachmentsApp);
    const allowUpload = attachmentsApp?.metadata?.allow_upload !== false;
    const presetTitle = String(options.title || '').trim();
    setDocEditId(null);
    docEditOriginalRef.current = null;
    setDocSourceTab(allowUpload ? 'file' : 'url');
    setDocForm({
      category: categories[0]?.value || 'general',
      title: presetTitle,
      description: '',
      url: '',
      selectedFile: null,
      existingFileName: null
    });
    setShowDocModal(true);
  };

  const openEditDocument = (row, attachmentsApp) => {
    const categories = getDocumentCategoryOptions(attachmentsApp);
    const allowUpload = attachmentsApp?.metadata?.allow_upload !== false;
    const cat = String(row.category || 'general').toLowerCase();
    const isS3 = String(row.storage_kind || '') === 's3';
    setDocEditId(row.id);
    docEditOriginalRef.current = {
      url: row.url || '',
      storage_kind: row.storage_kind || ''
    };
    setDocSourceTab(allowUpload && isS3 ? 'file' : 'url');
    setDocForm({
      category: categories.length
        ? categories.some((c) => c.value === cat)
          ? cat
          : categories[0].value
        : 'general',
      title: row.title || '',
      description: row.description || '',
      url: row.url || '',
      selectedFile: null,
      existingFileName: row.file_name || (isS3 ? 'Uploaded file' : null)
    });
    setShowDocModal(true);
  };

  const isDocumentContentChange = () => {
    if (!docEditId || !docEditOriginalRef.current) return false;
    if (docForm.selectedFile) return true;
    const originalUrl = String(docEditOriginalRef.current.url || '').trim();
    const nextUrl = String(docForm.url || '').trim();
    return docSourceTab === 'url' && nextUrl !== originalUrl;
  };

  const handleSaveDocument = async (attachmentsApp) => {
    const currentGp = (availableGroupsPeriods || []).find(
      gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    const ugpId = currentGp?.id;
    if (!attachmentsApp || !ugpId) return;

    const title = String(docForm.title || '').trim();
    if (!title) {
      setErrorModal({ title: 'Validation', message: 'Title is required.' });
      return;
    }

    const allowUpload = attachmentsApp?.metadata?.allow_upload !== false;
    const categoryOptions = getDocumentCategoryOptions(attachmentsApp);
    const contentChange = isDocumentContentChange();
    let url = String(docForm.url || '').trim();
    let mimeType = null;
    let fileName = null;
    let fileSizeBytes = null;
    let storageKind = 'external_link';

    if (docSourceTab === 'file') {
      if (!allowUpload) {
        setErrorModal({ title: 'Validation', message: 'File upload is not allowed for this assignment.' });
        return;
      }
      const file = docForm.selectedFile;
      if (file) {
        try {
          const uploaded = await uploadDocumentFile(
            file,
            contentChange || !docEditId ? undefined : docEditId
          );
          url = uploaded.url;
          mimeType = uploaded.mimeType;
          fileName = uploaded.fileName;
          fileSizeBytes = uploaded.fileSizeBytes;
          storageKind = 's3';
        } catch (err) {
          setErrorModal({ title: 'Upload failed', message: err.message || 'Could not upload file.' });
          return;
        }
      } else if (!url) {
        setErrorModal({ title: 'Validation', message: 'Choose a file to upload.' });
        return;
      } else {
        storageKind = 's3';
        fileName = docForm.existingFileName || null;
      }
    } else {
      if (!url) {
        setErrorModal({ title: 'Validation', message: 'Enter a link URL.' });
        return;
      }
      storageKind = url.includes('drive.google.com') || url.includes('docs.google.com')
        ? 'google_drive'
        : 'external_link';
    }

    const body = {
      title,
      description: String(docForm.description || '').trim() || null,
      url,
      storage_kind: storageKind,
      mime_type: mimeType,
      file_name: fileName,
      file_size_bytes: fileSizeBytes
    };
    if (categoryOptions.length > 0) {
      body.category = docForm.category;
    }

    setSavingDoc(true);
    try {
      let res;
      if (docEditId && !contentChange) {
        const metaBody = {
          title,
          description: body.description
        };
        if (categoryOptions.length > 0) {
          metaBody.category = body.category;
        }
        res = await fetchWithAuth(`/api/documents/${docEditId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(metaBody)
        });
      } else {
        const createBody = {
          user_groups_periods_id: ugpId,
          parent_table: 'user_groups_periods',
          parent_id: ugpId,
          ...body
        };
        if (docEditId && contentChange) {
          createBody.replaces_document_id = docEditId;
        }
        res = await fetchWithAuth('/api/documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(createBody)
        });
      }
      const data = await res.json();
      if (data.success) {
        setShowDocModal(false);
        await reloadDocuments();
      } else {
        setErrorModal({ title: 'Error', message: data.message || 'Failed to save document' });
      }
    } catch {
      setErrorModal({ title: 'Error', message: 'Failed to save document' });
    } finally {
      setSavingDoc(false);
    }
  };

  const handleMouseDown = (e, column) => {
    setIsDragging(column);
    setDragStartX(e.clientX);
    setDragStartWidths({ ...columnWidths });
    setDragStartLeftPx(leftColumnWidthPx);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;

      const deltaX = e.clientX - dragStartX;
      const containerWidth = window.innerWidth;
      const deltaPercentage = (deltaX / containerWidth) * 100;

      if (isDragging === 'left') {
        const newLeftPx = Math.max(
          LEFT_COLUMN_MIN_PX,
          Math.min(LEFT_COLUMN_MAX_PX, dragStartLeftPx + deltaX)
        );
        setLeftColumnWidthPx(newLeftPx);
        return;
      }

      setColumnWidths(prev => {
        if (isDragging === 'right') {
          const newRight = Math.max(20, Math.min(60, dragStartWidths.right - deltaPercentage));
          const newMiddle = Math.max(20, dragStartWidths.middle + deltaPercentage / 2);
          const newLeft = Math.max(20, dragStartWidths.left + deltaPercentage / 2);
          return {
            left: newLeft,
            middle: newMiddle,
            right: newRight
          };
        }
        return prev;
      });
    };

    const handleMouseUp = () => {
      setIsDragging(null);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStartX, dragStartWidths, dragStartLeftPx]);

  const getInitials = (firstName, lastName) => {
    const first = (firstName || '').trim().charAt(0).toUpperCase();
    const last = (lastName || '').trim().charAt(0).toUpperCase();
    return `${first}${last}` || 'U';
  };

  const openPreferredNameModal = () => {
    setPreferredNameDraft(user?.preferred_name || '');
    setShowPreferredNameModal(true);
  };

  const handleSavePreferredName = async () => {
    if (!user?.id) return;
    setSavingPreferredName(true);
    try {
      const trimmed = preferredNameDraft.trim();
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version: user.updated_at,
          preferredName: trimmed === '' ? null : trimmed
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowPreferredNameModal(false);
        setErrorModal(null);
        loadUserProfile(user.id);
      } else {
        setErrorModal({
          title: 'Update failed',
          message: data.message || 'Failed to update display name.'
        });
      }
    } catch (err) {
      setErrorModal({
        title: 'Error',
        message: 'Failed to update display name. Please try again.'
      });
      console.error(err);
    } finally {
      setSavingPreferredName(false);
    }
  };

  const uploadToS3 = async (file, userLike) => {
    // Use only the user ID for the filename
    const customFilename = userLike.id ? `${userLike.id}` : 'avatar';

    const presignRes = await fetch('/api/s3/presigned-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type,
        fileLocation: 'temp/avatars',
        useCustomFilename: true,
        customFilename
      })
    });
    const presignData = await presignRes.json();
    if (!presignData.success) throw new Error(presignData.message || 'Failed to get presigned URL');

    const { presignedUrl, s3Key, bucket, region } = presignData.data;
    const uploadRes = await fetch(presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.statusText}`);
    return `https://${bucket}.s3.${region}.amazonaws.com/${s3Key}`;
  };

  const handleAvatarSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setSelectedAvatarFile(file);
    const url = URL.createObjectURL(file);
    setAvatarPreviewUrl(url);
  };

  const handleCancelEdit = () => {
    // Reset form data to original values
    setEditFormData({ ...originalFormData });
    setSelectedAvatarFile(null);
    setAvatarPreviewUrl('');
    setShowPlatformPasswordField(false);
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const executeSaveBasicInfo = async () => {
    setIsLoading(true);
    try {
      const isDisablingPlatformAccess =
        originalFormData.platformAccess === true && editFormData.platformAccess === false;
      const isEnablingPlatformAccess =
        originalFormData.platformAccess === false && editFormData.platformAccess === true;
      const passwordChange =
        editFormData.platformAccess &&
        !!(editFormData.password && String(editFormData.password).trim());

      // Upload avatar if selected
      let avatarUrlForUpdate = undefined;
      if (selectedAvatarFile) {
        try {
          avatarUrlForUpdate = await uploadToS3(selectedAvatarFile, {
            id: user.id,
            first_name: editFormData.first_name,
            last_name: editFormData.last_name
          });
        } catch (err) {
          console.error('Avatar upload prior to update failed:', err);
        }
      }

      // Build update data
      const updateData = {
        version: user.updated_at,
        // Explicitly include email even if empty to allow clearing it (send null if empty)
        email: editFormData.email !== undefined ? (editFormData.email.trim() === '' ? null : editFormData.email.trim()) : undefined,
        firstName: editFormData.first_name || undefined,
        lastName: editFormData.last_name || undefined,
        dob: editFormData.dob !== undefined
          ? (String(editFormData.dob).trim() === '' ? null : String(editFormData.dob).trim())
          : undefined,
        avatar: avatarUrlForUpdate || undefined,
        password: passwordChange ? editFormData.password : undefined
      };

      if (canEditSignIn && isDisablingPlatformAccess) {
        updateData.canLogin = false;
      }
      if (!canEditSignIn) {
        delete updateData.password;
      }

      // Remove undefined values (but keep null values as they indicate intentional clearing)
      Object.keys(updateData).forEach(key => 
        updateData[key] === undefined && delete updateData[key]
      );

      const saveUserRequest = updateData.password
        ? fetchWithAuth
        : fetch;
      const response = await saveUserRequest(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (data.success) {
        setSelectedAvatarFile(null);
        setAvatarPreviewUrl('');
        setError(null);
        setErrorModal(null);
        if (passwordChange) {
          setShowPlatformPasswordField(false);
          setEditFormData((prev) => ({ ...prev, password: '' }));
          setOriginalFormData((prev) => ({ ...prev, password: '' }));
        }
        loadUserProfile(user.id);
      } else {
        setErrorModal({
          title: 'Update failed',
          message: data.message || 'Failed to update user. Please try again or reload the page.'
        });
        console.error('Update failed:', data);
      }
    } catch (error) {
      setErrorModal({
        title: 'Error',
        message: 'Failed to update user profile. Please try again or reload the page.'
      });
      console.error('Error updating user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleShellAccess = async () => {
    if (!canGrantPlatformAccess || shellAccessSaving || shellAccess == null) return;
    const currentGp = (availableGroupsPeriods || []).find(
      (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
    );
    if (!user?.id || !currentGp?.id) return;
    const next = !shellAccess;
    setShellAccessSaving(true);
    try {
      const res = await fetchWithAuth('/api/page-configurations/platform-access/membership', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          user_groups_periods_id: currentGp.id,
          access: next
        })
      });
      const data = await res.json();
      if (!data.success) {
        setErrorModal({
          title: 'Platform Access',
          message: data.message || 'Could not update Platform Access.'
        });
        return;
      }
      const hasAccess = data.data?.hasAccess === true;
      setShellAccess(hasAccess);
      if (next !== hasAccess) {
        setErrorModal({
          title: 'Platform Access',
          message: hasAccess
            ? 'This context still has Platform Access from another grant.'
            : 'This context still does not have Platform Access.'
        });
      }
      if (authUser?.id && String(authUser.id) === String(user.id)) {
        loadCurrentRole();
      }
    } catch (err) {
      console.error('Error updating Platform Access:', err);
      setErrorModal({
        title: 'Platform Access',
        message: 'Could not update Platform Access.'
      });
    } finally {
      setShellAccessSaving(false);
    }
  };

  const handleSaveBasicInfo = async () => {
    const isDisablingPlatformAccess =
      originalFormData.platformAccess === true && editFormData.platformAccess === false;
    const isEnablingPlatformAccess =
      originalFormData.platformAccess === false && editFormData.platformAccess === true;

    if (editFormData.platformAccess) {
      const emailTrimmed = editFormData.email != null ? String(editFormData.email).trim() : '';
      if (emailTrimmed === '') {
        setErrorModal({
          title: 'Cannot save',
          message:
            'Email cannot be empty when sign-in is on. Turn sign-in off first if you want to clear the email, or enter an email and try again.'
        });
        return;
      }
    }

    if (canEditSignIn && isDisablingPlatformAccess) {
      setBasicInfoSaveConfirm('disable_platform_access');
      return;
    }

    if (canEditSignIn && isEnablingPlatformAccess) {
      const hasEmail = editFormData.email != null && String(editFormData.email).trim() !== '';
      const hasPasswordInput =
        editFormData.password != null && String(editFormData.password).trim() !== '';
      const alreadyHasPassword = !!user?.has_password;
      const passwordOk = alreadyHasPassword || hasPasswordInput;
      if (!hasEmail || !passwordOk) {
        setErrorModal({
          title: 'Cannot enable sign-in',
          message: alreadyHasPassword
            ? 'Email is required to enable sign-in. Please set an email and try again, or reload the page.'
            : 'Email and a password are required to enable sign-in. Please set both and try again, or reload the page.'
        });
        return;
      }
    }

    const passwordChange =
      canEditSignIn &&
      editFormData.platformAccess &&
      !!(editFormData.password && String(editFormData.password).trim());

    if (passwordChange && !isEnablingPlatformAccess) {
      setBasicInfoSaveConfirm('password_change');
      return;
    }

    await executeSaveBasicInfo();
  };

  const handleFormSubmit = async (
    form,
    formData,
    submissionId = null,
    overrideId = null,
    action = SUBMISSION_ACTIONS.SAVE
  ) => {
    const formId = form.id;
    let effectiveSubmissionId = submissionId;
    const pendingBlobs = { ...(pendingFormFileBlobsRef.current[formId] || {}) };
    const ugpId = getUgpIdForForms();
    const needsUpload = hasPendingFormFileUploads(form, formData, pendingBlobs);

    if (needsUpload && !ugpId) {
      throw new Error('Select an enrollment period before uploading files for this form.');
    }

    const answersToSave = stripFileUploadAnswers(form, formData);
    const answersForApi =
      action === SUBMISSION_ACTIONS.GRADE
        ? stripFileUploadAnswers(form, formData)
        : answersToSave;
    const buildPayload = (answers, actionOverride) => ({
      form_id: form.id,
      answers,
      user_id: user.id,
      actor_user_id: authUser?.id || user.id,
      action: actionOverride || action,
      ...buildFormSubmissionReferencePayload(form),
      ...(overrideId ? { id: overrideId } : {})
    });
    const originalStripped = stripFileUploadAnswers(
      form,
      originalFormDataByFormId[formId] || {}
    );
    const nonFileAnswersChanged =
      JSON.stringify(answersToSave) !== JSON.stringify(originalStripped);

    try {
      setIsLoading(true);

      let createdForUpload = false;

      if (needsUpload && !effectiveSubmissionId) {
        const createRes = await fetch('/api/form-submissions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildPayload(answersToSave, SUBMISSION_ACTIONS.SAVE))
        });
        const createData = await createRes.json();
        if (!createData.success) {
          throw new Error(createData.message || 'Unable to create submission before uploading files.');
        }
        effectiveSubmissionId = createData.data.id;
        createdForUpload = true;
      }

      if (needsUpload && effectiveSubmissionId) {
        await uploadPendingFormFiles({
          form,
          answers: formData,
          submissionId: effectiveSubmissionId,
          ugpId,
          pendingBlobs,
          generateId: generateUuid
        });
        clearFormPendingBlobs(pendingFormFileBlobsRef.current, formId);
      }

      let savedSubmission = null;

      const shouldPersist =
        !effectiveSubmissionId ||
        nonFileAnswersChanged ||
        action !== SUBMISSION_ACTIONS.SAVE ||
        createdForUpload;

      if (shouldPersist) {
        const body = buildPayload(answersForApi);
        if (effectiveSubmissionId) {
          const response = await fetch(`/api/form-submissions/${effectiveSubmissionId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });
          const data = await response.json();
          if (!data.success) {
            console.error('Form submission failed:', data);
            throw new Error(data.message || 'Unable to save the form.');
          }
          savedSubmission = data.data;
        } else {
          const response = await fetch('/api/form-submissions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          });
          const data = await response.json();
          if (!data.success) {
            console.error('Form submission failed:', data);
            throw new Error(data.message || 'Unable to save the form.');
          }
          savedSubmission = data.data;
          effectiveSubmissionId = savedSubmission.id;
        }
      } else if (effectiveSubmissionId) {
        const getRes = await fetch(`/api/form-submissions/${effectiveSubmissionId}`);
        const getData = await getRes.json();
        if (getData.success) {
          savedSubmission = getData.data;
        } else {
          savedSubmission = { id: effectiveSubmissionId, answers: answersToSave };
        }
      }

      const hydrated = await hydrateSubmissionFiles(
        form,
        effectiveSubmissionId,
        savedSubmission?.answers || answersToSave
      );
      setOriginalFormDataByFormId((prev) => ({ ...prev, [formId]: { ...hydrated } }));
      setFormDataByFormId((prev) => ({ ...prev, [formId]: hydrated }));
      return savedSubmission;
    } catch (error) {
      console.error('Form submission error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSave = async (form, dataOverride = null, action = SUBMISSION_ACTIONS.SAVE) => {
    const formId = form.id;
    const existing = existingSubmissions[formId];
    const submissionId = selectedSubmissionIdByFormId[formId] || existing?.id || null;
    const hasExisting = !!submissionId;
    const list = submissionsByFormId[formId] || [];
    const selected = list.find((s) => String(s.id) === String(submissionId));

    if (action === SUBMISSION_ACTIONS.GRADE) {
      if (!canUpdatePoints) return;
    } else {
      const isDraft = !selected || canSubmitDraft(selected);
      if (
        !canEditProfileFormAnswers(formPerms, formPermCtx, {
          hasExistingSubmission: hasExisting,
          isDraft
        })
      ) {
        return;
      }
    }

    const currentData = dataOverride || formDataByFormId[formId] || {};
    try {
      setIsSubmittingFormId(formId);
      await handleFormSubmit(form, currentData, submissionId, null, action);
      const refreshed = await loadFormSubmissions(formId, form);
      if (Array.isArray(refreshed) && refreshed.length > 0) {
        const latest = refreshed[0];
        setSelectedSubmissionIdByFormId(prev => ({ ...prev, [formId]: latest.id }));
        const hydrated = await hydrateSubmissionFiles(form, latest.id, latest.answers || currentData);
        setFormDataByFormId(prev => ({ ...prev, [formId]: hydrated }));
        setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...hydrated } }));
      } else {
        await loadExistingSubmission(formId, form);
      }
    } catch (error) {
      setErrorModal({
        title: 'Form save failed',
        message: error.message || 'Could not save the form. Please try again.'
      });
    } finally {
      setIsSubmittingFormId(null);
    }
  };

  const handleFormSubmitFinal = async (form, dataOverride = null) => {
    const formId = form.id;
    const submissionId = selectedSubmissionIdByFormId[formId] || existingSubmissions[formId]?.id || null;
    const list = submissionsByFormId[formId] || [];
    const selected = list.find((s) => String(s.id) === String(submissionId));
    if (selected && isSubmissionLockedForStudent(selected)) return;
    if (!canEditProfileFormDraft(formPerms, formPermCtx)) return;

    const pendingBlobs = { ...(pendingFormFileBlobsRef.current[formId] || {}) };
    const currentData = dataOverride || formDataByFormId[formId] || {};
    const validation = validateRequiredForSubmit(form, currentData, pendingBlobs);
    if (!validation.ok) {
      setErrorModal({
        title: 'Cannot submit',
        message: validation.message || 'Complete all required questions before submitting.'
      });
      return;
    }

    try {
      setIsSubmittingFormId(formId);
      await handleFormSubmit(form, currentData, submissionId, null, SUBMISSION_ACTIONS.SUBMIT);
      const refreshed = await loadFormSubmissions(formId, form);
      if (Array.isArray(refreshed) && refreshed.length > 0) {
        const targetId = submissionId || refreshed[0].id;
        const hit = refreshed.find((s) => String(s.id) === String(targetId)) || refreshed[0];
        setSelectedSubmissionIdByFormId((prev) => ({ ...prev, [formId]: hit.id }));
        const hydrated = await hydrateSubmissionFiles(form, hit.id, hit.answers || currentData);
        setFormDataByFormId((prev) => ({ ...prev, [formId]: hydrated }));
        setOriginalFormDataByFormId((prev) => ({ ...prev, [formId]: { ...hydrated } }));
      } else {
        await loadExistingSubmission(formId, form);
      }
    } catch (error) {
      setErrorModal({
        title: 'Form submit failed',
        message: error.message || 'Could not submit the form. Please try again.'
      });
    } finally {
      setIsSubmittingFormId(null);
    }
  };

  const handleFormDelete = async (form) => {
    if (!canDeleteSubmission) return;
    const formId = form.id;
    const submissionId = selectedSubmissionIdByFormId[formId] || existingSubmissions[formId]?.id;
    if (!submissionId) return;
    if (!window.confirm('Delete this submission? This cannot be undone.')) return;
    try {
      setIsSubmittingFormId(formId);
      const res = await fetchWithAuth(`/api/form-submissions/${submissionId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actor_user_id: authUser?.id })
      });
      const data = await res.json();
      if (!data.success) {
        setErrorModal({ title: 'Delete failed', message: data.message || 'Could not delete submission.' });
        return;
      }
      const list = await loadFormSubmissions(formId, form);
      if (list.length > 0) {
        selectAssignmentSubmission(form, list[0].id);
      } else if (canCreateSubmission) {
        startNewAssignmentSubmission(form);
      } else {
        setSelectedSubmissionIdByFormId(prev => ({ ...prev, [formId]: null }));
        const defaults = getDefaultInitialAnswers(form);
        setFormDataByFormId(prev => ({ ...prev, [formId]: defaults }));
        setOriginalFormDataByFormId(prev => ({ ...prev, [formId]: { ...defaults } }));
      }
    } catch {
      setErrorModal({ title: 'Delete failed', message: 'Could not delete submission.' });
    } finally {
      setIsSubmittingFormId(null);
    }
  };

  const handleFormCancel = (form) => {
    const formId = form.id;
    const originalData = originalFormDataByFormId[formId] || {};
    setFormDataByFormId(prev => ({ ...prev, [formId]: { ...originalData } }));
  };

  const hasFormChanges = (formId) => {
    const current = formDataByFormId[formId] || {};
    const original = originalFormDataByFormId[formId] || {};
    
    // Deep comparison of objects
    const currentKeys = Object.keys(current);
    const originalKeys = Object.keys(original);
    
    if (currentKeys.length !== originalKeys.length) return true;
    
    for (const key of currentKeys) {
      if (current[key] !== original[key]) {
        return true;
      }
    }
    
    return false;
  };

  const hasChanges = () => {
    if (selectedAvatarFile) return true;
    return (
      editFormData.email !== originalFormData.email ||
      editFormData.first_name !== originalFormData.first_name ||
      editFormData.last_name !== originalFormData.last_name ||
      editFormData.dob !== originalFormData.dob ||
      editFormData.platformAccess !== originalFormData.platformAccess ||
      (editFormData.password && editFormData.password.trim())
    );
  };

  // Get enabled columns sorted by order
  const getEnabledColumns = () => {
    if (!profileViewConfig || !profileViewConfig.columns) {
      return [];
    }
    return profileViewConfig.columns
      .filter(col => col.enabled !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Get enabled buckets for a column, sorted by order
  const getEnabledBuckets = (columnId) => {
    if (!profileViewConfig || !profileViewConfig.columns) {
      return [];
    }
    const column = profileViewConfig.columns.find(col => col.id === columnId);
    if (!column || !column.buckets) {
      return [];
    }
    return column.buckets
      .filter(bucket => bucket.enabled !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Render bucket content based on bucket ID
  const renderBucketContent = (bucketId) => {
    switch (bucketId) {
      case 'evaluations':
        return (
          <ExpandableSection
            title="Evaluations"
            isExpanded={sectionsExpanded.evaluations}
            onToggle={() => toggleSection('evaluations')}
          >
            <p style={styles.placeholderText}>In development</p>
          </ExpandableSection>
        );
      case 'benchmarks':
        return (
          <ExpandableSection
            title="Benchmarks"
            isExpanded={sectionsExpanded.benchmarks}
            onToggle={() => toggleSection('benchmarks')}
          >
            <p style={styles.placeholderText}>In development</p>
          </ExpandableSection>
        );
      case 'grades':
        return (
          <ExpandableSection
            title="Grades"
            isExpanded={sectionsExpanded.grades}
            onToggle={() => toggleSection('grades')}
          >
            <p style={styles.placeholderText}>In development</p>
          </ExpandableSection>
        );
      case 'certificates':
        return (
          <ExpandableSection
            title="Certificates"
            isExpanded={sectionsExpanded.certificates}
            onToggle={() => toggleSection('certificates')}
          >
            <p style={styles.placeholderText}>In development</p>
          </ExpandableSection>
        );
      default:
        return null;
    }
  };

  const activeAssignmentForm = activeAssignmentFormId
    ? (forms || []).find((f) => String(f.id) === String(activeAssignmentFormId))
    : null;

  const activeAssignmentSubmission = (() => {
    if (!activeAssignmentForm) return null;
    const formId = activeAssignmentForm.id;
    const list = submissionsByFormId[formId] || [];
    const selId = selectedSubmissionIdByFormId[formId];
    if (!selId) return null;
    return list.find((s) => String(s.id) === String(selId)) || null;
  })();

  const activeAssignmentCanStartNew = (() => {
    if (!activeAssignmentForm || !canCreateSubmission) return false;
    const policy = resolveSubmissionPolicy({
      formMetadata: activeAssignmentForm.form_metadata,
      relationshipMetadata: activeAssignmentForm.relationship_metadata
    });
    return canStartNewSubmissionAttempt(
      policy,
      submissionsByFormId[activeAssignmentForm.id] || []
    ).ok;
  })();

  const activeAssignmentAnswersReadOnly = (() => {
    if (!activeAssignmentForm) return true;
    const sub = activeAssignmentSubmission;
    if (!sub) {
      return !canCreateSubmission;
    }
    const isDraft = canSubmitDraft(sub);
    return !canEditProfileFormAnswers(formPerms, formPermCtx, {
      hasExistingSubmission: true,
      isDraft
    });
  })();

  const activeAssignmentCollaborationCapabilities = useMemo(() => {
    if (!activeAssignmentForm) return null;
    const policy = resolveSubmissionPolicy({
      formMetadata: activeAssignmentForm.form_metadata,
      relationshipMetadata: activeAssignmentForm.relationship_metadata,
      overrideSettings: activeAssignmentSubmission?.override_settings
    });
    return resolveCollaborationCapabilities(formPerms, formPermCtx, policy);
  }, [activeAssignmentForm, activeAssignmentSubmission, formPerms, formPermCtx]);

  if (isLoading) {
    return <LoadingOverlay isVisible={true} message="Loading user profile..." />;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h2 style={styles.errorTitle}>Error</h2>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h2 style={styles.errorTitle}>User Not Found</h2>
          <p style={styles.errorText}>The requested user could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Top Controls Bar */}
      <div style={styles.topControlsBar}>
        <div className="profile-top-bar-start">
          {groupsReturnUrl && (
            <button
              type="button"
              className="profile-back-link"
              onClick={() => {
                window.location.href = groupsReturnUrl;
              }}
              aria-label={getGroupsBackLabel(groupsReturnUrl)}
            >
              <span className="profile-back-link-icon" aria-hidden>
                ←
              </span>
              {getGroupsBackLabel(groupsReturnUrl)}
            </button>
          )}
        </div>
        {availableGroupsPeriods.length >= 1 && (() => {
          const currentGp = availableGroupsPeriods.find(gp => 
            String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
          );
          const hasMultiple = availableGroupsPeriods.length > 1;
          const groupLabel = currentGp?.group_name || '—';
          const periodLabel = currentGp?.period_title || currentGp?.period_code || '—';
          const switcherBody = (
            <>
              <span className="profile-context-switcher-eyebrow">Viewing profile as</span>
              <span className="profile-context-switcher-role">{groupLabel}</span>
              <span className="profile-context-switcher-period">{periodLabel}</span>
            </>
          );
          return (
            <div className="profile-context-switcher-wrap">
              {hasMultiple ? (
                <button
                  type="button"
                  className="profile-context-switcher profile-context-switcher--interactive"
                  onClick={() => {
                    setDefaultRoleUgpId(user?.current_ugp_id ?? null);
                    setShowGroupsPeriodsModal(true);
                  }}
                  title="Switch role and period for this profile"
                  aria-haspopup="dialog"
                >
                  <div className="profile-context-switcher-main">{switcherBody}</div>
                  <span className="profile-context-switcher-action">
                    Switch
                    <span className="profile-context-switcher-chevron" aria-hidden>
                      ▾
                    </span>
                  </span>
                </button>
              ) : (
                <div
                  className="profile-context-switcher profile-context-switcher--static"
                  title="Profile context for this user"
                >
                  <div className="profile-context-switcher-main">{switcherBody}</div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
      
      {/* Groups Periods Selection Modal */}
      {showGroupsPeriodsModal && (
        <div 
          style={styles.modalOverlay} 
          onClick={() => setShowGroupsPeriodsModal(false)}
        >
          <div 
            style={styles.modalContent} 
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Switch Profile View</h3>
              <button
                onClick={() => setShowGroupsPeriodsModal(false)}
                style={styles.modalCloseButton}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, {
                    background: '#f1f5f9',
                    color: '#475569',
                  });
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.currentTarget.style, styles.modalCloseButton);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.modalContextInfo}>
                <div style={styles.modalContextIcon}>ℹ️</div>
                <div style={styles.modalContextText}>
                  <p style={styles.modalContextTitle}>
                    Multiple Group-Period Combinations
                  </p>
                  <p style={styles.modalContextDescription}>
                    This user belongs to multiple group-period combinations. Their profile dynamically changes depending on the selected group-period. Each combination may have different forms, evaluations, and data associated with it.
                  </p>
                  {canEditUserData && (
                    <p style={{ ...styles.modalContextDescription, marginTop: 8, fontWeight: 500 }}>
                      Default Role sets what role & organization is loaded when the user logs in.
                    </p>
                  )}
                </div>
              </div>
              
              <div style={styles.modalOptionsList}>
                {availableGroupsPeriods.map(gp => {
                  const gpId = String(gp.groups_periods_id || '');
                  const isViewSelected = selectedGroupsPeriodsId 
                    ? String(selectedGroupsPeriodsId) === gpId 
                    : false;
                  return (
                    <button
                      key={gp.groups_periods_id}
                      onClick={() => {
                        if (!isViewSelected) {
                          setSelectedGroupsPeriodsId(gpId);
                          setShowGroupsPeriodsModal(false);
                        }
                      }}
                      disabled={isViewSelected}
                      style={{
                        ...styles.modalOptionButton,
                        ...(isViewSelected ? styles.modalOptionButtonSelected : {}),
                        cursor: isViewSelected ? 'not-allowed' : 'pointer',
                        opacity: isViewSelected ? 0.7 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!isViewSelected) {
                          Object.assign(e.currentTarget.style, styles.modalOptionButtonHover || {});
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isViewSelected) {
                          Object.assign(e.currentTarget.style, styles.modalOptionButton || {});
                        }
                      }}
                    >
                      <span style={styles.modalOptionIcon}>
                        {isViewSelected ? '✓' : '○'}
                      </span>
                      <div style={styles.modalOptionContent}>
                        <div style={styles.modalOptionTitle}>
                          {gp.group_name} – {gp.period_title}
                        </div>
                        {gp.groups_periods_title && (
                          <div style={styles.modalOptionSubtitle}>
                            {gp.groups_periods_title}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {canEditUserData && (
                <div style={{ marginTop: 16 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: 6 }}>
                    Default Role
                  </label>
                  <select
                    value={defaultRoleUgpId != null ? String(defaultRoleUgpId) : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDefaultRoleUgpId(v === '' ? null : v);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      background: '#fff',
                    }}
                  >
                    <option value="">— Select default role —</option>
                    {availableGroupsPeriods.map(gp => {
                      const ugpId = gp.id;
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const ugpStartOk = !gp.start_date || new Date(gp.start_date) <= today;
                      const ugpEndOk = !gp.end_date || new Date(gp.end_date) >= today;
                      const periodStart = gp.period_start_date != null ? new Date(gp.period_start_date) : null;
                      const periodEnd = gp.period_end_date != null ? new Date(gp.period_end_date) : null;
                      const periodOk = (periodStart == null && periodEnd == null) ||
                        (periodStart != null && periodEnd != null && today >= periodStart && today <= periodEnd);
                      const canBeDefault = !!gp.is_active && ugpStartOk && ugpEndOk && periodOk;
                      let label = `${gp.group_name} – ${gp.period_title}`;
                      if (!canBeDefault) {
                        if (!gp.is_active) label += ' (Inactive)';
                        else if (!ugpStartOk) label += ' (Access not started)';
                        else if (!ugpEndOk) label += ' (Access ended)';
                        else if (!periodOk) label += ' (Period not current)';
                      }
                      return (
                        <option
                          key={ugpId}
                          value={String(ugpId)}
                          disabled={!canBeDefault}
                        >
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              {canEditUserData && (() => {
                const hasDefaultRoleChange = (defaultRoleUgpId == null && user?.current_ugp_id != null) ||
                  (defaultRoleUgpId != null && String(defaultRoleUgpId) !== String(user?.current_ugp_id));
                if (!hasDefaultRoleChange) return null;
                return (
                  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      onClick={() => setDefaultRoleUgpId(user?.current_ugp_id ?? null)}
                      style={styles.cancelEditButton}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (defaultRoleUgpId == null || !user?.id) return;
                        const gp = availableGroupsPeriods.find(g => String(g.id) === String(defaultRoleUgpId));
                        if (!gp) {
                          setErrorModal({ title: 'Invalid selection', message: 'The selected default role is not available for this user.' });
                          return;
                        }
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const ugpStartOk = !gp.start_date || new Date(gp.start_date) <= today;
                        const ugpEndOk = !gp.end_date || new Date(gp.end_date) >= today;
                        const periodStart = gp.period_start_date != null ? new Date(gp.period_start_date) : null;
                        const periodEnd = gp.period_end_date != null ? new Date(gp.period_end_date) : null;
                        const periodOk = (periodStart == null && periodEnd == null) ||
                          (periodStart != null && periodEnd != null && today >= periodStart && today <= periodEnd);
                        const canBeDefault = !!gp.is_active && ugpStartOk && ugpEndOk && periodOk;
                        if (!canBeDefault) {
                          let reason = 'The selected role cannot be set as default.';
                          if (!gp.is_active) reason = 'The selected role is inactive and cannot be set as default.';
                          else if (!ugpStartOk) reason = 'The selected role\'s access period has not started yet.';
                          else if (!ugpEndOk) reason = 'The selected role\'s access period has ended.';
                          else if (!periodOk) reason = 'The selected role\'s period is not current (started or ended).';
                          setErrorModal({ title: 'Cannot set default role', message: reason });
                          return;
                        }
                        setSavingDefaultRole(true);
                        try {
                          const res = await fetch(`/api/users/${user.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ current_ugp_id: defaultRoleUgpId, version: user.updated_at }),
                          });
                          const data = await res.json();
                          if (data.success) {
                            setUser(prev => prev ? { ...prev, current_ugp_id: defaultRoleUgpId } : null);
                            setShowGroupsPeriodsModal(false);
                          } else {
                            setErrorModal({ title: 'Update failed', message: data.message || 'Failed to set default role.' });
                          }
                        } catch (err) {
                          setErrorModal({ title: 'Error', message: 'Failed to set default role. Please try again.' });
                          console.error(err);
                        } finally {
                          setSavingDefaultRole(false);
                        }
                      }}
                      disabled={savingDefaultRole}
                      style={styles.saveButton}
                    >
                      {savingDefaultRole ? 'Saving...' : 'Save default role'}
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      
      <div
        style={styles.columnsLayout}
        className={activeAssignmentForm ? 'profile-columns--assignment-open' : undefined}
      >
        {/* Left Column */}
        <div
          className={
            leftColumnCollapsed
              ? 'profile-left-column profile-left-column--collapsed'
              : 'profile-left-column'
          }
          style={{
            ...styles.column,
            width: leftColumnCollapsed ? undefined : leftColumnWidthPx,
            flexShrink: 0,
            minHeight: 0
          }}
        >
          <div style={styles.columnHeaderPrimary} className="profile-left-column__header">
            {leftColumnCollapsed ? (
              <button
                type="button"
                className="profile-left-column-collapse-btn is-expand"
                onClick={expandLeftColumn}
                aria-label="Expand profile column"
                title="Show profile"
              >
                <span aria-hidden>›</span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="profile-left-column-collapse-btn"
                  onClick={collapseLeftColumn}
                  aria-label="Collapse profile column"
                  title="Hide profile column"
                >
                  <span aria-hidden>‹</span>
                </button>
                {canEditUserData ? (
                  <button
                    type="button"
                    className="profile-display-name-btn"
                    onClick={openPreferredNameModal}
                    aria-label="Edit preferred or display name"
                  >
                    {getUserProfileDisplayName(user)}
                  </button>
                ) : (
                  <h2 style={styles.columnTitlePrimary}>{getUserProfileDisplayName(user)}</h2>
                )}
              </>
            )}
          </div>
          {!leftColumnCollapsed ? (
          <div className="profile-column-scroll" style={styles.columnContent}>
            <ExpandableSection
              title="Basic Information"
              isExpanded={sectionsExpanded.basicInfo}
              onToggle={() => toggleSection('basicInfo')}
              showActions={canEditUserData || canEditSignIn}
              hasChanges={(canEditUserData || canEditSignIn) && hasChanges()}
              onSave={handleSaveBasicInfo}
              onCancel={handleCancelEdit}
            >
              <div className="profile-basic">
                <div className="profile-basic-header">
                  <div className="profile-basic-name-row">
                    <InfoFieldEdit
                      label="First Name"
                      name="first_name"
                      value={editFormData.first_name}
                      onChange={handleInputChange}
                      disabled={!canEditUserData}
                      compact
                    />
                    <InfoFieldEdit
                      label="Last Name"
                      name="last_name"
                      value={editFormData.last_name}
                      onChange={handleInputChange}
                      disabled={!canEditUserData}
                      compact
                    />
                  </div>
                  <div className="profile-basic-avatar-wrap">
                    <div
                      className={`profile-basic-avatar${!canEditUserData ? ' profile-basic-avatar--disabled' : ''}`}
                      onClick={() => canEditUserData && fileInputRef.current?.click()}
                      onKeyDown={(e) => {
                        if (canEditUserData && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                      role={canEditUserData ? 'button' : undefined}
                      tabIndex={canEditUserData ? 0 : undefined}
                      aria-label="Change profile photo"
                    >
                      {avatarPreviewUrl ? (
                        <img src={avatarPreviewUrl} alt="" />
                      ) : user?.avatar ? (
                        <img src={user.avatar} alt="" />
                      ) : (
                        <span className="profile-basic-avatar-initials">
                          {getInitials(user?.first_name, user?.last_name)}
                        </span>
                      )}
                    </div>
                    <span className="profile-basic-avatar-label" aria-hidden>
                      Avatar
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarSelected}
                      style={{ display: 'none' }}
                      disabled={!canEditUserData}
                    />
                  </div>
                </div>
                <InfoFieldEdit
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={editFormData.dob}
                  onChange={handleInputChange}
                  disabled={!canEditUserData}
                  compact
                />
                <InfoFieldEdit
                  label="Email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleInputChange}
                  disabled={!canEditUserData}
                  compact
                />
                {(() => {
                  const isPlatformOwnerAccount = user?.is_system_owner === true;
                  const viewingSelf =
                    authUser?.id && user?.id && String(authUser.id) === String(user.id);
                  const canEditOwnerPassword = isPlatformOwnerAccount
                    ? viewingSelf && canEditSignIn
                    : canEditSignIn;
                  const needsPasswordSet =
                    !!editFormData.platformAccess && canEditOwnerPassword && !user?.has_password;
                  const showPasswordInput =
                    !!editFormData.platformAccess &&
                    (needsPasswordSet || showPlatformPasswordField);
                  const accessBadges = getProfileAccessBadges({
                    canLogin: !!editFormData.platformAccess,
                    shellAccess
                  });

                  return (
                    <>
                      {isPlatformOwnerAccount && !viewingSelf && (
                        <p className="profile-owner-password-hint">
                          Platform Owner password can only be changed by the Owner on their Profile page.
                        </p>
                      )}
                      <div className="profile-access-badges">
                        <ProfileAccessBadge
                          badge={accessBadges.loginBadge}
                          ariaChecked={!!editFormData.platformAccess}
                          disabled={!canEditSignIn}
                          onClick={() => {
                            if (!canEditSignIn) return;
                            const turningOff = editFormData.platformAccess;
                            setEditFormData((prev) => ({
                              ...prev,
                              platformAccess: !prev.platformAccess,
                              password: turningOff ? '' : prev.password
                            }));
                            if (turningOff) {
                              setShowPlatformPasswordField(false);
                            } else if (!user?.has_password) {
                              setShowPlatformPasswordField(true);
                            }
                          }}
                        />
                        {editFormData.platformAccess && canEditOwnerPassword && user?.has_password ? (
                          <button
                            type="button"
                            className="profile-platform-password-trigger"
                            onClick={() => setShowPlatformPasswordField((open) => !open)}
                            aria-expanded={showPlatformPasswordField}
                          >
                            {showPlatformPasswordField ? 'Hide Password' : 'Update Password'}
                          </button>
                        ) : null}
                        <ProfileAccessBadge
                          badge={accessBadges.platformBadge}
                          ariaChecked={shellAccess === true}
                          disabled={
                            !canGrantPlatformAccess ||
                            shellAccess == null ||
                            shellAccessSaving
                          }
                          onClick={handleToggleShellAccess}
                        />
                      </div>
                      {showPasswordInput && canEditOwnerPassword ? (
                        needsPasswordSet ? (
                          <div className="profile-platform-password-missing">
                            <label
                              className="profile-platform-password-missing__label"
                              htmlFor="profile-field-password"
                            >
                              Password
                            </label>
                            <input
                              id="profile-field-password"
                              type="password"
                              name="password"
                              className="profile-field-input profile-field-input--required-missing"
                              value={editFormData.password || ''}
                              onChange={handleInputChange}
                              placeholder="Set a password"
                              autoComplete="new-password"
                              disabled={!canEditOwnerPassword}
                              required
                              aria-required="true"
                            />
                          </div>
                        ) : (
                          <InfoFieldEdit
                            label="Password"
                            name="password"
                            type="password"
                            value={editFormData.password}
                            onChange={handleInputChange}
                            placeholder=""
                            autoComplete="new-password"
                            disabled={!canEditOwnerPassword}
                            compact
                          />
                        )
                      ) : null}
                    </>
                  );
                })()}
              </div>
            </ExpandableSection>
            {user?.id ? (
              <AddressesSection
                userId={user.id}
                canEdit={canEditUserData}
                fetchWithAuth={fetchWithAuth}
                isExpanded={sectionsExpanded.addresses}
                onToggle={() => toggleSection('addresses')}
              />
            ) : null}
            {/* Person Links section — only when person_links app is assigned */}
            {(() => {
              const personLinksApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.PERSON_LINKS);
              const personLinksLabel = personLinksApp?.metadata?.container_label_override || 'Person Links';
              const counselorVisibleProps = getCounselorVisibleProperties(personLinksApp?.metadata);
              const enabledPersonLinkTypes = getEnabledPersonLinkTypes(personLinksApp?.metadata);
              return personLinksApp && (
              <ExpandableSection
                title={personLinksLabel}
                isExpanded={sectionsExpanded.personLinks}
                onToggle={() => toggleSection('personLinks')}
                showActions={canAddPersonLinks && enabledPersonLinkTypes.length > 0}
                headerExtra={
                  canAddPersonLinks && enabledPersonLinkTypes.length > 0 && sectionsExpanded.personLinks ? (
                    <ProfileSectionAddButton
                      title="Add person link"
                      onClick={() => {
                        const cards = getPersonLinkTypeCardsForMetadata(personLinksApp?.metadata);
                        if (cards.length) setAddPersonLinkRelationshipType(cards[0].value);
                        setShowAddPersonLinkModal(true);
                      }}
                    />
                  ) : null
                }
              >
                <PersonLinksSection
                  personLinks={personLinks}
                  counselorVisibleProperties={counselorVisibleProps}
                  personLinksMetadata={personLinksApp?.metadata}
                  profileReturnUrl={`${location.pathname}${location.search}`}
                  canRemove={canRemovePersonLinks}
                  canEdit={canEditPersonLinks}
                  onEdit={(pl) => {
                    setEditPersonLinkItem(pl);
                    setEditPersonLinkGuardianType(pl.metadata?.guardian_type || 'father');
                    setEditPersonLinkSiblingType(pl.metadata?.sibling_type || 'brother');
                    setEditPersonLinkPrimary(!!pl.metadata?.primary);
                    setEditPersonLinkEmergency(!!pl.metadata?.emergency);
                    setShowEditPersonLinkModal(true);
                  }}
                  onRemoveRequest={(relId, personName) => setRemoveConfirmModal({ relId, personName })}
                  onRemove={async (relId) => {
                    try {
                      const res = await fetchWithAuth(`/api/relationships/${relId}`, { method: 'DELETE' });
                      const data = await res.json();
                      if (data.success) {
                        const currentGp = availableGroupsPeriods.find(gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId));
                        if (currentGp?.id) {
                          const r = await fetch(
                            buildPersonLinksApiUrl({
                              userGroupsPeriodsId: currentGp.id,
                              userId: user?.id
                            })
                          );
                          const d = await r.json();
                          if (d.success) setPersonLinks(d.data || []);
                        }
                      } else setErrorModal({ title: 'Error', message: data.message || 'Failed to remove link' });
                    } catch (e) {
                      setErrorModal({ title: 'Error', message: 'Failed to remove link' });
                    }
                  }}
                />
              </ExpandableSection>
            );
            })()}
            {(() => {
              const associationsApp = (assignedApps || []).find((a) => a.slug === APP_SLUGS.ASSOCIATIONS);
              if (!associationsApp) return null;
              const associationsLabel =
                associationsApp?.metadata?.container_label_override || 'Associations';
              const currentGp = (availableGroupsPeriods || []).find(
                (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
              );
              const enabledTypes = parseEnabledAssociationTypes(associationsApp?.metadata);
              return (
                <ExpandableSection
                  title={associationsLabel}
                  isExpanded={sectionsExpanded.associations}
                  onToggle={() => toggleSection('associations')}
                  showActions={canAddAssociations}
                  headerExtra={
                    canAddAssociations && sectionsExpanded.associations ? (
                      <ProfileSectionAddButton
                        title="Add association"
                        onClick={() => setShowAddAssociationModal(true)}
                      />
                    ) : null
                  }
                >
                  <AssociationsSection
                    associations={associations}
                    canEdit={canRemoveAssociations}
                    onRemoveRequest={
                      canRemoveAssociations
                        ? (row) =>
                            setAssocRemoveConfirm({ id: row.id, label: row.groups_periods_label })
                        : undefined
                    }
                  />
                  <AddAssociationModal
                    isOpen={showAddAssociationModal}
                    onClose={() => setShowAddAssociationModal(false)}
                    ownerUgpId={currentGp?.id}
                    enabledTypes={enabledTypes}
                    assignmentMetadata={associationsApp?.metadata}
                    onCreated={async () => {
                      if (!currentGp?.id) return;
                      const r = await fetch(
                        buildAssociationsApiUrl(currentGp.id, { includeEnded: true })
                      );
                      const d = await r.json();
                      if (d.success) setAssociations(d.data || []);
                    }}
                  />
                </ExpandableSection>
              );
            })()}
            {(() => {
              const ppApp = (assignedApps || []).find((a) => a.slug === APP_SLUGS.PROFILE_PROPERTIES);
              if (!ppApp) return null;
              const ppLabel = ppApp?.metadata?.container_label_override || 'Profile Properties';
              const currentGp = (availableGroupsPeriods || []).find(
                (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
              );
              return (
                <ExpandableSection
                  title={ppLabel}
                  isExpanded={sectionsExpanded.profileProperties}
                  onToggle={() => toggleSection('profileProperties')}
                  headerExtra={
                    canAddUgpProperties && sectionsExpanded.profileProperties ? (
                      <ProfileSectionAddButton
                        title="Add property"
                        onClick={() => setShowUgpPropertyAddSheet(true)}
                      />
                    ) : null
                  }
                >
                  <UgpPropertiesSection
                    properties={ugpProperties}
                    assignmentMetadata={ppApp?.metadata}
                    loading={loadingUgpProperties}
                    canAdd={canAddUgpProperties}
                    canEdit={canEditUgpProperties}
                    canRemove={canRemoveUgpProperties}
                    ownerUgpId={currentGp?.id}
                    ownerGroupsPeriodsId={currentGp?.groups_periods_id}
                    addSheetOpen={showUgpPropertyAddSheet}
                    onAddSheetOpenChange={setShowUgpPropertyAddSheet}
                    onReload={reloadUgpProperties}
                  />
                </ExpandableSection>
              );
            })()}
            {/* Custom Forms — all forms assigned to this enrollment when custom_forms app is on */}
            {(() => {
              const customFormsApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.CUSTOM_FORMS);
              if (!customFormsApp) return null;
              const customFormsLabel = customFormsApp?.metadata?.container_label_override || 'Custom Forms';
              const leftColumnForms = forms || [];
              const visibleForms = canViewFormsSection
                ? leftColumnForms
                : [];
              return (
            <ExpandableSection
              title={customFormsLabel}
              isExpanded={sectionsExpanded.customForms}
              onToggle={() => toggleSection('customForms')}
            >
              {(function(){
                if (!canViewFormsSection) {
                  return (
                    <p style={styles.emptyText}>
                      You do not have permission to view forms on this profile.
                    </p>
                  );
                }
                if (visibleForms.length === 0) {
                  return <p style={styles.emptyText}>No forms assigned to this user.</p>;
                }
                return (
                  <div>
                  {visibleForms.map((form) => {
                    const relType =
                      normalizeFormRelationshipType(form.relationship_type) ||
                      FORM_RELATIONSHIP_TYPES.FILL_OUT;
                    const isAssignment = relType === FORM_RELATIONSHIP_TYPES.ASSIGNMENT;
                    const typeLabel = formatFormRelationshipTypeLabel(relType);
                    const hasSubmission = !!(
                      selectedSubmissionIdByFormId[form.id] || existingSubmissions[form.id]?.id
                    );
                    const existingSub = existingSubmissions[form.id];
                    const isDraft = !existingSub || canSubmitDraft(existingSub);
                    const formEditable = canEditProfileFormAnswers(formPerms, formPermCtx, {
                      hasExistingSubmission: hasSubmission,
                      isDraft
                    });
                    const formReadOnly =
                      !formEditable && canSeeProfileFormSubmission(formPerms, formPermCtx);
                    const showFillOutActions = formEditable && hasFormChanges(form.id);

                    if (isAssignment) {
                      const isActive = String(activeAssignmentFormId) === String(form.id);
                      return (
                        <button
                          key={form.id}
                          type="button"
                          className={`profile-custom-form-row${isActive ? ' is-active' : ''}`}
                          onClick={() => openAssignmentForm(form)}
                        >
                          <span className="profile-custom-form-row__title">{form.title}</span>
                          <span className="profile-custom-form-row__badge profile-custom-form-row__badge--assignment">
                            {typeLabel}
                          </span>
                        </button>
                      );
                    }

                    return (
                  <ExpandableSection
                    key={form.id}
                    title={form.title}
                    isExpanded={!!expandedFormIds[form.id]}
                    onToggle={() => toggleFormExpansion(form)}
                    showActions={showFillOutActions}
                    hasChanges={showFillOutActions}
                    onSave={() => handleFormSave(form)}
                    onCancel={() => handleFormCancel(form)}
                    headerExtra={
                      <span className="profile-custom-form-row__badge profile-custom-form-row__badge--fill-out">
                        {typeLabel}
                      </span>
                    }
                  >
                    <div>
                      {form.source_name && (
                        <div style={styles.badgeRow}>
                          <span style={styles.badgeSource}>{form.source_name}</span>
                        </div>
                      )}
                      {!canSeeProfileFormSubmission(formPerms, formPermCtx) && !formEditable ? (
                        <p style={styles.emptyText}>You do not have permission to view submissions.</p>
                      ) : (
                      <FormRenderer
                        form={form}
                        onSubmit={async () => {
                          await handleFormSave(form);
                        }}
                        isSubmitting={isSubmittingFormId === form.id}
                        initialData={formDataByFormId[form.id] !== undefined 
                          ? formDataByFormId[form.id] 
                          : submissionAnswersToFormData(
                              existingSubmissions[form.id]?.answers ||
                                getDefaultInitialAnswers(form)
                            )}
                        onFormDataChange={formEditable ? (data) => {
                          setFormDataByFormId(prev => ({ ...prev, [form.id]: data }));
                        } : undefined}
                        onPendingFileRegister={(localKey, file) =>
                          registerPendingFormFile(form.id, localKey, file)
                        }
                        onPendingFileRelease={(localKey) =>
                          releasePendingFormFile(form.id, localKey)
                        }
                        documentUrlById={formDocumentUrlByFormId[form.id] || {}}
                        hideTitle={true}
                        hideSubmitButton={true}
                        readOnly={formReadOnly}
                      />
                      )}
                    </div>
                  </ExpandableSection>
                    );
                  })}
              </div>
              );
            })()}
            </ExpandableSection>
            );
            })()}
            {/* Student Information section — only when student_information app is assigned */}
            {(() => {
              const studentInfoApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.STUDENT_INFORMATION);
              if (!studentInfoApp) return null;
              const label = studentInfoApp?.metadata?.container_label_override || 'Student Information';
              const enabledAttrs = Array.isArray(studentInfoApp?.metadata?.enabled_attributes)
                ? studentInfoApp.metadata.enabled_attributes
                : DEFAULT_STUDENT_INFO_ENABLED_ATTRIBUTES;
              const meta = studentInfoApp?.metadata || {};
              const gradeOptions = getGradeLevelOptions(meta);
              const statusOptions = getStudentStatusOptions(meta);
              const endedStatuses = getEndedStudentStatuses(meta, statusOptions);
              const popupEnabled = meta.exitdate_sync_popup !== false;
              const exitDateEnabled = enabledAttrs.includes('exit_date');
              const attrLabel = (key) => key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
              const optionsWithCurrent = (options, current) => {
                const list = [...options];
                const c = String(current ?? '').trim();
                if (c && !list.includes(c)) list.unshift(c);
                return list;
              };

              const handleStatusPick = (next) => {
                const val = studentInfoDraft.status;
                const wasEnded = endedStatuses.includes(val);
                const isEnded = endedStatuses.includes(next);
                const exitEmpty = !studentInfoDraft.exit_date;
                if (isEnded && !wasEnded && popupEnabled && exitDateEnabled && exitEmpty) {
                  setExitDateConfirm({ newStatus: next });
                  setStudentInfoBadgePicker(null);
                  return;
                }
                setStudentInfoDraft((prev) => ({ ...prev, status: next }));
                setStudentInfoBadgePicker(null);
              };

              return (
                <ExpandableSection
                  title={label}
                  isExpanded={sectionsExpanded.studentInformation}
                  onToggle={() => toggleSection('studentInformation')}
                  showActions={canEditStudentInformation && studentInfoReady}
                  hasChanges={hasStudentInfoChanges()}
                  onSave={handleSaveStudentInfo}
                  onCancel={handleCancelStudentInfo}
                >
                  <div className="profile-student-info">
                    {(() => {
                      const dateAttrs = enabledAttrs.filter((a) => a === 'enrollment_date' || a === 'exit_date');
                      const nonDateAttrs = enabledAttrs.filter((a) => a !== 'enrollment_date' && a !== 'exit_date');
                      const showStatusBadge = nonDateAttrs.includes('status');
                      const showGradeBadge = nonDateAttrs.includes('grade_level');
                      const showVendorBadge = nonDateAttrs.includes('vendor_groups_periods_id');
                      const showTrackBadge = nonDateAttrs.includes('track_period_id');
                      const showPathwayBadge = nonDateAttrs.includes('primary_pathway');
                      const otherNonDateAttrs = nonDateAttrs.filter(
                        (a) =>
                          a !== 'status' &&
                          a !== 'grade_level' &&
                          a !== 'vendor_groups_periods_id' &&
                          a !== 'track_period_id' &&
                          a !== 'primary_pathway'
                      );
                      const compactAttrKeys = ['previously_enrolled', 'transfer_credits'];
                      const showPrevEnrolled = otherNonDateAttrs.includes('previously_enrolled');
                      const showTransferCredits = otherNonDateAttrs.includes('transfer_credits');
                      const restNonDateAttrs = otherNonDateAttrs.filter(
                        (a) => !compactAttrKeys.includes(a)
                      );
                      const prevEnrolledDef = getStudentInformationFieldDef('previously_enrolled');
                      const prevEnrolledOffLabel = prevEnrolledDef?.label || 'Previously Enrolled?';
                      const prevEnrolledOnLabel =
                        prevEnrolledDef?.onLabel?.replace(/^\s*✓\s*/, '') || 'Previously Enrolled';
                      const statusVal = studentInfoDraft.status;
                      const gradeVal = studentInfoDraft.grade_level;
                      const vendorVal = studentInfoDraft.vendor_groups_periods_id;
                      const trackVal = studentInfoDraft.track_period_id;
                      const pathwayVal = studentInfoDraft.primary_pathway;
                      const vendorOptions = studentInfoFieldOptions.vendor_groups_periods_id || [];
                      const trackOptions = studentInfoFieldOptions.track_period_id || [];
                      const pathwayOptions = studentInfoFieldOptions.primary_pathway || [];
                      const vendorDisplay = !vendorVal
                        ? 'Not set'
                        : resolvedVendorGroupName ||
                          vendorOptions.find((o) => String(o.id) === String(vendorVal))?.group_name ||
                          studentInfo?.vendor_group_name ||
                          studentInfo?.vendor_groups_periods_label ||
                          '…';
                      const trackDisplay = (() => {
                        if (!trackVal) return 'Not set';
                        const hit = trackOptions.find((o) => String(o.id) === String(trackVal));
                        return hit?.label || studentInfo?.track_period_label || 'Selected';
                      })();
                      const pathwayDisplay = (() => {
                        if (!pathwayVal) return 'Not set';
                        const hit = pathwayOptions.find((o) => String(o.id) === String(pathwayVal));
                        return hit?.label || studentInfo?.primary_pathway_label || 'Selected';
                      })();
                      const creditsVal = studentInfoDraft.transfer_credits;
                      const hasCredits = hasTransferCreditsValue(creditsVal);
                      const creditsBadgeValue =
                        formatTransferCreditsBadgeValue(creditsVal) || TRANSFER_CREDITS_EMPTY_LABEL;

                      const renderTransferCreditsPicker = () => (
                        <div className="profile-choice-badge-menu-custom">
                          <label className="profile-choice-badge-menu__label" htmlFor="si-credits-picker">
                            Amount
                          </label>
                          <input
                            id="si-credits-picker"
                            type="number"
                            step="0.01"
                            min="0"
                            className="profile-field-input"
                            value={creditsVal ?? ''}
                            placeholder="0"
                            disabled={!canEditStudentInformation}
                            onChange={(e) =>
                              setStudentInfoDraft((prev) => ({
                                ...prev,
                                transfer_credits: e.target.value
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') setStudentInfoBadgePicker(null);
                            }}
                          />
                          <div className="profile-choice-badge-menu__actions">
                            {hasCredits && (
                              <button
                                type="button"
                                className="profile-choice-badge-menu__secondary"
                                onClick={() => {
                                  setStudentInfoDraft((prev) => ({ ...prev, transfer_credits: '' }));
                                  setStudentInfoBadgePicker(null);
                                }}
                              >
                                Clear
                              </button>
                            )}
                            <button
                              type="button"
                              className="profile-choice-badge-menu__done"
                              onClick={() => setStudentInfoBadgePicker(null)}
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      );

                      const renderDateBadgeField = (attr) => (
                        <StudentInfoDateBadge
                          key={attr}
                          attr={attr}
                          value={studentInfoDraft[attr]}
                          label={getStudentInformationFieldDef(attr)?.label || attrLabel(attr)}
                          disabled={!canEditStudentInformation}
                          onChange={(v) =>
                            setStudentInfoDraft((prev) => ({ ...prev, [attr]: v }))
                          }
                        />
                      );

                      const showBadgeGrid =
                        showStatusBadge ||
                        showGradeBadge ||
                        showVendorBadge ||
                        showTrackBadge ||
                        showPathwayBadge ||
                        showPrevEnrolled ||
                        showTransferCredits;

                      return (
                        <div className="profile-student-info-badges-wrap" ref={studentInfoBadgesWrapRef}>
                          {showBadgeGrid && (
                            <div className="profile-student-info-badge-grid">
                              {showStatusBadge && (
                                <div className="profile-si-badge-cell">
                                <StudentInfoChoiceBadge
                                  kicker={getStudentInformationFieldDef('status')?.label || 'Status'}
                                  displayValue={statusVal || 'Not set'}
                                  tone={statusBadgeTone(statusVal)}
                                  disabled={!canEditStudentInformation}
                                  isOpen={studentInfoBadgePicker === 'status'}
                                  menuId="si-status-picker"
                                  onToggle={() =>
                                    setStudentInfoBadgePicker((p) => (p === 'status' ? null : 'status'))
                                  }
                                >
                                  {optionsWithCurrent(statusOptions, statusVal).map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      role="option"
                                      aria-selected={opt === statusVal}
                                      className={`profile-choice-badge-menu__item${
                                        opt === statusVal ? ' is-selected' : ''
                                      }`}
                                      onClick={() => handleStatusPick(opt)}
                                    >
                                      {opt}
                                    </button>
                                  ))}
                                </StudentInfoChoiceBadge>
                                </div>
                              )}
                              {showGradeBadge && (
                                <div className="profile-si-badge-cell">
                                <StudentInfoChoiceBadge
                                  kicker={getStudentInformationFieldDef('grade_level')?.label || 'Grade'}
                                  displayValue={gradeVal || 'Not set'}
                                  tone="neutral"
                                  disabled={!canEditStudentInformation}
                                  isOpen={studentInfoBadgePicker === 'grade_level'}
                                  menuId="si-grade-picker"
                                  onToggle={() =>
                                    setStudentInfoBadgePicker((p) =>
                                      p === 'grade_level' ? null : 'grade_level'
                                    )
                                  }
                                >
                                  {gradeOptions.length > 0 ? (
                                    <>
                                      <button
                                        type="button"
                                        role="option"
                                        aria-selected={!gradeVal}
                                        className={`profile-choice-badge-menu__item${
                                          !gradeVal ? ' is-selected' : ''
                                        }`}
                                        onClick={() => {
                                          setStudentInfoDraft((prev) => ({ ...prev, grade_level: '' }));
                                          setStudentInfoBadgePicker(null);
                                        }}
                                      >
                                        —
                                      </button>
                                      {optionsWithCurrent(gradeOptions, gradeVal).map((opt) => (
                                        <button
                                          key={opt}
                                          type="button"
                                          role="option"
                                          aria-selected={String(opt) === String(gradeVal)}
                                          className={`profile-choice-badge-menu__item${
                                            String(opt) === String(gradeVal) ? ' is-selected' : ''
                                          }`}
                                          onClick={() => {
                                            setStudentInfoDraft((prev) => ({
                                              ...prev,
                                              grade_level: String(opt)
                                            }));
                                            setStudentInfoBadgePicker(null);
                                          }}
                                        >
                                          {opt}
                                        </button>
                                      ))}
                                    </>
                                  ) : (
                                    <div className="profile-choice-badge-menu-custom">
                                      <input
                                        type="text"
                                        className="profile-field-input"
                                        value={gradeVal ?? ''}
                                        placeholder="Enter grade level"
                                        disabled={!canEditStudentInformation}
                                        onChange={(e) =>
                                          setStudentInfoDraft((prev) => ({
                                            ...prev,
                                            grade_level: e.target.value
                                          }))
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') setStudentInfoBadgePicker(null);
                                        }}
                                      />
                                      <button
                                        type="button"
                                        className="profile-choice-badge-menu__done"
                                        onClick={() => setStudentInfoBadgePicker(null)}
                                      >
                                        Done
                                      </button>
                                    </div>
                                  )}
                                </StudentInfoChoiceBadge>
                                </div>
                              )}
                              {showVendorBadge && (
                                <div className="profile-si-badge-cell">
                                  <StudentInfoChoiceBadge
                                    kicker={
                                      getStudentInformationFieldDef('vendor_groups_periods_id')?.label ||
                                      'Vendor partner'
                                    }
                                    displayValue={vendorDisplay}
                                    tone="neutral"
                                    disabled={!canEditStudentInformation}
                                    isOpen={studentInfoBadgePicker === 'vendor_groups_periods_id'}
                                    menuId="si-vendor-picker"
                                    onToggle={() => {
                                      setStudentInfoBadgePicker((p) => {
                                        const next =
                                          p === 'vendor_groups_periods_id'
                                            ? null
                                            : 'vendor_groups_periods_id';
                                        if (next === 'vendor_groups_periods_id') {
                                          loadStudentInfoVendorOptions();
                                        }
                                        return next;
                                      });
                                    }}
                                  >
                                    {studentInfoVendorOptionsLoading ? (
                                      <p className="profile-field-hint--subtle">Loading teams…</p>
                                    ) : vendorOptions.length === 0 ? (
                                      <p className="profile-field-hint--subtle">
                                        No vendor teams available for this organization.
                                      </p>
                                    ) : (
                                      <>
                                    <button
                                      type="button"
                                      role="option"
                                      aria-selected={!vendorVal}
                                      className={`profile-choice-badge-menu__item${
                                        !vendorVal ? ' is-selected' : ''
                                      }`}
                                      onClick={() => {
                                        setStudentInfoDraft((prev) => ({
                                          ...prev,
                                          vendor_groups_periods_id: ''
                                        }));
                                        setResolvedVendorGroupName('');
                                        setStudentInfoBadgePicker(null);
                                      }}
                                    >
                                      —
                                    </button>
                                    {vendorOptions.map((opt) => (
                                      <button
                                        key={opt.id}
                                        type="button"
                                        role="option"
                                        aria-selected={String(opt.id) === String(vendorVal)}
                                        className={`profile-choice-badge-menu__item${
                                          String(opt.id) === String(vendorVal) ? ' is-selected' : ''
                                        }`}
                                        onClick={() => {
                                          setStudentInfoDraft((prev) => ({
                                            ...prev,
                                            vendor_groups_periods_id: String(opt.id)
                                          }));
                                          setResolvedVendorGroupName(opt.group_name || opt.label || '');
                                          setStudentInfoBadgePicker(null);
                                        }}
                                      >
                                        {opt.label || opt.group_name}
                                      </button>
                                    ))}
                                      </>
                                    )}
                                  </StudentInfoChoiceBadge>
                                </div>
                              )}
                              {showTrackBadge && (
                                <div className="profile-si-badge-cell">
                                  <StudentInfoChoiceBadge
                                    kicker={
                                      getStudentInformationFieldDef('track_period_id')?.label ||
                                      'Schedule track'
                                    }
                                    displayValue={trackDisplay}
                                    tone="neutral"
                                    disabled={!canEditStudentInformation}
                                    isOpen={studentInfoBadgePicker === 'track_period_id'}
                                    menuId="si-track-picker"
                                    onToggle={() =>
                                      setStudentInfoBadgePicker((p) =>
                                        p === 'track_period_id' ? null : 'track_period_id'
                                      )
                                    }
                                  >
                                    <button
                                      type="button"
                                      role="option"
                                      aria-selected={!trackVal}
                                      className={`profile-choice-badge-menu__item${
                                        !trackVal ? ' is-selected' : ''
                                      }`}
                                      onClick={() => {
                                        setStudentInfoDraft((prev) => ({
                                          ...prev,
                                          track_period_id: ''
                                        }));
                                        setStudentInfoBadgePicker(null);
                                      }}
                                    >
                                      —
                                    </button>
                                    {trackOptions.map((opt) => (
                                      <button
                                        key={opt.id}
                                        type="button"
                                        role="option"
                                        aria-selected={String(opt.id) === String(trackVal)}
                                        className={`profile-choice-badge-menu__item${
                                          String(opt.id) === String(trackVal) ? ' is-selected' : ''
                                        }`}
                                        onClick={() => {
                                          setStudentInfoDraft((prev) => ({
                                            ...prev,
                                            track_period_id: String(opt.id)
                                          }));
                                          setStudentInfoBadgePicker(null);
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    ))}
                                  </StudentInfoChoiceBadge>
                                </div>
                              )}
                              {showPathwayBadge && (
                                <div className="profile-si-badge-cell">
                                  <StudentInfoChoiceBadge
                                    kicker={
                                      getStudentInformationFieldDef('primary_pathway')?.label ||
                                      'Primary pathway'
                                    }
                                    displayValue={pathwayDisplay}
                                    tone="neutral"
                                    disabled={!canEditStudentInformation}
                                    isOpen={studentInfoBadgePicker === 'primary_pathway'}
                                    menuId="si-pathway-picker"
                                    onToggle={() =>
                                      setStudentInfoBadgePicker((p) =>
                                        p === 'primary_pathway' ? null : 'primary_pathway'
                                      )
                                    }
                                  >
                                    {pathwayOptions.length === 0 ? (
                                      <p className="profile-field-hint--subtle">
                                        No concentrations available in taxonomy for this organization.
                                      </p>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          role="option"
                                          aria-selected={!pathwayVal}
                                          className={`profile-choice-badge-menu__item${
                                            !pathwayVal ? ' is-selected' : ''
                                          }`}
                                          onClick={() => {
                                            setStudentInfoDraft((prev) => ({
                                              ...prev,
                                              primary_pathway: ''
                                            }));
                                            setStudentInfoBadgePicker(null);
                                          }}
                                        >
                                          —
                                        </button>
                                        {pathwayOptions.map((opt) => (
                                          <button
                                            key={opt.id}
                                            type="button"
                                            role="option"
                                            aria-selected={String(opt.id) === String(pathwayVal)}
                                            className={`profile-choice-badge-menu__item${
                                              String(opt.id) === String(pathwayVal) ? ' is-selected' : ''
                                            }`}
                                            onClick={() => {
                                              setStudentInfoDraft((prev) => ({
                                                ...prev,
                                                primary_pathway: String(opt.id)
                                              }));
                                              setStudentInfoBadgePicker(null);
                                            }}
                                          >
                                            {opt.label}
                                          </button>
                                        ))}
                                      </>
                                    )}
                                  </StudentInfoChoiceBadge>
                                </div>
                              )}
                              {showPrevEnrolled && (
                                <div className="profile-si-badge-cell">
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!!studentInfoDraft.previously_enrolled}
                                    className={`profile-si-toggle-badge${
                                      studentInfoDraft.previously_enrolled ? ' is-on' : ''
                                    }`}
                                    disabled={!canEditStudentInformation}
                                    onClick={() =>
                                      canEditStudentInformation &&
                                      setStudentInfoDraft((prev) => ({
                                        ...prev,
                                        previously_enrolled: !prev.previously_enrolled
                                      }))
                                    }
                                  >
                                    {studentInfoDraft.previously_enrolled ? (
                                      <>
                                        <span
                                          className="profile-badge-check profile-badge-check--green"
                                          aria-hidden
                                        >
                                          ✓
                                        </span>{' '}
                                        {prevEnrolledOnLabel}
                                      </>
                                    ) : (
                                      prevEnrolledOffLabel
                                    )}
                                  </button>
                                </div>
                              )}
                              {showTransferCredits && (
                                <div className="profile-si-badge-cell">
                                  <StudentInfoChoiceBadge
                                    hideKicker
                                    kicker="Transfer Credits"
                                    displayValue={creditsBadgeValue}
                                    tone="neutral"
                                    disabled={!canEditStudentInformation}
                                    isOpen={studentInfoBadgePicker === 'transfer_credits'}
                                    menuId="si-credits-picker-menu"
                                    onToggle={() =>
                                      setStudentInfoBadgePicker((p) =>
                                        p === 'transfer_credits' ? null : 'transfer_credits'
                                      )
                                    }
                                  >
                                    {renderTransferCreditsPicker()}
                                  </StudentInfoChoiceBadge>
                                </div>
                              )}
                            </div>
                          )}
                          {restNonDateAttrs.map((attr) => {
                            const val = studentInfoDraft[attr];
                            const setVal = (v) => setStudentInfoDraft((prev) => ({ ...prev, [attr]: v }));

                            return (
                              <div key={attr} className="profile-field">
                                <label className="profile-field-label" htmlFor={`si-${attr}`}>
                                  {attrLabel(attr)}
                                </label>
                                <input
                                  id={`si-${attr}`}
                                  type="text"
                                  className="profile-field-input"
                                  value={val ?? ''}
                                  onChange={(e) => setVal(e.target.value)}
                                  disabled={!canEditStudentInformation}
                                />
                              </div>
                            );
                          })}
                          {dateAttrs.length > 0 && (
                            <div className="profile-student-info-badge-grid profile-student-info-badge-grid--dates">
                              {dateAttrs.map(renderDateBadgeField)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {savingStudentInfo && <p className="profile-field-hint--subtle">Saving…</p>}
                </ExpandableSection>
              );
            })()}
            {/* Credentials & Keys — logins and keys per platform (credentials_keys table) */}
            {(() => {
              const ckApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.CREDENTIALS_AND_KEYS);
              if (!ckApp) return null;
              const label = ckApp?.metadata?.container_label_override || 'Credentials & Keys';
              const control = String(ckApp?.metadata?.credential_control || 'both');
              const locked = Array.isArray(ckApp?.metadata?.locked_platforms) ? ckApp.metadata.locked_platforms : [];
              const lockedConfigOk = control !== 'locked_only' || locked.length > 0;
              const {
                requiredFlexibleSlots,
                requiredCredentialSlots,
                requiredKeySlots,
                extraCredentialRows,
                extraKeyRows
              } = buildCredentialDisplayModel(credentialsKeys, ckApp?.metadata);
              const hasCredentialContent =
                requiredCredentialSlots.length > 0 ||
                extraCredentialRows.length > 0 ||
                requiredFlexibleSlots.some(
                  (s) =>
                    s.missing || (s.row && rowEntryType(s.row) === 'credentials')
                );
              const hasKeyContent =
                requiredKeySlots.length > 0 ||
                extraKeyRows.length > 0 ||
                requiredFlexibleSlots.some(
                  (s) => s.row && rowEntryType(s.row) === 'key'
                );
              const hasAnyCkContent = hasCredentialContent || hasKeyContent;
              const rowDeleteLabel = (row) => {
                const head = row.title ? `${row.platform_label} — ${row.title}` : row.platform_label;
                return String(row.entry_type || '').toLowerCase() === 'key' ? `Key: ${head}` : head;
              };
              const hasTxt = (v) => v != null && String(v).trim() !== '';

              const renderMissingCkSlot = (slot) => {
                const isKey = slot.entryType === 'key';
                const isFlexible = slot.entryType === 'any';
                const kindLabel = isFlexible
                  ? 'credential or key'
                  : isKey
                    ? 'key'
                    : 'credential';
                const addOptions = { title: slot.requiredTitle };
                if (!isFlexible) addOptions.entry_type = slot.entryType;
                return (
                <div
                  key={`required-missing-${slot.entryType}-${slot.requiredTitle}`}
                  className="profile-credential-row profile-credential-row--required-missing"
                >
                  <button
                    type="button"
                    className="profile-credential-row-missing-trigger"
                    disabled={!canAddCredentials}
                    onClick={() => {
                      if (canAddCredentials) {
                        openAddCredentialsKeysEntry(addOptions);
                      }
                    }}
                    aria-label={
                      canAddCredentials
                        ? `Add required ${kindLabel}: ${slot.requiredTitle}`
                        : `Required ${kindLabel} missing: ${slot.requiredTitle}`
                    }
                    title={canAddCredentials ? `Add: ${slot.requiredTitle}` : slot.requiredTitle}
                  >
                    <span className="profile-credential-row-title profile-credential-row-title--missing">
                      {slot.requiredTitle}
                    </span>
                  </button>
                </div>
                );
              };

              const renderCkEntryRow = (row) => {
                const isKey = String(row.entry_type || '').toLowerCase() === 'key';
                const rid = String(row.id);
                const expanded = !!ckExpandedRowIds[rid];
                return (
                  <div
                    key={row.id}
                    className={
                      isKey
                        ? 'profile-credential-row'
                        : 'profile-credential-row profile-credential-row--credential'
                    }
                    style={{
                      background: '#fff',
                      borderRadius: '8px',
                      marginBottom: '6px',
                      border: '1px solid #e2e8f0',
                      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setCkExpandedRowIds((prev) => ({ ...prev, [rid]: !prev[rid] }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setCkExpandedRowIds((prev) => ({ ...prev, [rid]: !prev[rid] }));
                        }
                      }}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        outline: 'none',
                        gap: 10
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                          <span
                            style={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 6,
                              background: '#e0f2fe',
                              color: '#0369a1',
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em'
                            }}
                          >
                            {row.platform_label}
                          </span>
                          {row.title && (
                            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' }}>
                              {row.title}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          {expanded ? 'Click to collapse' : 'Click to view details'}
                        </div>
                      </div>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {(canEditCredentials || canRemoveCredentials) && (
                          <>
                            {canEditCredentials && (
                              <button
                                type="button"
                                onClick={() => openEditCredentialsKeysEntry(row)}
                                style={{
                                  padding: '2px 4px',
                                  color: '#3b82f6',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                aria-label="Edit entry"
                                title="Edit entry"
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                            )}
                            {canRemoveCredentials && (
                              <button
                                type="button"
                                onClick={() => setCkDeleteConfirm({ id: row.id, label: rowDeleteLabel(row) })}
                                style={{
                                  padding: '2px 4px',
                                  fontSize: '1rem',
                                  color: '#dc2626',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  lineHeight: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                aria-label="Delete entry"
                                title="Delete entry"
                              >
                                ×
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {expanded && (
                      <div
                        style={{
                          padding: '12px',
                          borderTop: '1px solid #f1f5f9',
                          background: '#fafafa',
                          fontSize: '0.85rem',
                          color: '#334155',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8
                        }}
                      >
                        {(() => {
                          const missingTone = { color: '#94a3b8', fontStyle: 'italic' };
                          if (isKey) {
                            return (
                              <>
                                <div>
                                  <span style={{ fontWeight: 600, color: '#64748b' }}>Value </span>
                                  {hasTxt(row.password) ? (
                                    <span style={{ wordBreak: 'break-all' }}>{String(row.password).trim()}</span>
                                  ) : (
                                    <span style={missingTone}>No value given</span>
                                  )}
                                </div>
                                {hasTxt(row.notes) && (
                                  <div>
                                    <span style={{ fontWeight: 600, color: '#64748b' }}>Notes </span>
                                    <span style={{ whiteSpace: 'pre-wrap' }}>{String(row.notes).trim()}</span>
                                  </div>
                                )}
                              </>
                            );
                          }
                          return (
                            <>
                              <div>
                                <span style={{ fontWeight: 600, color: '#64748b' }}>Email </span>
                                {hasTxt(row.email) ? (
                                  <span>{String(row.email).trim()}</span>
                                ) : (
                                  <span style={missingTone}>No email given</span>
                                )}
                              </div>
                              {hasTxt(row.username) && (
                                <div>
                                  <span style={{ fontWeight: 600, color: '#64748b' }}>Username </span>
                                  <span>{String(row.username).trim()}</span>
                                </div>
                              )}
                              <div>
                                <span style={{ fontWeight: 600, color: '#64748b' }}>Password </span>
                                {hasTxt(row.password) ? (
                                  <span style={{ wordBreak: 'break-all' }}>{String(row.password).trim()}</span>
                                ) : (
                                  <span style={missingTone}>No password given</span>
                                )}
                              </div>
                              {hasTxt(row.notes) && (
                                <div>
                                  <span style={{ fontWeight: 600, color: '#64748b' }}>Notes </span>
                                  <span style={{ whiteSpace: 'pre-wrap' }}>{String(row.notes).trim()}</span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <ExpandableSection
                  title={label}
                  isExpanded={sectionsExpanded.credentialsAndKeys}
                  onToggle={() => toggleSection('credentialsAndKeys')}
                  showActions={false}
                  headerExtra={
                    canAddCredentials && lockedConfigOk && sectionsExpanded.credentialsAndKeys ? (
                      <ProfileSectionAddButton
                        title="Add entry"
                        onClick={() => openAddCredentialsKeysEntry()}
                      />
                    ) : null
                  }
                >
                  {!lockedConfigOk ? (
                    <p style={styles.emptyText}>
                      For &quot;Locked list only&quot;, add at least one platform in Apps Manager assignment settings.
                    </p>
                  ) : !hasAnyCkContent ? (
                    <p style={styles.emptyText}>No credentials or keys yet.</p>
                  ) : (
                    <>
                      {hasCredentialContent && (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                            Credentials
                          </div>
                          {requiredFlexibleSlots.map((slot) => {
                            if (!slot.missing && slot.row && rowEntryType(slot.row) === 'credentials') {
                              return renderCkEntryRow(slot.row);
                            }
                            if (slot.missing) return renderMissingCkSlot(slot);
                            return null;
                          })}
                          {requiredCredentialSlots.map((slot) => {
                            if (!slot.missing && slot.row) {
                              return renderCkEntryRow(slot.row);
                            }
                            return renderMissingCkSlot(slot);
                          })}
                          {extraCredentialRows.map((row) => renderCkEntryRow(row))}
                        </div>
                      )}
                      {hasKeyContent && (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                            Keys
                          </div>
                          {requiredFlexibleSlots.map((slot) => {
                            if (!slot.missing && slot.row && rowEntryType(slot.row) === 'key') {
                              return renderCkEntryRow(slot.row);
                            }
                            return null;
                          })}
                          {requiredKeySlots.map((slot) => {
                            if (!slot.missing && slot.row) {
                              return renderCkEntryRow(slot.row);
                            }
                            return renderMissingCkSlot(slot);
                          })}
                          {extraKeyRows.map((row) => renderCkEntryRow(row))}
                        </div>
                      )}
                    </>
                  )}
                </ExpandableSection>
              );
            })()}
            {/* Attachments app — documents table (links + uploads) */}
            {(() => {
              const attachmentsApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.ATTACHMENTS);
              if (!attachmentsApp) return null;
              const label = attachmentsApp?.metadata?.container_label_override || 'Attachments';
              const categoryOptions = getDocumentCategoryOptions(attachmentsApp);
              const showCategories = categoryOptions.length > 0;
              const { requiredSlots, extraDocuments } = buildAttachmentDisplayModel(
                documents,
                attachmentsApp?.metadata
              );
              const renderDocumentWidget = (doc) => {
                const mediaKey = String(doc.media_type || '').toLowerCase() || 'other';
                const mediaLabel = DOC_MEDIA_LABELS[mediaKey] || 'File';
                return (
                  <article key={doc.id} className="profile-document-widget" role="listitem">
                    {(canEditAttachments || canRemoveAttachments) && (
                      <div className="profile-document-widget-actions">
                        {canEditAttachments && (
                          <button
                            type="button"
                            className="profile-document-edit"
                            onClick={() => openEditDocument(doc, attachmentsApp)}
                            aria-label="Edit attachment"
                            title="Edit attachment"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        )}
                        {canRemoveAttachments && (
                          <button
                            type="button"
                            className="profile-document-delete"
                            onClick={() =>
                              setDocDeleteConfirm({ id: doc.id, title: doc.title || 'Attachment' })
                            }
                            aria-label="Remove attachment"
                            title="Remove attachment"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      className="profile-document-widget-open"
                      disabled={!doc.url}
                      onClick={() => doc.url && window.open(doc.url, '_blank', 'noopener,noreferrer')}
                      aria-label={doc.url ? `Open ${doc.title || 'attachment'} in new tab` : 'No link'}
                      title={doc.url ? 'Open in new tab' : undefined}
                    >
                      <div className="profile-document-widget-icon" aria-hidden>
                        {getDocMediaIcon(mediaKey)}
                      </div>
                      {showCategories && doc.category ? (
                        <span className="profile-document-widget-category">
                          {formatDocCategoryLabel(doc.category)}
                        </span>
                      ) : (
                        <span className="profile-document-widget-type">{mediaLabel}</span>
                      )}
                      <span className="profile-document-widget-title">{doc.title}</span>
                      {doc.description ? (
                        <span className="profile-document-widget-description">{doc.description}</span>
                      ) : null}
                    </button>
                  </article>
                );
              };

              return (
                <ExpandableSection
                  title={label}
                  isExpanded={sectionsExpanded.attachmentsApp}
                  onToggle={() => toggleSection('attachmentsApp')}
                  showActions={false}
                  headerExtra={
                    canAddAttachments && sectionsExpanded.attachmentsApp ? (
                      <ProfileSectionAddButton
                        title="Add attachment"
                        onClick={() => openAddDocument(attachmentsApp)}
                      />
                    ) : null
                  }
                >
                  {requiredSlots.length === 0 && documents.length === 0 ? (
                    <p style={styles.emptyText}>No attachments yet.</p>
                  ) : (
                    <div className="profile-documents-grid" role="list">
                      {requiredSlots.map((slot) => {
                        if (!slot.missing && slot.doc) {
                          return renderDocumentWidget(slot.doc);
                        }
                        return (
                          <article
                            key={`required-missing-${slot.requiredTitle}`}
                            className="profile-document-widget profile-document-widget--required-missing"
                            role="listitem"
                          >
                            <button
                              type="button"
                              className="profile-document-widget-open profile-document-widget-open--missing"
                              disabled={!canAddAttachments}
                              onClick={() => {
                                if (canAddAttachments) {
                                  openAddDocument(attachmentsApp, { title: slot.requiredTitle });
                                }
                              }}
                              aria-label={
                                canAddAttachments
                                  ? `Add required attachment: ${slot.requiredTitle}`
                                  : `Required attachment missing: ${slot.requiredTitle}`
                              }
                              title={canAddAttachments ? `Add: ${slot.requiredTitle}` : slot.requiredTitle}
                            >
                              <div className="profile-document-widget-icon profile-document-widget-icon--missing" aria-hidden>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                  <line x1="12" y1="11" x2="12" y2="17" />
                                  <line x1="9" y1="14" x2="15" y2="14" />
                                </svg>
                              </div>
                              <span className="profile-document-widget-title profile-document-widget-title--missing">
                                {slot.requiredTitle}
                              </span>
                            </button>
                          </article>
                        );
                      })}
                      {extraDocuments.map((doc) => renderDocumentWidget(doc))}
                    </div>
                  )}
                </ExpandableSection>
              );
            })()}
            {/* Payments app — payments table (monthly schedule inherited from group-period) */}
            {(() => {
              const paymentsApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.PAYMENTS);
              if (!paymentsApp) return null;
              const label = paymentsApp?.metadata?.container_label_override || 'Payments';
              return (
                <ExpandableSection
                  title={label}
                  isExpanded={sectionsExpanded.payments}
                  onToggle={() => toggleSection('payments')}
                  showActions={false}
                >
                  {payments.length === 0 ? (
                    <p style={styles.emptyText}>No payment schedule for this group-period.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {payments.map((item) => {
                        // Family-facing surface: map the derived status through the charge's
                        // profile display rules (e.g. partial → "No" under binary_strict).
                        const disp = resolveDisplayStatus(item.status, item.display, 'profile');
                        const meta = disp.meta;
                        const key = item.requirement_id || item.label || item.month_key;
                        const amountText =
                          item.amount != null ? formatPaymentAmount(item.amount, item.currency) : '';
                        const showAmount = item.display?.profile?.show_amount !== false;
                        const isAmountMode = item.tracking_mode === 'amount';
                        return (
                          <div
                            key={key}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: 8,
                              background: '#f8fafc'
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a' }}>
                                {item.label || formatMonthKeyLabel(item.month_key)}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                {showAmount && amountText ? amountText : ''}
                                {showAmount && isAmountMode && item.net_paid > 0
                                  ? ` · paid ${formatPaymentAmount(item.net_paid, item.currency)}`
                                  : ''}
                                {item.required_by_date ? ` · due ${item.required_by_date}` : ''}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => canEditPayments && openPaymentEditor(item)}
                              disabled={!canEditPayments}
                              style={{
                                flexShrink: 0,
                                padding: '4px 10px',
                                border: `1px solid ${meta.border}`,
                                borderRadius: 6,
                                background: meta.bg,
                                color: meta.color,
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                cursor: canEditPayments ? 'pointer' : 'default'
                              }}
                              title={canEditPayments ? 'Manage payments' : disp.label}
                            >
                              {disp.label}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ExpandableSection>
              );
            })()}
          </div>
          ) : null}
        </div>

        {/* Resize Handle */}
        {!leftColumnCollapsed ? (
        <div 
          style={{ 
            ...styles.resizeHandle, 
            background: (isDragging || hoveredHandle === 'left') ? '#3b82f6' : '#f3f4f6'
          }}
          onMouseDown={(e) => handleMouseDown(e, 'left')}
          onMouseEnter={() => setHoveredHandle('left')}
          onMouseLeave={() => setHoveredHandle(null)}
        />
        ) : null}

        {activeAssignmentForm ? (
          <div className="profile-assignment-shell">
            <div className="profile-assignment-column">
              <FormAssignmentViewer
                  form={activeAssignmentForm}
                  submissions={submissionsByFormId[activeAssignmentForm.id] || []}
                  selectedSubmissionId={selectedSubmissionIdByFormId[activeAssignmentForm.id]}
                  formData={
                    formDataByFormId[activeAssignmentForm.id] !== undefined
                      ? formDataByFormId[activeAssignmentForm.id]
                      : getDefaultInitialAnswers(activeAssignmentForm)
                  }
                  originalFormData={originalFormDataByFormId[activeAssignmentForm.id] || {}}
                  isSubmitting={isSubmittingFormId === activeAssignmentForm.id}
                  answersReadOnly={activeAssignmentAnswersReadOnly}
                  canCreateSubmission={activeAssignmentCanStartNew}
                  canDeleteSubmission={canDeleteSubmission}
                  canViewQuestionAnswers={canViewQuestionAnswers}
                  canUpdatePoints={canUpdatePoints}
                  assignmentRelationshipId={activeAssignmentForm.relationship_id}
                  collaborationCapabilities={activeAssignmentCollaborationCapabilities}
                  currentUserId={authUser?.id}
                  fetchWithAuth={fetchWithAuth}
                  assignmentMetadata={activeAssignmentForm.relationship_metadata}
                  formMetadata={activeAssignmentForm.form_metadata}
                  hasSubmission={
                    !!selectedSubmissionIdByFormId[activeAssignmentForm.id]
                  }
                  selectedSubmission={activeAssignmentSubmission}
                  pendingFileBlobs={
                    pendingFormFileBlobsRef.current[activeAssignmentForm.id] || {}
                  }
                  onSelectSubmission={(submissionId) =>
                    selectAssignmentSubmission(activeAssignmentForm, submissionId)
                  }
                  onFormDataChange={(data) => {
                    setFormDataByFormId((prev) => ({ ...prev, [activeAssignmentForm.id]: data }));
                  }}
                  onSave={(data, action) =>
                    handleFormSave(activeAssignmentForm, data, action || SUBMISSION_ACTIONS.SAVE)
                  }
                  onApplyPoints={(data) =>
                    handleFormSave(activeAssignmentForm, data, SUBMISSION_ACTIONS.GRADE)
                  }
                  onSubmit={(data) => handleFormSubmitFinal(activeAssignmentForm, data)}
                  onCancel={() => handleFormCancel(activeAssignmentForm)}
                  onDelete={() => handleFormDelete(activeAssignmentForm)}
                  onNewSubmission={() => startNewAssignmentSubmission(activeAssignmentForm)}
                  onClose={closeAssignmentForm}
                  onPendingFileRegister={(localKey, file) =>
                    registerPendingFormFile(activeAssignmentForm.id, localKey, file)
                  }
                  onPendingFileRelease={(localKey) =>
                    releasePendingFormFile(activeAssignmentForm.id, localKey)
                  }
                  documentUrlById={formDocumentUrlByFormId[activeAssignmentForm.id] || {}}
                />
            </div>
          </div>
        ) : null}

        {/* Dynamic Columns based on config */}
        <div
          style={styles.columnsDynamicWrap}
          className="profile-columns-dynamic-wrap"
        >
        {(() => {
          const enabledColumns = getEnabledColumns();
          const columnCount = enabledColumns.length;
          const dynamicColumnWidth = columnCount > 0 ? 100 / columnCount : 100;
          
          return enabledColumns.map((column, colIndex) => {
            const enabledBuckets = getEnabledBuckets(column.id);
            
            return (
              <React.Fragment key={column.id}>
                {/* Resize Handle (except before first dynamic column) */}
                {colIndex > 0 && (
                  <div 
                    style={{ 
                      ...styles.resizeHandle, 
                      background: (isDragging || hoveredHandle === `col-${colIndex}`) ? '#3b82f6' : '#f3f4f6'
                    }}
                    onMouseDown={(e) => handleMouseDown(e, colIndex === 1 ? 'left' : 'right')}
                    onMouseEnter={() => setHoveredHandle(`col-${colIndex}`)}
                    onMouseLeave={() => setHoveredHandle(null)}
                  />
                )}
                
                {/* Dynamic Column */}
                <div
                  className={
                    activeAssignmentForm
                      ? 'profile-column profile-column--collapsed'
                      : 'profile-column'
                  }
                  style={{ ...styles.column, flex: `1 1 ${dynamicColumnWidth}%`, minWidth: 0, minHeight: 0 }}
                >
                  <div style={styles.columnHeader}>
                    <h2 style={styles.columnTitle}>{column.title || column.id}</h2>
                  </div>
                  <div className="profile-column-scroll" style={styles.columnContent}>
                    {enabledBuckets.length === 0 ? (
                      <p style={styles.emptyText}>No buckets enabled for this column.</p>
                    ) : (
                      enabledBuckets.map(bucket => renderBucketContent(bucket.id))
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          });
        })()}
        </div>
      </div>
      {/* Add Person Link modal — modern UI */}
      {showAddPersonLinkModal && (() => {
        const personLinksApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.PERSON_LINKS);
        const requiredFields = (personLinksApp?.metadata?.required_fields || []).map(f => String(f).toLowerCase());
        const currentGp = availableGroupsPeriods.find(gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId));
        const parentUgpId = currentGp?.id;
        const personLinkTypeCards = getPersonLinkTypeCardsForMetadata(personLinksApp?.metadata);
        const REL_TITLES = PERSON_LINK_ADD_TITLES;
        const addSearchOnly = isPersonLinkSearchOnlyType(addPersonLinkRelationshipType);
        const CURRENT_USER_LABEL = PERSON_LINK_CURRENT_USER_LABELS;
        const TARGET_LABEL = PERSON_LINK_TARGET_LABELS;
        const searchUsers = async () => {
          const q = addPersonLinkUserSearch.trim();
          if (!q) return;
          const params = new URLSearchParams();
          params.set('search', q);
          if (addSearchOnly && selectedGroupsPeriodsId) {
            params.set('groups_periods_id', selectedGroupsPeriodsId);
            params.set('person_link_org_scope', 'true');
            if (!personLinkRequiresPeriodMatch(addPersonLinkRelationshipType)) {
              params.set('person_link_match_period', 'false');
            }
          }
          const url = `/api/users?${params.toString()}`;
          setAddPersonLinkSearching(true);
          try {
            const res = await fetchWithAuth(url);
            const data = await res.json();
            if (data.success) {
              const list = Array.isArray(data.data) ? data.data : [];
              const people = list.filter(u => u.id !== user?.id);
              if (addSearchOnly) {
                const seats = [];
                people.forEach((person) => {
                  const anchors = Array.isArray(person.anchor_ugps) ? person.anchor_ugps : [];
                  anchors.forEach((anchor) => {
                    seats.push({ ...person, anchorUgp: anchor, resultKey: String(anchor.id) });
                  });
                });
                setAddPersonLinkUserResults(seats);
              } else {
                setAddPersonLinkUserResults(people);
              }
            } else setAddPersonLinkUserResults([]);
          } catch {
            setAddPersonLinkUserResults([]);
          } finally {
            setAddPersonLinkSearching(false);
          }
        };
        const resetModal = () => {
          setShowAddPersonLinkModal(false);
          setAddPersonLinkSelectedUser(null);
          setAddPersonLinkUserSearch('');
          setAddPersonLinkUserResults([]);
          setAddPersonLinkMode('create');
          setAddPersonLinkGuardianType('father');
          setAddPersonLinkSiblingType('brother');
          setAddPersonLinkPrimary(false);
          setAddPersonLinkEmergency(false);
          setAddPersonLinkCreateFirstName('');
          setAddPersonLinkCreateLastName('');
          setAddPersonLinkCreateEmail('');
          setAddPersonLinkCreatePhone('');
        };
        const existingGuardians = (personLinks || []).filter(pl => (pl.relationship_type || '').toLowerCase() === 'guardian');
        const hasPrimaryGuardian = existingGuardians.some(pl => pl.metadata?.primary === true);
        const usedGuardianIndices = new Set(existingGuardians.map(pl => (pl.metadata?.guardian_index ?? 0)));
        let nextGuardianIndex = 0;
        while (usedGuardianIndices.has(nextGuardianIndex)) nextGuardianIndex++;
        const existingSameType = (personLinks || []).filter(pl => (pl.relationship_type || '').toLowerCase() === addPersonLinkRelationshipType);
        const usedIndices = new Set(
          existingSameType.map(
            (pl) => pl.metadata?.index ?? pl.metadata?.guardian_index ?? pl.metadata?.sibling_index ?? 0
          )
        );
        let nextIndex = 0;
        while (usedIndices.has(nextIndex)) nextIndex++;
        const createBaseValid = !!addPersonLinkCreateFirstName.trim() && !!addPersonLinkCreateLastName.trim();
        const createEmailValid = !requiredFields.includes('email') || !!addPersonLinkCreateEmail.trim();
        const createPhoneValid = !requiredFields.includes('phone') || !!addPersonLinkCreatePhone.trim();
        const canSubmit = addPersonLinkMode === 'search'
          ? !!addPersonLinkSelectedUser
          : (createBaseValid && createEmailValid && createPhoneValid);
        const showPersonLinkActions =
          addPersonLinkMode === 'search' ? !!addPersonLinkSelectedUser : createBaseValid;
        const isGuardianLink = isGuardianPersonLinkType(addPersonLinkRelationshipType);
        const isSiblingLink = isSiblingPersonLinkType(addPersonLinkRelationshipType);
        const isUserToUserLink = isUserToUserPersonLinkType(addPersonLinkRelationshipType);
        const enrollmentContextLabel = currentGp
          ? [currentGp.group_name, currentGp.period_title || currentGp.period_code].filter(Boolean).join(' · ')
          : null;
        const selectPersonLinkType = (next) => {
          setAddPersonLinkRelationshipType(next);
          if (isPersonLinkSearchOnlyType(next)) {
            setAddPersonLinkMode('search');
            setAddPersonLinkSelectedUser(null);
            setAddPersonLinkUserResults([]);
          }
        };
        const canSubmitLink = isUserToUserLink ? !!user?.id : !!parentUgpId;
        const handleAdd = async () => {
          if (!canSubmit) return;
          if (isUserToUserLink) {
            if (!user?.id) return;
          } else if (!parentUgpId) {
            return;
          }
          setSavingPersonLink(true);
          try {
            let targetUser = null;
            let childUgp = null;
            if (addPersonLinkMode === 'search') {
              targetUser = addPersonLinkSelectedUser;
              if (!isUserToUserLink) {
                const matchPeriod = personLinkRequiresPeriodMatch(addPersonLinkRelationshipType);
                childUgp = targetUser.anchorUgp || null;
                if (!childUgp) {
                  const ugpParams = new URLSearchParams({
                    userId: String(targetUser.id),
                    groups_periods_id: String(selectedGroupsPeriodsId),
                    active: 'true'
                  });
                  ugpParams.set('person_link_org_scope', 'true');
                  if (!matchPeriod) ugpParams.set('person_link_match_period', 'false');
                  const ugpRes = await fetchWithAuth(`/api/user-groups-periods?${ugpParams.toString()}`);
                  const ugpData = await ugpRes.json();
                  childUgp = ugpData.success && ugpData.data?.length > 0 ? ugpData.data[0] : null;
                }
                if (!childUgp) {
                  setErrorModal({
                    title: matchPeriod ? 'Not in this organization period' : 'Not in this organization',
                    message: matchPeriod
                      ? 'This person must have an active assignment in the same organization and school period before you can link them.'
                      : 'This person must have an active assignment in the same organization before you can link them. Their period can differ from this student.'
                  });
                  setSavingPersonLink(false);
                  return;
                }
              }
            } else {
              const createRes = await fetch('/api/users/create-without-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  first_name: addPersonLinkCreateFirstName.trim(),
                  last_name: addPersonLinkCreateLastName.trim(),
                  email: addPersonLinkCreateEmail.trim() || null,
                  phone: addPersonLinkCreatePhone.trim() || null
                })
              });
              const createData = await createRes.json();
              if (!createData.success || !createData.user) {
                setErrorModal({ title: 'Error', message: createData.message || 'Failed to create person' });
                setSavingPersonLink(false);
                return;
              }
              targetUser = createData.user;
            }
            const metadata = isGuardianLink
              ? {
                  guardian_type: addPersonLinkGuardianType,
                  primary: addPersonLinkPrimary,
                  emergency: addPersonLinkEmergency,
                  guardian_index: nextGuardianIndex,
                  ...(parentUgpId ? { created_from_ugp_id: parentUgpId } : {})
                }
              : isSiblingLink
                ? {
                    sibling_type: addPersonLinkSiblingType,
                    emergency: addPersonLinkEmergency,
                    sibling_index: nextIndex,
                    ...(parentUgpId ? { created_from_ugp_id: parentUgpId } : {})
                  }
                : { index: nextIndex };
            let relBody;
            if (isUserToUserLink) {
              relBody = {
                parent_table: 'users',
                parent_id: user.id,
                child_table: 'users',
                child_id: targetUser.id,
                relationship_type: addPersonLinkRelationshipType,
                metadata
              };
            } else {
              relBody = {
                parent_table: 'user_groups_periods',
                parent_id: parentUgpId,
                child_table: 'user_groups_periods',
                child_id: childUgp.id,
                relationship_type: addPersonLinkRelationshipType,
                metadata
              };
            }
            const res = await fetch('/api/relationships', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(relBody)
            });
            const data = await res.json();
            if (data.success) {
              const r = await fetch(
                buildPersonLinksApiUrl({
                  userGroupsPeriodsId: parentUgpId,
                  userId: user?.id
                })
              );
              const d = await r.json();
              if (d.success) setPersonLinks(d.data || []);
              resetModal();
            } else setErrorModal({ title: 'Error', message: data.message || 'Failed to add link' });
          } catch (e) {
            setErrorModal({ title: 'Error', message: 'Failed to add link' });
          } finally {
            setSavingPersonLink(false);
          }
        };
        return (
          <div className="profile-sheet-overlay profile-sheet-overlay--raised">
            <div
              className="profile-sheet-panel profile-sheet-panel--form profile-sheet-panel--pl-form"
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-person-link-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="profile-sheet-header">
                <div className="profile-sheet-header-main">
                  <h2 id="add-person-link-title" className="profile-sheet-title">
                    {REL_TITLES[addPersonLinkRelationshipType] || 'Add Person Link'}
                  </h2>
                  <p className="profile-sheet-subtext">
                    Choose a relationship type, then link the other person on the right.
                  </p>
                </div>
                <button type="button" className="profile-sheet-close" onClick={resetModal} aria-label="Close">
                  ×
                </button>
              </div>
              <div className="profile-sheet-form-body profile-sheet-form-body--pl-flow">
                <div className="profile-pl-flow" ref={addPlFlowRef}>
                  {addPlConnectorPath && (
                    <svg className="profile-pl-flow-connector" aria-hidden="true">
                      <defs>
                        <marker
                          id="profile-pl-flow-arrow"
                          markerWidth="8"
                          markerHeight="8"
                          refX="7"
                          refY="4"
                          orient="auto"
                        >
                          <path d="M0,0 L8,4 L0,8 Z" fill="#64748b" />
                        </marker>
                      </defs>
                      <path
                        d={addPlConnectorPath}
                        fill="none"
                        stroke="#94a3b8"
                        strokeWidth="2"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        markerEnd="url(#profile-pl-flow-arrow)"
                      />
                    </svg>
                  )}
                  <div className="profile-pl-flow-spine">
                    <div className="profile-pl-flow-node profile-pl-flow-node--subject">
                      <span className="profile-pl-flow-kicker">
                        {CURRENT_USER_LABEL[addPersonLinkRelationshipType]}
                      </span>
                      <strong className="profile-pl-flow-name">
                        {user?.first_name} {user?.last_name}
                      </strong>
                      {enrollmentContextLabel && (
                        <span className="profile-pl-flow-context">{enrollmentContextLabel}</span>
                      )}
                      <span
                        className={`profile-pl-flow-badge${
                          isUserToUserLink ? ' profile-pl-flow-badge--user' : ' profile-pl-flow-badge--ugp'
                        }`}
                      >
                        {isUserToUserLink ? 'User (all periods)' : 'This UGP context'}
                      </span>
                    </div>

                    <div className="profile-pl-flow-line profile-pl-flow-line--short" aria-hidden="true" />

                    <div className="profile-pl-rel-cards" role="radiogroup" aria-label="Relationship type">
                      {personLinkTypeCards.map((card) => {
                        const selected = addPersonLinkRelationshipType === card.value;
                        return (
                          <button
                            key={card.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={`profile-pl-rel-card${selected ? ' is-selected' : ''}`}
                            onClick={() => selectPersonLinkType(card.value)}
                          >
                            <span className="profile-pl-rel-card-head">
                              <span className="profile-pl-rel-card-label">{card.label}</span>
                              <span className="profile-pl-rel-card-shape">{card.linkShape}</span>
                            </span>
                            {selected && (
                              <>
                                <p className="profile-pl-rel-card-sub">{card.subtext}</p>
                                {card.scopeNote && (
                                  <p className="profile-pl-rel-card-note">{card.scopeNote}</p>
                                )}
                              </>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="profile-pl-flow-target">
                    <div
                      ref={addPlTargetHeadRef}
                      className="profile-pl-flow-node profile-pl-flow-node--target"
                    >
                      <span className="profile-pl-flow-kicker">
                        {TARGET_LABEL[addPersonLinkRelationshipType]}
                      </span>
                      <span className="profile-pl-flow-target-hint">
                        {isUserToUserLink
                          ? isGuardianLink
                            ? 'Search for an existing person or create a new guardian user.'
                            : 'Search for an existing person or create a new sibling user.'
                          : personLinkRequiresPeriodMatch(addPersonLinkRelationshipType)
                            ? 'Search for someone assigned in this org and school period.'
                            : 'Search for someone in this organization, then choose the enrollment seat to link.'}
                      </span>
                    </div>

                    {!addSearchOnly && (
                      <div className="profile-sheet-toggle profile-pl-flow-toggle">
                        <button
                          type="button"
                          className={`profile-sheet-toggle-btn${addPersonLinkMode === 'search' ? ' is-active' : ''}`}
                          onClick={() => setAddPersonLinkMode('search')}
                        >
                          Search existing
                        </button>
                        <button
                          type="button"
                          className={`profile-sheet-toggle-btn${addPersonLinkMode === 'create' ? ' is-active' : ''}`}
                          onClick={() => setAddPersonLinkMode('create')}
                        >
                          Create new
                        </button>
                      </div>
                    )}

                    <div className="profile-pl-flow-target-body">
                      {(addSearchOnly || addPersonLinkMode === 'search') ? (
                        <>
                          <div className="profile-sheet-search-row">
                            <input
                              type="text"
                              className="profile-sheet-input"
                              value={addPersonLinkUserSearch}
                              onChange={(e) => setAddPersonLinkUserSearch(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                              placeholder="Search by name or email"
                            />
                            <button
                              type="button"
                              className="profile-sheet-search-submit"
                              onClick={searchUsers}
                              disabled={addPersonLinkSearching || !addPersonLinkUserSearch.trim()}
                              aria-label="Search users"
                            >
                              {addPersonLinkSearching ? '…' : 'Go'}
                            </button>
                          </div>
                          {addPersonLinkSearching && (
                            <p className="profile-sheet-hint">Searching…</p>
                          )}
                          {!addPersonLinkSearching && addPersonLinkUserResults.length > 0 && (
                            <div className="profile-sheet-results">
                              {addPersonLinkUserResults.map((u) => {
                                const seat = u.anchorUgp;
                                const seatLabel = seat
                                  ? [
                                      seat.group_name,
                                      seat.is_permanent ? 'Permanent' : (seat.period_title || seat.period_code)
                                    ].filter(Boolean).join(' · ')
                                  : null;
                                const selected = seat
                                  ? addPersonLinkSelectedUser?.resultKey === u.resultKey
                                  : addPersonLinkSelectedUser?.id === u.id && !addPersonLinkSelectedUser?.anchorUgp;
                                return (
                                <div
                                  key={u.resultKey || u.id}
                                  role="button"
                                  tabIndex={0}
                                  className={`profile-sheet-result-item${selected ? ' is-selected' : ''}`}
                                  onClick={() => setAddPersonLinkSelectedUser(u)}
                                  onKeyDown={(e) => e.key === 'Enter' && setAddPersonLinkSelectedUser(u)}
                                >
                                  {u.first_name} {u.last_name}
                                  {seatLabel ? <span>{seatLabel}</span> : u.email && <span>{u.email}</span>}
                                </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="profile-sheet-form-group">
                            <label className="profile-sheet-label" htmlFor="add-pl-first">First name</label>
                            <input
                              id="add-pl-first"
                              type="text"
                              className="profile-sheet-input"
                              value={addPersonLinkCreateFirstName}
                              onChange={(e) => setAddPersonLinkCreateFirstName(e.target.value)}
                              placeholder="First name"
                            />
                          </div>
                          <div className="profile-sheet-form-group">
                            <label className="profile-sheet-label" htmlFor="add-pl-last">Last name</label>
                            <input
                              id="add-pl-last"
                              type="text"
                              className="profile-sheet-input"
                              value={addPersonLinkCreateLastName}
                              onChange={(e) => setAddPersonLinkCreateLastName(e.target.value)}
                              placeholder="Last name"
                            />
                          </div>
                          <div className="profile-sheet-form-group">
                            <label className="profile-sheet-label" htmlFor="add-pl-email">
                              Email{requiredFields.includes('email') ? ' (required)' : ''}
                            </label>
                            <input
                              id="add-pl-email"
                              type="email"
                              className="profile-sheet-input"
                              value={addPersonLinkCreateEmail}
                              onChange={(e) => setAddPersonLinkCreateEmail(e.target.value)}
                              placeholder="Email address"
                            />
                          </div>
                          <div className="profile-sheet-form-group">
                            <label className="profile-sheet-label" htmlFor="add-pl-phone">
                              Phone{requiredFields.includes('phone') ? ' (required)' : ''}
                            </label>
                            <input
                              id="add-pl-phone"
                              type="tel"
                              className="profile-sheet-input"
                              value={addPersonLinkCreatePhone}
                              onChange={(e) => setAddPersonLinkCreatePhone(e.target.value)}
                              placeholder="Phone number"
                            />
                          </div>
                        </>
                      )}

                      {(isGuardianLink || isSiblingLink) &&
                        (addPersonLinkSelectedUser || addPersonLinkMode === 'create') && (
                          <div className="profile-sheet-inset">
                            {isGuardianLink ? (
                              <>
                            <div className="profile-sheet-form-group">
                              <label className="profile-sheet-label" htmlFor="add-pl-guardian-type">Guardian type</label>
                              <select
                                id="add-pl-guardian-type"
                                className="profile-sheet-select"
                                value={addPersonLinkGuardianType}
                                onChange={(e) => setAddPersonLinkGuardianType(e.target.value)}
                              >
                                {GUARDIAN_SUBTYPE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                            <label
                              className={`profile-sheet-check${hasPrimaryGuardian ? ' is-disabled' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={addPersonLinkPrimary}
                                onChange={(e) =>
                                  !hasPrimaryGuardian && setAddPersonLinkPrimary(e.target.checked)
                                }
                                disabled={hasPrimaryGuardian}
                              />
                              Primary guardian {hasPrimaryGuardian && '(one already set)'}
                            </label>
                              </>
                            ) : (
                              <div className="profile-sheet-form-group">
                                <label className="profile-sheet-label" htmlFor="add-pl-sibling-type">Sibling type</label>
                                <select
                                  id="add-pl-sibling-type"
                                  className="profile-sheet-select"
                                  value={addPersonLinkSiblingType}
                                  onChange={(e) => setAddPersonLinkSiblingType(e.target.value)}
                                >
                                  {SIBLING_SUBTYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <label className="profile-sheet-check">
                              <input
                                type="checkbox"
                                checked={addPersonLinkEmergency}
                                onChange={(e) => setAddPersonLinkEmergency(e.target.checked)}
                              />
                              Emergency contact
                            </label>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
              {showPersonLinkActions && (
                <div className="profile-sheet-actions">
                  <button type="button" className="profile-sheet-btn profile-sheet-btn--secondary" onClick={resetModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="profile-sheet-btn profile-sheet-btn--primary"
                    disabled={!canSubmit || savingPersonLink || !canSubmitLink}
                    onClick={handleAdd}
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {showCkModal && (() => {
        const ckApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.CREDENTIALS_AND_KEYS);
        const control = String(ckApp?.metadata?.credential_control || 'both');
        const locked = Array.isArray(ckApp?.metadata?.locked_platforms) ? ckApp.metadata.locked_platforms : [];
        const entryIsKey = String(ckForm.entry_type || 'credentials').toLowerCase() === 'key';
        const closeCkModal = () => {
          if (!savingCk) setShowCkModal(false);
        };
        return (
          <div className="profile-sheet-overlay" role="presentation" onClick={closeCkModal}>
            <div
              className="profile-sheet-panel profile-sheet-panel--form"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ck-sheet-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="profile-sheet-header">
                <div className="profile-sheet-header-main">
                  <h2 id="ck-sheet-title" className="profile-sheet-title">
                    {ckEditId ? 'Edit entry' : 'Add entry'}
                  </h2>
                  <p className="profile-sheet-subtext">
                    {entryIsKey
                      ? 'Platform and key value. Title distinguishes multiple entries.'
                      : 'Platform plus email, username, or password.'}
                  </p>
                </div>
                <button type="button" className="profile-sheet-close" onClick={closeCkModal} aria-label="Close">
                  ×
                </button>
              </div>
              <div className="profile-sheet-form-body">
                <div className="profile-sheet-form-group">
                  <label className="profile-sheet-label" htmlFor="ck-entry-type">Entry type</label>
                  <select
                    id="ck-entry-type"
                    className="profile-sheet-select"
                    value={entryIsKey ? 'key' : 'credentials'}
                    onChange={(e) => setCkForm((prev) => ({ ...prev, entry_type: e.target.value }))}
                  >
                    <option value="credentials">Credentials</option>
                    <option value="key">Key</option>
                  </select>
                </div>
                {control === 'freeform_only' && (
                  <div className="profile-sheet-form-group">
                    <label className="profile-sheet-label" htmlFor="ck-platform-custom">Platform</label>
                    <input
                      id="ck-platform-custom"
                      type="text"
                      className="profile-sheet-input"
                      value={ckForm.platform_custom}
                      onChange={(e) => setCkForm((prev) => ({ ...prev, platform_custom: e.target.value }))}
                      placeholder="e.g. Google Classroom"
                    />
                  </div>
                )}
                {control === 'locked_only' && (
                  <div className="profile-sheet-form-group">
                    <label className="profile-sheet-label" htmlFor="ck-platform-pick">Platform</label>
                    <select
                      id="ck-platform-pick"
                      className="profile-sheet-select"
                      value={ckForm.platform_pick}
                      onChange={(e) => setCkForm((prev) => ({ ...prev, platform_pick: e.target.value }))}
                    >
                      <option value="">— Select —</option>
                      {locked.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {control === 'both' && (
                  <div className="profile-sheet-form-group">
                    <label className="profile-sheet-label" htmlFor="ck-platform-both">Platform</label>
                    <select
                      id="ck-platform-both"
                      className="profile-sheet-select"
                      value={ckForm.platform_pick}
                      onChange={(e) => setCkForm((prev) => ({ ...prev, platform_pick: e.target.value }))}
                    >
                      <option value="">— Select —</option>
                      {locked.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                      <option value="__other__">Other (custom)</option>
                    </select>
                    {ckForm.platform_pick === '__other__' && (
                      <div className="profile-sheet-form-group" style={{ marginTop: 10, marginBottom: 0 }}>
                        <label className="profile-sheet-label" htmlFor="ck-platform-other">Custom platform</label>
                        <input
                          id="ck-platform-other"
                          type="text"
                          className="profile-sheet-input"
                          value={ckForm.platform_custom}
                          onChange={(e) =>
                            setCkForm((prev) => ({ ...prev, platform_custom: e.target.value }))
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className="profile-sheet-form-group">
                  <label className="profile-sheet-label" htmlFor="ck-title">Title</label>
                  <input
                    id="ck-title"
                    type="text"
                    className="profile-sheet-input"
                    value={ckForm.title}
                    onChange={(e) => setCkForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Library, Class A, Production API"
                  />
                </div>
                {entryIsKey ? (
                  <>
                    <div className="profile-sheet-form-group">
                      <label className="profile-sheet-label" htmlFor="ck-value">Value</label>
                      <input
                        id="ck-value"
                        type="text"
                        className="profile-sheet-input"
                        value={ckForm.password}
                        onChange={(e) => setCkForm((prev) => ({ ...prev, password: e.target.value }))}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </div>
                    <div className="profile-sheet-form-group">
                      <label className="profile-sheet-label" htmlFor="ck-notes-key">Notes</label>
                      <textarea
                        id="ck-notes-key"
                        className="profile-sheet-textarea"
                        value={ckForm.notes}
                        onChange={(e) => setCkForm((prev) => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="profile-sheet-form-group">
                      <label className="profile-sheet-label" htmlFor="ck-email">Email</label>
                      <input
                        id="ck-email"
                        type="email"
                        className="profile-sheet-input"
                        value={ckForm.email}
                        onChange={(e) => setCkForm((prev) => ({ ...prev, email: e.target.value }))}
                      />
                    </div>
                    <div className="profile-sheet-form-group">
                      <label className="profile-sheet-label" htmlFor="ck-username">Username</label>
                      <input
                        id="ck-username"
                        type="text"
                        className="profile-sheet-input"
                        value={ckForm.username}
                        onChange={(e) => setCkForm((prev) => ({ ...prev, username: e.target.value }))}
                      />
                    </div>
                    <div className="profile-sheet-form-group">
                      <label className="profile-sheet-label" htmlFor="ck-password">Password</label>
                      <input
                        id="ck-password"
                        type="text"
                        className="profile-sheet-input"
                        value={ckForm.password}
                        onChange={(e) => setCkForm((prev) => ({ ...prev, password: e.target.value }))}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </div>
                    <div className="profile-sheet-form-group">
                      <label className="profile-sheet-label" htmlFor="ck-notes">Notes</label>
                      <textarea
                        id="ck-notes"
                        className="profile-sheet-textarea"
                        value={ckForm.notes}
                        onChange={(e) => setCkForm((prev) => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="profile-sheet-actions">
                <button
                  type="button"
                  className="profile-sheet-btn profile-sheet-btn--secondary"
                  disabled={savingCk}
                  onClick={closeCkModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="profile-sheet-btn profile-sheet-btn--primary"
                  disabled={savingCk}
                  onClick={handleSaveCredentialsKeysEntry}
                >
                  {savingCk ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {showDocModal && (() => {
        const attachmentsApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.ATTACHMENTS);
        if (!attachmentsApp) return null;
        const allowUpload = attachmentsApp?.metadata?.allow_upload !== false;
        const categoryOptions = getDocumentCategoryOptions(attachmentsApp);
        return (
          <div
            style={styles.modalOverlay}
            onClick={() => !savingDoc && setShowDocModal(false)}
          >
            <div style={{ ...styles.modalContent, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>
                  {docEditId ? 'Edit attachment' : 'Add attachment'}
                </h3>
                <button
                  type="button"
                  onClick={() => !savingDoc && setShowDocModal(false)}
                  style={styles.modalCloseButton}
                  disabled={savingDoc}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div style={styles.modalBody}>
                {categoryOptions.length > 0 && (
                  <>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                      Category
                    </label>
                    <select
                      value={docForm.category}
                      onChange={(e) => setDocForm((prev) => ({ ...prev, category: e.target.value }))}
                      disabled={savingDoc}
                      className="profile-doc-field"
                    >
                      {categoryOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                  Title
                </label>
                <input
                  type="text"
                  value={docForm.title}
                  onChange={(e) => setDocForm((prev) => ({ ...prev, title: e.target.value }))}
                  disabled={savingDoc}
                  className="profile-doc-field"
                />
                {allowUpload ? (
                  <div className="profile-doc-source">
                    <div className="profile-doc-source-tabs" role="tablist" aria-label="Attachment source">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={docSourceTab === 'file'}
                        className={`profile-doc-source-tab${docSourceTab === 'file' ? ' profile-doc-source-tab--active' : ''}`}
                        onClick={() => setDocSourceTab('file')}
                        disabled={savingDoc}
                      >
                        Upload file
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={docSourceTab === 'url'}
                        className={`profile-doc-source-tab${docSourceTab === 'url' ? ' profile-doc-source-tab--active' : ''}`}
                        onClick={() => setDocSourceTab('url')}
                        disabled={savingDoc}
                      >
                        Link URL
                      </button>
                    </div>
                    <div className="profile-doc-source-panel">
                      {docSourceTab === 'file' ? (
                        <>
                          <button
                            type="button"
                            className="profile-doc-file-drop"
                            onClick={() => !savingDoc && docFileInputRef.current?.click()}
                            disabled={savingDoc}
                          >
                            <span className="profile-doc-file-drop-icon" aria-hidden>+</span>
                            <span className="profile-doc-file-drop-label">
                              {docForm.selectedFile
                                ? docForm.selectedFile.name
                                : docForm.existingFileName && docEditId
                                  ? docForm.existingFileName
                                  : 'Choose file'}
                            </span>
                            <span className="profile-doc-file-drop-hint">
                              {docForm.selectedFile || (docForm.existingFileName && docEditId)
                                ? 'Click to replace'
                                : 'Click or drop a file here'}
                            </span>
                          </button>
                          <input
                            ref={docFileInputRef}
                            type="file"
                            className="profile-doc-file-input"
                            disabled={savingDoc}
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (!file) return;
                              setDocForm((prev) => ({
                                ...prev,
                                selectedFile: file,
                                existingFileName: null
                              }));
                            }}
                          />
                        </>
                      ) : (
                        <input
                          type="url"
                          value={docForm.url}
                          onChange={(e) => setDocForm((prev) => ({ ...prev, url: e.target.value }))}
                          disabled={savingDoc}
                          placeholder="https://…"
                          className="profile-doc-field profile-doc-field--flush"
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                      Link URL
                    </label>
                    <input
                      type="url"
                      value={docForm.url}
                      onChange={(e) => setDocForm((prev) => ({ ...prev, url: e.target.value }))}
                      disabled={savingDoc}
                      placeholder="https://…"
                      className="profile-doc-field"
                    />
                  </>
                )}
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 6, marginTop: 12 }}>
                  Description <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={docForm.description}
                  onChange={(e) => setDocForm((prev) => ({ ...prev, description: e.target.value }))}
                  disabled={savingDoc}
                  className="profile-doc-field"
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <button
                    type="button"
                    className="profile-section-cancel"
                    disabled={savingDoc}
                    onClick={() => setShowDocModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="profile-section-save"
                    disabled={savingDoc}
                    onClick={() => handleSaveDocument(attachmentsApp)}
                  >
                    {savingDoc ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {docDeleteConfirm && (
        <AlertModal
          isOpen={true}
          onClose={() => setDocDeleteConfirm(null)}
          presentation="prompt"
          overlayOpacity={0.16}
          overlayColor="15, 23, 42"
          variant="warning"
          subject="Remove attachment?"
          body={`Remove "${docDeleteConfirm.title}"?`}
          showCloseButton={false}
          allowOverlayClose={true}
          actions={[
            { label: 'Cancel', onClick: () => setDocDeleteConfirm(null), variant: 'secondary' },
            {
              label: 'Remove',
              onClick: async () => {
                try {
                  const res = await fetchWithAuth(`/api/documents/${docDeleteConfirm.id}`, {
                    method: 'DELETE'
                  });
                  const data = await res.json();
                  if (data.success) await reloadDocuments();
                  else setErrorModal({ title: 'Error', message: data.message || 'Failed to remove' });
                } catch {
                  setErrorModal({ title: 'Error', message: 'Failed to remove attachment' });
                }
                setDocDeleteConfirm(null);
              },
              variant: 'primary',
              style: { background: '#dc2626' }
            }
          ]}
          zIndex={2000}
        />
      )}
      <PaymentChargeEditor
        open={!!paymentEditModal}
        onClose={() => !savingPayment && setPaymentEditModal(null)}
        saving={savingPayment}
        title={paymentEditModal?.item?.label || formatMonthKeyLabel(paymentEditModal?.item?.month_key)}
        charge={paymentEditModal?.item || {}}
        canQuickSet={!!paymentEditModal?.item?.requirement_id}
        onQuickSet={handleQuickSetPayment}
        onAddTransaction={handleAddTransaction}
        onDeleteTransaction={handleDeleteTransaction}
      />
      {ckDeleteConfirm && (
        <AlertModal
          isOpen={true}
          onClose={() => setCkDeleteConfirm(null)}
          presentation="prompt"
          overlayOpacity={0.16}
          overlayColor="15, 23, 42"
          variant="warning"
          subject="Delete entry?"
          body={`Remove "${ckDeleteConfirm.label}"?`}
          showCloseButton={false}
          allowOverlayClose={true}
          actions={[
            { label: 'Cancel', onClick: () => setCkDeleteConfirm(null), variant: 'secondary' },
            {
              label: 'Delete',
              onClick: async () => {
                try {
                  const res = await fetchWithAuth(`/api/credentials-keys/${ckDeleteConfirm.id}`, { method: 'DELETE' });
                  const data = await res.json();
                  if (data.success) await reloadCredentialsKeys();
                  else setErrorModal({ title: 'Error', message: data.message || 'Delete failed' });
                } catch {
                  setErrorModal({ title: 'Error', message: 'Delete failed' });
                }
                setCkDeleteConfirm(null);
              },
              variant: 'primary',
              style: { background: '#dc2626' }
            }
          ]}
          zIndex={2000}
        />
      )}
      {showPreferredNameModal && (
        <div
          style={styles.modalOverlay}
          onClick={() => !savingPreferredName && setShowPreferredNameModal(false)}
        >
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Update preferred / display name</h3>
              <button
                type="button"
                onClick={() => !savingPreferredName && setShowPreferredNameModal(false)}
                style={styles.modalCloseButton}
                disabled={savingPreferredName}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ margin: '0 0 12px', fontSize: '0.8125rem', color: '#64748b', lineHeight: 1.45 }}>
                Shown at the top of this profile. Leave blank to use first name (
                {getUserDefaultDisplayName(user) || 'not set'}).
              </p>
              <label htmlFor="profile-preferred-name" style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                Preferred / display name
              </label>
              <input
                id="profile-preferred-name"
                type="text"
                value={preferredNameDraft}
                onChange={(e) => setPreferredNameDraft(e.target.value)}
                disabled={savingPreferredName}
                autoFocus
                placeholder={getUserDefaultDisplayName(user) || 'Display name'}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 10px',
                  fontSize: '0.875rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontFamily: 'inherit'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSavePreferredName();
                  }
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button
                  type="button"
                  className="profile-section-cancel"
                  disabled={savingPreferredName}
                  onClick={() => setShowPreferredNameModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="profile-section-save"
                  disabled={savingPreferredName}
                  onClick={handleSavePreferredName}
                >
                  {savingPreferredName ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {errorModal && (
        <ErrorModal
          isOpen={true}
          onClose={() => setErrorModal(null)}
          title={errorModal.title}
          message={errorModal.message}
          zIndex={2000}
        />
      )}
      {basicInfoSaveConfirm === 'disable_platform_access' && (
        <AlertModal
          isOpen={true}
          onClose={() => setBasicInfoSaveConfirm(null)}
          presentation="prompt"
          overlayOpacity={0.16}
          overlayColor="15, 23, 42"
          variant="warning"
          subject="Disable sign-in?"
          body="This person will not be able to sign in to any product. AcuitySIS Platform Access is unchanged."
          showCloseButton={false}
          allowOverlayClose={true}
          actions={[
            {
              label: 'Cancel',
              onClick: () => setBasicInfoSaveConfirm(null),
              variant: 'secondary'
            },
            {
              label: 'Continue',
              onClick: async () => {
                setBasicInfoSaveConfirm(null);
                const passwordChange =
                  editFormData.platformAccess &&
                  !!(editFormData.password && String(editFormData.password).trim());
                const isEnablingPlatformAccess =
                  originalFormData.platformAccess === false && editFormData.platformAccess === true;
                if (passwordChange && !isEnablingPlatformAccess) {
                  setBasicInfoSaveConfirm('password_change');
                  return;
                }
                await executeSaveBasicInfo();
              },
              variant: 'primary'
            }
          ]}
          zIndex={2000}
        />
      )}
      {basicInfoSaveConfirm === 'password_change' && (
        <AlertModal
          isOpen={true}
          onClose={() => setBasicInfoSaveConfirm(null)}
          presentation="prompt"
          overlayOpacity={0.16}
          overlayColor="15, 23, 42"
          variant="warning"
          subject="Change password?"
          body="Are you sure you want to change this user's password?"
          showCloseButton={false}
          allowOverlayClose={true}
          actions={[
            {
              label: 'Cancel',
              onClick: () => setBasicInfoSaveConfirm(null),
              variant: 'secondary'
            },
            {
              label: 'Change password',
              onClick: async () => {
                setBasicInfoSaveConfirm(null);
                await executeSaveBasicInfo();
              },
              variant: 'primary'
            }
          ]}
          zIndex={2000}
        />
      )}
      {exitDateConfirm && (
        <AlertModal
          isOpen={true}
          onClose={() => setExitDateConfirm(null)}
          presentation="prompt"
          overlayOpacity={0.16}
          overlayColor="15, 23, 42"
          variant="warning"
          subject="Set exit date to today?"
          body={`Status is now ${exitDateConfirm.newStatus}.`}
          showCloseButton={false}
          allowOverlayClose={true}
          actions={[
            {
              label: 'Status only',
              onClick: () => {
                setStudentInfoDraft(prev => ({ ...prev, status: exitDateConfirm.newStatus }));
                setExitDateConfirm(null);
              },
              variant: 'secondary'
            },
            {
              label: 'Set exit date',
              onClick: () => {
                const today = new Date().toISOString().slice(0, 10);
                setStudentInfoDraft(prev => ({
                  ...prev,
                  status: exitDateConfirm.newStatus,
                  exit_date: today
                }));
                setExitDateConfirm(null);
              },
              variant: 'primary'
            }
          ]}
          zIndex={2000}
        />
      )}

      {assocRemoveConfirm && (
        <AlertModal
          isOpen={true}
          onClose={() => setAssocRemoveConfirm(null)}
          presentation="prompt"
          overlayOpacity={0.16}
          overlayColor="15, 23, 42"
          variant="warning"
          subject="Remove association?"
          body={`Remove association to ${assocRemoveConfirm.label || 'this group-period'}?`}
          showCloseButton={false}
          allowOverlayClose={true}
          actions={[
            { label: 'Cancel', onClick: () => setAssocRemoveConfirm(null), variant: 'secondary' },
            {
              label: 'Remove',
              onClick: async () => {
                const currentGp = availableGroupsPeriods.find(
                  (gp) => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId)
                );
                try {
                  const params = new URLSearchParams({
                    user_groups_periods_id: String(currentGp?.id || '')
                  });
                  const res = await fetchWithAuth(
                    `/api/associations/${assocRemoveConfirm.id}?${params.toString()}`,
                    { method: 'DELETE' }
                  );
                  const data = await res.json();
                  if (data.success && currentGp?.id) {
                    const r = await fetch(buildAssociationsApiUrl(currentGp.id, { includeEnded: true }));
                    const d = await r.json();
                    if (d.success) setAssociations(d.data || []);
                  } else {
                    setErrorModal({ title: 'Error', message: data.message || 'Failed to remove association' });
                  }
                } catch {
                  setErrorModal({ title: 'Error', message: 'Failed to remove association' });
                }
                setAssocRemoveConfirm(null);
              },
              variant: 'primary',
              style: { background: '#dc2626' }
            }
          ]}
          zIndex={2000}
        />
      )}

      {removeConfirmModal && (
        <AlertModal
          isOpen={true}
          onClose={() => setRemoveConfirmModal(null)}
          presentation="prompt"
          overlayOpacity={0.16}
          overlayColor="15, 23, 42"
          variant="warning"
          subject="Remove guardian?"
          body={`Remove ${removeConfirmModal.personName} from this profile?`}
          showCloseButton={false}
          allowOverlayClose={true}
          actions={[
            { label: 'Cancel', onClick: () => setRemoveConfirmModal(null), variant: 'secondary' },
            { label: 'Remove', onClick: async () => {
              await (async (relId) => {
                try {
                  const res = await fetchWithAuth(`/api/relationships/${relId}`, { method: 'DELETE' });
                  const data = await res.json();
                  if (data.success) {
                    const currentGp = availableGroupsPeriods.find(gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId));
                    if (currentGp?.id) {
                      const r = await fetch(
                        buildPersonLinksApiUrl({
                          userGroupsPeriodsId: currentGp.id,
                          userId: user?.id
                        })
                      );
                      const d = await r.json();
                      if (d.success) setPersonLinks(d.data || []);
                    }
                  } else setErrorModal({ title: 'Error', message: data.message || 'Failed to remove link' });
                } catch (e) {
                  setErrorModal({ title: 'Error', message: 'Failed to remove link' });
                }
              })(removeConfirmModal.relId);
              setRemoveConfirmModal(null);
            }, variant: 'primary', style: { background: '#dc2626' } }
          ]}
          zIndex={2000}
        />
      )}
      {showEditPersonLinkModal && editPersonLinkItem && (() => {
        const personLinksApp = (assignedApps || []).find(a => a.slug === APP_SLUGS.PERSON_LINKS);
        const editRelType = String(editPersonLinkItem.relationship_type || '').toLowerCase();
        const isEditingGuardian = editRelType === 'guardian';
        const isEditingSibling = editRelType === 'sibling';
        const isEditingCounselor = editRelType === 'counselor';
        const isEditingTor = editRelType === 'teacher_of_record';
        const counselorVisibleProps = getCounselorVisibleProperties(personLinksApp?.metadata);
        const counselorMeetingLinkMissing =
          isEditingCounselor &&
          counselorVisibleProps.includes('meeting_link') &&
          !personLinkHasResolvedProperty(editPersonLinkItem, 'meeting_link');
        const editModalTitle = isEditingGuardian
          ? 'Edit Guardian'
          : isEditingSibling
            ? 'Edit Sibling'
          : isEditingTor
            ? 'Teacher of Record'
          : isEditingCounselor
            ? 'Counselor'
            : 'Supervisor';
        const existingGuardians = (personLinks || []).filter(pl => (pl.relationship_type || '').toLowerCase() === 'guardian' && pl.id !== editPersonLinkItem.id);
        const hasPrimaryGuardian = existingGuardians.some(pl => pl.metadata?.primary === true);
        const openLinkedProfileNewTab = () => {
          if (!editPersonLinkItem.linked_user_id) return;
          const url = buildUserProfileUrl({
            userId: editPersonLinkItem.linked_user_id,
            userGroupsPeriodsId: editPersonLinkItem.child_ugp_id,
            from: `${location.pathname}${location.search}`
          });
          window.open(url, '_blank', 'noopener,noreferrer');
        };
        const handleSave = async () => {
          if (!isEditingGuardian && !isEditingSibling) return;
          setSavingPersonLink(true);
          try {
            const metadata = {
              ...(editPersonLinkItem.metadata || {}),
              ...(isEditingGuardian
                ? {
                    guardian_type: editPersonLinkGuardianType,
                    primary: editPersonLinkPrimary,
                    emergency: editPersonLinkEmergency
                  }
                : {
                    sibling_type: editPersonLinkSiblingType,
                    emergency: editPersonLinkEmergency
                  })
            };
            const relRes = await fetchWithAuth(`/api/relationships/${editPersonLinkItem.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ metadata })
            });
            const relData = await relRes.json();
            if (relData.success) {
              const currentGp = availableGroupsPeriods.find(gp => String(gp.groups_periods_id) === String(selectedGroupsPeriodsId));
              if (currentGp?.id) {
                const r = await fetch(
                  buildPersonLinksApiUrl({
                    userGroupsPeriodsId: currentGp.id,
                    userId: user?.id
                  })
                );
                const d = await r.json();
                if (d.success) setPersonLinks(d.data || []);
              }
              setShowEditPersonLinkModal(false);
              setEditPersonLinkItem(null);
            } else {
              setErrorModal({ title: 'Error', message: relData.message || 'Failed to save changes' });
            }
          } catch (e) {
            setErrorModal({ title: 'Error', message: 'Failed to save changes' });
          } finally {
            setSavingPersonLink(false);
          }
        };
        const closeEditPersonLink = () => {
          if (!savingPersonLink) {
            setShowEditPersonLinkModal(false);
            setEditPersonLinkItem(null);
          }
        };
        return (
          <div className="profile-sheet-overlay profile-sheet-overlay--raised">
            <div
              className="profile-sheet-panel profile-sheet-panel--form"
              role="dialog"
              aria-modal="true"
              aria-labelledby="edit-person-link-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="profile-sheet-header">
                <div className="profile-sheet-header-main">
                  <div className="profile-sheet-title-row">
                    <h2 id="edit-person-link-title" className="profile-sheet-title">
                      {editModalTitle}
                    </h2>
                    {editPersonLinkItem.linked_user_id && (
                      <button
                        type="button"
                        className="profile-sheet-title-open-tab"
                        onClick={openLinkedProfileNewTab}
                        aria-label="Open profile in new tab"
                        title="Open profile in new tab"
                      >
                        <PersonLinkOpenTabIcon />
                      </button>
                    )}
                  </div>
                  <p className="profile-sheet-subtext">
                    {isEditingGuardian
                      ? 'Update how this person is linked on this profile. Contact details are edited on their own profile.'
                      : isEditingSibling
                        ? 'Update the sibling type for this link. Contact details are edited on their own profile.'
                      : isEditingTor
                        ? 'Linked teacher of record for this school period.'
                      : isEditingCounselor
                        ? counselorMeetingLinkMissing
                          ? 'Add the counselor’s appointment link on their profile for this school period.'
                          : 'Linked counselor for this school period.'
                        : 'Linked supervisor for this school period.'}
                  </p>
                </div>
                <button type="button" className="profile-sheet-close" onClick={closeEditPersonLink} aria-label="Close">
                  ×
                </button>
              </div>
              <div className="profile-sheet-form-body">
                {isEditingGuardian && (
                  <>
                    <div className="profile-sheet-form-group">
                      <label className="profile-sheet-label" htmlFor="edit-pl-guardian-type">Guardian type</label>
                      <select
                        id="edit-pl-guardian-type"
                        className="profile-sheet-select"
                        value={editPersonLinkGuardianType}
                        onChange={(e) => setEditPersonLinkGuardianType(e.target.value)}
                      >
                        {GUARDIAN_SUBTYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="profile-sheet-form-group">
                      <label
                        className={`profile-sheet-check${hasPrimaryGuardian ? ' is-disabled' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={editPersonLinkPrimary}
                          onChange={(e) =>
                            !hasPrimaryGuardian && setEditPersonLinkPrimary(e.target.checked)
                          }
                          disabled={hasPrimaryGuardian}
                        />
                        Primary guardian {hasPrimaryGuardian && '(one already set)'}
                      </label>
                    </div>
                  </>
                )}
                {isEditingSibling && (
                  <div className="profile-sheet-form-group">
                    <label className="profile-sheet-label" htmlFor="edit-pl-sibling-type">Sibling type</label>
                    <select
                      id="edit-pl-sibling-type"
                      className="profile-sheet-select"
                      value={editPersonLinkSiblingType}
                      onChange={(e) => setEditPersonLinkSiblingType(e.target.value)}
                    >
                      {SIBLING_SUBTYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}
                {(isEditingGuardian || isEditingSibling) && (
                  <div className="profile-sheet-form-group">
                    <label className="profile-sheet-check">
                      <input
                        type="checkbox"
                        checked={editPersonLinkEmergency}
                        onChange={(e) => setEditPersonLinkEmergency(e.target.checked)}
                      />
                      Emergency contact
                    </label>
                  </div>
                )}
                {!isEditingGuardian && !isEditingSibling && (
                  <div className="profile-sheet-readonly-block">
                    <div className="profile-sheet-readonly-name">
                      {editPersonLinkItem.linked_user_name || 'Unknown'}
                    </div>
                    <div className="profile-sheet-readonly-row">
                      <span className="profile-sheet-label">Email</span>
                      <span className={editPersonLinkItem.linked_user_email ? '' : 'profile-sheet-readonly-empty'}>
                        {editPersonLinkItem.linked_user_email || 'No email'}
                      </span>
                    </div>
                    <div className="profile-sheet-readonly-row">
                      <span className="profile-sheet-label">Phone</span>
                      <span className={editPersonLinkItem.linked_user_phone ? '' : 'profile-sheet-readonly-empty'}>
                        {editPersonLinkItem.linked_user_phone || 'No phone'}
                      </span>
                    </div>
                    {isEditingCounselor &&
                      counselorVisibleProps.map((propKey) => {
                        const propLabel = getPropertyLabel(propKey, personLinksApp?.metadata);
                        const url = personLinkResolvedProperty(editPersonLinkItem, propKey);
                        return (
                          <div key={propKey} className="profile-sheet-readonly-row">
                            <span className="profile-sheet-label">{propLabel}</span>
                            {url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="profile-sheet-readonly-link"
                              >
                                {propLabel}
                              </a>
                            ) : (
                              <span className="profile-pl-missing-meeting-link">
                                Missing {propLabel.toLowerCase()}
                              </span>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
                {counselorMeetingLinkMissing && (
                  <div className="profile-sheet-callout profile-sheet-callout--warning">
                    <p className="profile-sheet-callout-text">
                      Meeting link is missing. Open their profile (icon next to the title) and add it under
                      Profile Properties for an audience that includes this student.
                    </p>
                  </div>
                )}
              </div>
              <div className="profile-sheet-actions">
                {isEditingGuardian || isEditingSibling ? (
                  <>
                    <button
                      type="button"
                      className="profile-sheet-btn profile-sheet-btn--secondary"
                      onClick={closeEditPersonLink}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="profile-sheet-btn profile-sheet-btn--primary"
                      disabled={savingPersonLink}
                      onClick={handleSave}
                    >
                      Save
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="profile-sheet-btn profile-sheet-btn--primary"
                    onClick={closeEditPersonLink}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {savingPersonLink && <LoadingOverlay isVisible={true} message={showEditPersonLinkModal ? 'Saving...' : 'Adding...'} />}
    </div>
  );
};

const styles = {
  container: {
    height: 'calc(100vh - 80px)',
    background: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },
  topControlsBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    background: 'white',
    borderBottom: '1px solid #e5e7eb',
    flexShrink: 0,
    zIndex: 100,
  },
  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  modalContent: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '80vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 24px',
    borderBottom: '1px solid #f1f5f9',
    background: '#fafbfc',
  },
  modalTitle: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#334155',
    letterSpacing: '-0.01em',
  },
  modalCloseButton: {
    background: 'transparent',
    border: 'none',
    fontSize: '1.25rem',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '4px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    transition: 'color 0.15s ease, background 0.15s ease',
    lineHeight: 1,
  },
  modalBody: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  },
  modalContextInfo: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  modalContextIcon: {
    fontSize: '1.5rem',
    flexShrink: 0,
  },
  modalContextText: {
    flex: 1,
  },
  modalContextTitle: {
    margin: '0 0 8px 0',
    fontSize: '0.9375rem',
    fontWeight: '600',
    color: '#1e40af',
  },
  modalContextDescription: {
    margin: 0,
    fontSize: '0.875rem',
    color: '#1e3a8a',
    lineHeight: '1.5',
  },
  modalOptionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  modalOptionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  modalOptionButtonHover: {
    borderColor: '#667eea',
    background: '#f5f7fa',
    transform: 'translateX(4px)',
  },
  modalOptionButtonSelected: {
    borderColor: '#667eea',
    background: 'linear-gradient(135deg, #eff6ff 0%, #f0e7ff 100%)',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)',
  },
  modalOptionIcon: {
    fontSize: '1.25rem',
    color: '#667eea',
    fontWeight: 'bold',
    flexShrink: 0,
    width: '24px',
    textAlign: 'center',
  },
  modalOptionContent: {
    flex: 1,
  },
  modalOptionTitle: {
    fontSize: '0.9375rem',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '4px',
  },
  modalOptionSubtitle: {
    fontSize: '0.8125rem',
    color: '#6b7280',
  },
  columnsLayout: {
    display: 'flex',
    width: '100%',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    borderRight: '1px solid #e5e7eb',
    overflow: 'hidden',
    minHeight: 0,
  },
  columnsDynamicWrap: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
  columnHeader: {
    padding: '20px',
    borderBottom: '2px solid #e5e7eb',
    background: '#f9fafb',
    flexShrink: 0,
  },
  columnHeaderPrimary: {
    padding: '16px 20px 8px',
    borderBottom: 'none',
    background: '#ffffff',
    flexShrink: 0,
  },
  columnTitle: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#111827',
    margin: 0,
  },
  columnTitlePrimary: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#334155',
    margin: 0,
    letterSpacing: '-0.01em',
  },
  columnContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '20px',
    WebkitOverflowScrolling: 'touch',
  },
  resizeHandle: {
    width: '8px',
    background: '#f3f4f6',
    cursor: 'col-resize',
    flexShrink: 0,
    borderRight: '1px solid #e5e7eb',
    borderLeft: '1px solid #e5e7eb',
    transition: 'background 0.2s ease',
  },
  resizeHandleHover: {
    background: '#3b82f6',
  },
  expandableSection: {
    marginBottom: '10px',
    overflow: 'hidden',
    background: '#fff',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    background: '#fff',
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background 0.15s ease',
  },
  sectionHeaderHover: {
    background: '#f8f9fa',
  },
  sectionTitle: {},
  expandIcon: {},
  sectionContent: {
    padding: '12px',
    background: '#fff',
  },
  infoField: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '15px',
    paddingBottom: '4px',
    borderBottom: '1px solid #e5e7eb',
  },
  infoLabel: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#6b7280',
    marginBottom: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  infoValue: {
    fontSize: '0.95rem',
    color: '#111827',
    wordBreak: 'break-word',
  },
  input: {
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    fontSize: '0.95rem',
    boxSizing: 'border-box',
  },
  emptyText: {
    color: '#6b7280',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '20px',
  },
  formItem: {
    marginBottom: '12px',
    padding: '10px 12px',
    background: '#fafbfc',
    borderRadius: '4px',
    border: '1px solid #f1f5f9',
  },
  formItemClickable: {
    cursor: 'pointer',
    transition: 'background 0.2s ease, border-color 0.2s ease',
    ':hover': {
      background: '#f3f4f6',
      borderColor: '#d1d5db',
    }
  },
  formTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0',
  },
  formMeta: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: '4px 0',
  },
  formSource: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: '4px 0',
    fontStyle: 'italic',
  },
  formsSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  formsSummaryText: {
    margin: 0,
    color: '#6b7280',
  },
  openFormsButton: {
    padding: '6px 12px',
    fontSize: '0.85rem',
    background: '#f9fafb',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '6px',
    marginBottom: '8px',
  },
  badgeSource: {
    display: 'inline-block',
    padding: '2px 8px',
    fontSize: '0.7rem',
    fontWeight: '500',
    color: '#64748b',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  placeholderText: {
    color: '#9ca3af',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '40px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    padding: '40px',
  },
  errorTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#dc2626',
    marginBottom: '20px',
  },
  errorText: {
    fontSize: '1.1rem',
    color: '#6b7280',
  },
  sectionHeaderActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  editButton: {
    padding: '6px 12px',
    fontSize: '0.85rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  saveButton: {
    padding: '5px 10px',
    fontSize: '0.75rem',
    fontWeight: '500',
    background: 'var(--color-brand)',
    color: 'var(--color-brand-on)',
    border: '1px solid var(--color-brand)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  cancelEditButton: {
    padding: '5px 10px',
    fontSize: '0.75rem',
    fontWeight: '500',
    background: 'transparent',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  inputField: {
    width: '100%',
    padding: '0',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  inputFieldUnfocused: {
    background: 'transparent',
    border: 'none',
    padding: '2px 0',
    color: '#111827',
    minHeight: '20px',
  },
  inputFieldFocused: {
    background: 'white',
    border: '1px solid #3b82f6',
    borderRadius: '4px',
    padding: '6px 10px',
    color: '#111827',
  },
  avatarUploadSection: {
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'center',
  },
  avatarContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0',
  },
  avatarCircleClickable: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: '#e0f2fe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #bae6fd',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  avatarImgPreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarInitials: {
    color: '#0369a1',
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  avatarHint: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: 0,
    textAlign: 'center',
  },
  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
    paddingBottom: '0',
  },
  firstNameInlineContainer: {
    flex: 1,
  },
  nameStackContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldHint: {
    fontSize: '0.8rem',
    color: '#6b7280',
    fontStyle: 'italic',
    marginTop: '-12px',
    marginBottom: '15px',
    paddingLeft: '4px',
  },
  formRow: {
    marginBottom: 16,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    cursor: 'pointer',
  },
  passwordWarningMessage: {
    marginTop: '10px',
    padding: '8px 0',
    fontSize: '0.85rem',
    color: '#6b7280',
    fontStyle: 'italic',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'color 0.2s ease',
  },
};

// Define InfoFieldEdit component to prevent recreation on each render
const InfoFieldEdit = React.memo(
  ({
    label,
    name,
    value,
    onChange,
    type = 'text',
    required = false,
    placeholder = '',
    autoComplete,
    disabled = false,
    compact = false
  }) => {
    const [isFocused, setIsFocused] = useState(false);

    if (compact) {
      return (
        <div className="profile-field">
          <label className="profile-field-label" htmlFor={`profile-field-${name}`}>
            {label}
          </label>
          <input
            id={`profile-field-${name}`}
            type={type}
            name={name}
            className="profile-field-input"
            value={value || ''}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            autoComplete={autoComplete}
            disabled={disabled}
          />
        </div>
      );
    }

    return (
      <div style={styles.infoField}>
        <span style={styles.infoLabel}>{label}:</span>
        <input
          type={type}
          name={name}
          value={value || ''}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          required={required}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          style={{
            ...styles.inputField,
            ...(isFocused && !disabled ? styles.inputFieldFocused : styles.inputFieldUnfocused),
            ...(disabled ? { cursor: 'default', opacity: 0.8 } : {})
          }}
        />
      </div>
    );
  }
);
InfoFieldEdit.displayName = 'InfoFieldEdit';

// Person Links section: group by relationship type (only when present)
const GUARDIAN_TYPE_LABELS = GUARDIAN_SUBTYPE_LABELS;

/** Row click opens edit sheet when user has edit permission (all person link types). */
const personLinkRowOpensEdit = (pl, canEdit) => {
  if (!canEdit) return false;
  const type = String(pl?.relationship_type || '').toLowerCase();
  return PERSON_LINKS_RELATIONSHIP_TYPES.includes(type);
};

const PersonLinkOpenTabIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const PersonLinksSection = ({
  personLinks = [],
  counselorVisibleProperties = [],
  personLinksMetadata = {},
  profileReturnUrl = '',
  canRemove,
  canEdit,
  onEdit,
  onRemoveRequest
}) => {
  const openLinkedProfileNewTab = (pl) => {
    if (!pl?.linked_user_id) return;
    const url = buildUserProfileUrl({
      userId: pl.linked_user_id,
      userGroupsPeriodsId: pl.child_ugp_id,
      from: profileReturnUrl
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const byType = (personLinks || []).reduce((acc, pl) => {
    const t = (pl.relationship_type || 'guardian').toLowerCase();
    if (!acc[t]) acc[t] = [];
    acc[t].push(pl);
    return acc;
  }, {});
  const types = PERSON_LINK_DISPLAY_ORDER.filter(t => (byType[t] || []).length > 0);
  return (
    <div>
      {types.length === 0 ? (
        <p style={styles.emptyText}>No person links yet.</p>
      ) : (
        types.map(type => (
          <div key={type} style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
              {PERSON_LINK_SECTION_LABELS[type] || type}
            </div>
            {(byType[type] || []).map(pl => {
              const rowOpensEdit = personLinkRowOpensEdit(pl, canEdit);
              return (
              <div
                key={pl.id}
                role={rowOpensEdit ? 'button' : undefined}
                tabIndex={rowOpensEdit ? 0 : undefined}
                className={`profile-pl-row${rowOpensEdit ? '' : ' profile-pl-row--disabled'}`}
                onClick={() => rowOpensEdit && onEdit(pl)}
                onKeyDown={(e) => rowOpensEdit && e.key === 'Enter' && onEdit(pl)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    {type === 'guardian' && pl.metadata?.guardian_type && GUARDIAN_TYPE_LABELS[pl.metadata.guardian_type] && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#e0f2fe', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {GUARDIAN_TYPE_LABELS[pl.metadata.guardian_type]}
                      </span>
                    )}
                    {type === 'sibling' && pl.metadata?.sibling_type && SIBLING_SUBTYPE_LABELS[pl.metadata.sibling_type] && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#fce7f3', color: '#9d174d', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {SIBLING_SUBTYPE_LABELS[pl.metadata.sibling_type]}
                      </span>
                    )}
                    {type === 'teacher_of_record' && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#dcfce7', color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.03em' }}>TOR</span>
                    )}
                    {type === 'counselor' && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#fef3c7', color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Counselor</span>
                    )}
                    {type === 'supervisor' && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: '#ede9fe', color: '#5b21b6', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Supervisor</span>
                    )}
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' }}>{pl.linked_user_name || 'Unknown'}</span>
                    {pl.metadata?.primary && <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: '#d1fae5', color: '#059669' }}>Primary</span>}
                    {pl.metadata?.emergency && (type === 'guardian' || type === 'sibling') && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: '#ffedd5', color: '#c2410c' }}>Emergency</span>
                    )}
                    {(isPersonLinkSearchOnlyType(type)) && pl.linked_user_id && (
                      <button
                        type="button"
                        className="profile-sheet-title-open-tab profile-pl-open-tab"
                        onClick={(e) => {
                          e.stopPropagation();
                          openLinkedProfileNewTab(pl);
                        }}
                        aria-label={`Open ${pl.linked_user_name || 'profile'} in new tab`}
                        title="Open profile in new tab (same enrollment context)"
                      >
                        <PersonLinkOpenTabIcon />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: '0 12px', alignItems: 'center' }}>
                    {pl.linked_user_email && <span>{pl.linked_user_email}</span>}
                    {pl.linked_user_phone && <span>{pl.linked_user_phone}</span>}
                    {type === 'counselor' &&
                      counselorVisibleProperties.map((propKey) => {
                        const label = getPropertyLabel(propKey, personLinksMetadata);
                        const url = personLinkResolvedProperty(pl, propKey);
                        if (url) {
                          return (
                            <a
                              key={propKey}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ color: '#0369a1' }}
                            >
                              {label}
                            </a>
                          );
                        }
                        return (
                          <span key={propKey} className="profile-pl-missing-meeting-link">
                            Missing {label.toLowerCase()}
                          </span>
                        );
                      })}
                    {!pl.linked_user_email &&
                      !pl.linked_user_phone &&
                      !(type === 'counselor' && counselorVisibleProperties.length > 0) && (
                      <span style={{ fontStyle: 'italic' }}>No contact info</span>
                    )}
                  </div>
                </div>
                <div className="profile-pl-row-actions" onClick={(e) => e.stopPropagation()}>
                  {canRemove && (
                    <button
                      type="button"
                      className="profile-document-delete"
                      onDoubleClick={() => onRemoveRequest(pl.id, pl.linked_user_name || 'this person')}
                      aria-label="Remove link (double-click to confirm)"
                      title="Double-click to remove"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        ))
      )}
    </div>
  );
};

function StudentInfoDateBadge({ attr, value, label, disabled, onChange }) {
  let display = 'Not set';
  if (value) {
    const d = new Date(`${value}T12:00:00`);
    display = Number.isNaN(d.getTime())
      ? String(value)
      : d.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
  }

  return (
    <div className="profile-si-badge-cell profile-si-date-badge-wrap">
      <div
        className={`profile-choice-badge profile-choice-badge--date${
          disabled ? ' is-disabled' : ''
        }`}
      >
        <span className="profile-choice-badge__kicker">{label}</span>
        <span className="profile-choice-badge__value">{display}</span>
        {!disabled && <span className="profile-choice-badge__chev" aria-hidden>▾</span>}
      </div>
      {!disabled ? (
        <input
          id={`si-${attr}`}
          type="date"
          className="profile-si-date-input-overlay"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          aria-label={`${label}: ${display}. Choose date`}
        />
      ) : null}
    </div>
  );
}

function StudentInfoChoiceBadge({
  kicker,
  displayValue,
  tone = 'neutral',
  disabled,
  isOpen,
  onToggle,
  menuId,
  hideKicker = false,
  children
}) {
  const triggerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setMenuStyle(null);
      return undefined;
    }
    const updatePosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const menuMaxH = 280;
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
      const maxHeight = Math.min(menuMaxH, Math.max(100, openUp ? spaceAbove - gap : spaceBelow - gap));
      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: Math.max(rect.width, 168),
        maxHeight,
        zIndex: 10050,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap })
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  return (
    <div className={`profile-choice-badge-wrap${isOpen ? ' is-open' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`profile-choice-badge profile-choice-badge--${tone}${disabled ? ' is-disabled' : ''}`}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? menuId : undefined}
        onClick={onToggle}
      >
        {!hideKicker && kicker ? (
          <span className="profile-choice-badge__kicker">{kicker}</span>
        ) : null}
        <span
          className={`profile-choice-badge__value${
            hideKicker ? ' profile-choice-badge__value--solo' : ''
          }`}
        >
          {displayValue}
        </span>
        {!disabled && <span className="profile-choice-badge__chev" aria-hidden>▾</span>}
      </button>
      {isOpen &&
        menuStyle &&
        createPortal(
          <div
            id={menuId}
            className="profile-choice-badge-menu profile-choice-badge-menu--portal"
            style={menuStyle}
            role="listbox"
          >
            {children}
          </div>,
          document.body
        )}
    </div>
  );
}

// Define ExpandableSection component
const ExpandableSection = ({ title, isExpanded, onToggle, children, showActions = false, onSave, onCancel, hasChanges = false, headerExtra = null }) => (
  <div className="profile-expandable-section" style={styles.expandableSection}>
    <div
      className="profile-expandable-section-header"
      style={{ ...styles.sectionHeader, cursor: 'pointer' }}
      onClick={onToggle}
    >
      <span className="profile-section-title">{title}</span>
      <div style={styles.sectionHeaderActions}>
        {showActions && hasChanges && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSave();
              }}
              className="profile-section-save"
            >
              Save
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              className="profile-section-cancel"
            >
              Cancel
            </button>
          </>
        )}
        {headerExtra ? (
          <span
            className="profile-section-header-extra"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {headerExtra}
          </span>
        ) : null}
        {isExpanded && <span className="profile-section-expand">▼</span>}
      </div>
    </div>
    {isExpanded && (
      <div style={styles.sectionContent}>
        {children}
      </div>
    )}
  </div>
);

// Define InfoField component
const InfoField = ({ label, value }) => (
  <div style={styles.infoField}>
    <span style={styles.infoLabel}>{label}:</span>
    <span style={styles.infoValue}>{value || 'N/A'}</span>
  </div>
);

export default UserProfileView;

